/**
 * One match's runtime: everything that decides what happens, nothing about how
 * it looks (BUILDLIST Phase 51). The Game class in main.ts is becoming the shell
 * around it — renderer, DOM, input, network, camera, audio — and the headless
 * harness will drive the same object.
 *
 * 51.1 (this step) is the skeleton: the match-state fields moved here from Game,
 * which reaches them through accessors, so behaviour is unchanged. Logic moves
 * in over the following steps (planning, rounds, airborne, impacts, flight,
 * turn advance, actions, bot host, lifecycle).
 *
 * Rules for this directory: no `three`, no DOM, no rendering/ui/audio/network
 * imports (tests/matchImports.test.ts). Game logic stays in core/ as pure
 * functions; Match sequences them and owns the mutable state between calls.
 */

import { stepObjectives, type ObjectiveEvent } from '../core/storyObjectives';   // 69.50 step 1
import { jumpFromOf, jumpGravityScale, jumpSpeed } from '../core/jump';
import { bulletExplosion, explosionBlast, vehicleBlastOwner, shellExplosion, burstModelFor } from '../core/explosions';
import { SHELL_HEART, BLAST_RADII } from '../constants/explosions';
import { getStructureWorldPosition } from '../core/gameState';
import { ACTION_WIND, AIRSTRIKE, BASE_CREW, DROPSHIP, DROPSHIP_PAYLOADS, GRENADES, MORTAR, MOUNT_LAUNCHERS, STRIKE_DRONE } from '../constants/action';
import type { ExplosionClass } from '../constants/explosions';
import { markBodies, pruneMarks } from '../core/marks';
import { footprintObstacles, personMover, movingHullNear, personHip, outOfHulls, hullClearance } from '../core/hits';
import { CALL_IN_SPREAD } from '../core/callIn';
import { emptyRecord, recordEvents, type MatchRecord, type RecordEvent, heldChanged } from '../core/scoreboard';
import { tickChecksum } from './tickChecksum';
import { decidePhase, type DecideDriver } from './decide';
import { contentChanged } from '../core/contentHash';
import { canSpendMovement, knowledgeTurn } from '../core/clockRules';
import { gravityScaleOf, drives, seatArmed, structureTeam } from '../core/body';
import { stepTeamFog } from '../core/teamFog';
import { tickMatchEvents, actionEventClock, ACTION_EVENT_PERIOD_TICKS, claimSurveyBounty } from '../core/matchEvents';
import { dacos, dhypot, datan2 } from '../core/dmath';
import { applyOrderInput, pruneTasks, takeRefusalLog } from '../core/squads';
import { stepExtraction } from '../core/withdrawal';
import { applyReserveCall, placeArrival, placeArrivalAt, arrivalRegionFor } from '../core/reserves';
import { arrivalAim, opsTarget, spendDrops } from '../core/drops';
import { callPods, stepPods } from '../core/pods';
import { joinDropSquad, stepDropSquads } from '../core/dropSquad';
import { applySpawnAt, closedSpawns, noteSpawnArrivals, sideSpawn, spawnAtRefusal, stepHotSpawns } from '../core/spawnAt';
import type { OrderIssuer, GunshipWeapon, HeliWeapon, VehicleKind, DronePayload, GrenadeKind, SmokeCloud, StrikeTarget, StrikeTask, SquadOrder, ActionShell, BotDecision, GroundWeaponKind, BotFogContext, Bullet, MissileFlight, CraftControls, Sighting, UnitIntent, GameState, FireParams, Vec3, ProjectileSimResult, ProjectileState, IncomingFireIntel, TurnFlow, PlayerState, AirborneCause, PlanetState, ShotOutcome, ShotRecord, JetpackParams, ItemKey, ShopParams, StructureType, StructureState, GroundItemState, RuinState, CollectActionParams, CommandParams, CollectibleNode, CarryableObjectState, ActionBrainState, TeamTrackEntries, MatchInput, MatchInputBody, LoggedInput, PlayRefusal, GamePhase, PlanState, FlightState, CurrentShot } from '../types';
import type { QueuedOrder, PlannedMove } from '../core/turnFlow';
import { FrameScheduler } from '../core/scheduler';
import { createProjectile, startFiringPhase, startResolvingPhase, setPhase, movePlayer, addStructure, getPlayerWorldPosition, canPlantFlag, plantFlag, arcDistance } from '../core/gameState';
import { applyFireCost, refundFireCost, computeMoveCost, spendMovement, chargeJump, checkFire, computeJumpLaunch, applyJump, type JumpLaunch } from '../core/actions';
import { raycastHitscan, applyHitscanDamage } from '../core/hitscan';
import { checkTouchReturn, checkCapture, executeCapture, computeFlagStatus, pickupObject, dropObject } from '../core/carryables';
import { removeStake } from '../core/stakes';
import { canBuildDrill, buildDrillSite } from '../core/excavation';
import { canInteract, startInteraction, tickInteraction } from '../core/interactions';
import { collectItem, canCarryMore } from '../core/collectibles';
import { processFragmentUpload } from '../core/uplink';
import { processSalvageDelivery } from '../core/salvageRun';
import { startClaim } from '../core/claims';
import { issueCommand, cancelCommand } from '../core/botCommands';
import { dispatchInput, SILENT_PRESENTER, seatActor, seatTeam } from './input';
import { checkGroundItemPickup } from '../core/groundItems';
import { applyHeal, applyDamage } from '../core/damage';
import { calculateShopTotalWithFuel, applyShopPurchasesWithFuel, canBuildEcotourism } from '../core/economy';
import { facingAfterWalk, facingFromAzimuth } from '../core/perception';
import { vecClone, vecCross, vecDot, vecNormalize, vecSub, vecAdd, vecScale, vecLength, directionToAzimuthElevation, uprightFrame } from '../math';
import { TURRET_PARTS_FROM } from '../constants/hitboxes';
import { isTurretSeat, layTurret, tankMuzzle } from '../core/turret';
import { layRate, mainGunReload } from '../core/crewman';
import type { TankRound } from '../types/body';
import type { BodyKey } from '../constants/body';
import { simulateRound, beginRound as beginRoundRun, stepRound as stepRoundRun, finishRound as finishRoundRun, type RoundRecord, type RoundOrder, type RoundOptions, type RoundRun } from '../core/roundSim';
import { buildRoundOrders, roundInitiativeOrder, controlLeavesPlanners, shouldResolveBatch } from '../core/turnFlow';
import { buildShotRecord, appendShotRecord } from '../core/shotRecord';
import { resolveImpact, queueClusterSubmunitions, type ImpactResolution, type CraterFn } from '../core/resolveImpact';
import { crashesDue, landCrash, decayWrecks } from '../core/shipCrash';   // 69.3b, the ship's crash
import { stepPointDefence, pdOn } from '../core/pointDefence';   // 69.9
import { createCrater } from '../core/damage';
import { accumulateScorch, recolorVertices, computePlanetVertexColors, recolouredFacets } from '../core/planetColors';
import { popPendingCluster } from '../core/gameState';
import { refreshFogColors, refreshFogColorsAt, changedVertices, changedVerticesAmong, updateFogForPlayer, updateExposureMarkers, pruneFogMarkers } from '../core/fog';
import { noise, type Loudness } from '../core/perception';

type AlertChange = ReturnType<typeof noise>['changes'][number];
import { createCraterWasm, updatePlanetTerrain, setGeysers, setHitboxes, simulateProjectileFull, setPlanetAlive, selectKernelWorld, dropKernelWorld, DEFAULT_KERNEL_WORLD, setWindGrids, setWindGusts } from '../ai/wasmPhysics';
import { windGusts } from '../core/wind';
import { windGrids, gridSecond } from '../core/windField';
import { resolveTurnStart, activateInteraction, type TurnResolution } from '../core/resolveTurn';
import { processProximityEffects } from '../core/hazardEffects';
import { simulateFlight, simulateFlightSegment, buildGeyserSims, buildHitboxes, type SegmentSimulator } from '../core/flight';
import { GHOST_MARKER_MAX_AGE, BOT_PRESETS, WEAPONS, MELEE_DAMAGE, MELEE_MOVEMENT_COST, HEAL_ITEMS, HEAL_MOVEMENT_COST, BUILD_MOVEMENT_COST, SUPPLY, SUPPORT, FOB, CHARGE } from '../constants';
import { revealAlongPath, getEnemiesAlongPath, getVisibleEnemies, getTeamVisibleEnemies, updateGhostMarkers, getStructuresAlongPath, updateStructureGhosts, revealAtPoint, getEnemiesAtPoint, getStructuresAtPoint } from '../core/fog';
import { updateAirbornePlayer, hasAirbornePlayers, gravityAccel, checkCollision } from '../core/physics';
import { personGroundHit, personPointAt, personSurfaceRadius } from '../core/fineTerrain';
import { footingAt, slipStep, downhillAt, velocityAsWalk } from '../core/groundWalk';
import { getSurfaceRadius } from '../core/surface';
import { GAIT, STANCE_SPEED, WEAPON_DRAW_SECONDS, GUN_RELOAD_SECONDS, GUN_RELOAD_DEFAULT, GUN_UNSPOTTED, GUN_SHUN_SECONDS, AIM_YAW_MAX } from '../constants';
import { WORN_GUN } from '../constants/damage';
import { roleLost, gunWear } from '../core/parts';
import { weaponSpec, weaponOf, primaryOf, roundsLeft, roundsIn, reserveLeft, finishReload, mountedGunSpec, sidearmOf, tankRoundSpec } from '../core/weapons';
import { canBandage, beginBandage, stepBandage, cancelBandage, stepTreatments } from '../core/medical';
import { stepSupplies, refillPointCrates, type SupplyEvent } from '../core/supply';
import { stepSupplyDrones, callSupplyDrop } from '../core/supplyDrone';
import { stepCases, stepCaseRefills, stepHandOuts, clearCases, supportOn, freshCase, type SupportEvent } from '../core/supportRole';
import { refillFobCrates } from '../core/fob';
import { stepRearm, loadBeds, type RearmEvent, type BedEvent } from '../core/munitions';
import { stepConvoys, convoysOn, baseSpawnFor, noteBaseSpawn, redeploysDue, endRun, truckCrewSeat, type ConvoyEvent } from '../core/convoys';
import { baseCrewSeat } from '../core/baseCrew';
import { stepEvasion } from '../core/skyEvade';
import { stepGunships, steerGuided } from '../core/gunship';
import { stepHeliGuns } from '../core/heliGuns';
import { standingBeacon, beaconArrival, beaconsLostWith, type BeaconLostEvent } from '../core/beacon';
import { stepBuilds, type BuildEvent } from '../core/build';
import { isStandingRelay, updateRelaySightings } from '../core/relay';
import { updateWebs } from '../core/web';
import { stepStructureCapture, takenWithSector, type StructureCaptureEvent } from '../core/structureCapture';
import { applyCoordinate, stepPlaysLogged, airDest, airFlown } from '../core/plays';
import { hotWanted } from '../core/hotCall';
import { applyRespawnPick, pickedSpot, respawnPickRefusal } from '../core/respawnPick';
import { applyVehicleUse } from '../core/vehicleUse';
import { stepScouts } from '../core/scouts';
import { stepObjectiveIntel } from '../core/objectiveIntel';
import { stepLandingIntel } from '../core/landingIntel';
import { stepMountIntel } from '../core/mountIntel';   // 69.9 step 5
import { stepIntel } from '../core/intelRecord';   // 69.73 step 2a
import { skyOpened, standingHolds, holdsLost, type ConsequenceEvent } from '../core/consequences';   // 69.61 step 3
import { callLighter, stepLighters, stepLighterOrders, setLighterOrder } from '../core/lighter';   // 69.10
import { stepDrive, type DriveEvent } from '../core/drive';
import { rocketBlast } from '../core/rocket';
import { zonesOn, loadWidening } from '../core/vehicleZones';
import { ZONE_RULES, BURST_PENETRATION } from '../constants/vehicleZones';
import { stepMissileLock, stepMountLock, launchMissile, stepMissiles, missileBlast } from '../core/antiDropshipMissile';
import { stepCountermeasures, type CountermeasureEvent } from '../core/countermeasures';   // 69.58
import { takeShip, stepPiloted, type PilotEvent } from '../core/piloted';
import { sendStrike, stepStrikeDrones, strikeBlast, strikeDroneInFlight, strikeTaskFor } from '../core/strikeDrone';
import { canThrow, grenadeBlast, stepGrenades, throwGrenade, windGrenade, stepRealTiming } from '../core/grenades';
import { chargeRefusal, startCharge, blowWire, stepCharges, freshCharges, type ChargeEvent } from '../core/charges';
import type { ChargeFuse, PlacedCharge } from '../types/charge';
import { eyeScaleOf, bodyStanceOf, stanceChangeSeconds } from '../core/stance';
import { applyEfforts } from '../core/efforts';
import { SIGHT_PERIOD_TICKS } from '../core/picture';
import { squadsOnRespawn, stepSquadMembership, type MembershipEvent } from '../core/squadMembership';
import { checkGeyserBoost } from '../core/hazardEffects';
import { getPlayerCarrying, respawnCoreAtBase } from '../core/carryables';
import { dropHealItemsOnDeath } from '../core/groundItems';
import { applyDeathCooldown } from '../core/resolveImpact';
import { isGameOver } from '../core/gameState';
import { buildBotHostContext } from '../ai/botFogContext';
import { stepWalk, planetObstacles, headingOf, turnHeading, rightOf, airborneAgainstObstacles } from '../core/surfaceWalk';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { mountedSeat, seatAt, seatUnit, canMount, canDismount, mountUnit, dismountUnit, reconcileSeats, isSeatShot, fireCheckSubject, stepSeats, shellsLeft, spendShell, gunSpottingOn, isSatSeat, satSeatPos, type SeatEvent } from '../core/seats';
import { fireShell, stepShells, shellProjectile, predictShellLanding, aimSpotted } from '../core/actionShells';
import { keyedGenerator } from '../core/random';
import { clockPolicyFor, type ClockPolicy } from './clockPolicy';
import { isTowerSensor, updateTowerSightings, mergeSightings } from '../core/towers';
import { RATES, isDue, respawnWaveRate } from './rates';
import { canLaunchDrone, canTaskDrone, launchDrone, stepDrones, droneSightingsNow, droneBusy, beginDroneTasking, cancelDroneTasking, tickDroneLaunches, type DroneEvent } from '../core/drones';
import { fireBullet, stepBullets, type BulletEvent } from '../core/bullets';
import { canPopSmoke, popVehicleSmoke } from '../core/vehicleSmoke';
import { seatBuild } from '../core/buildSeat';
import { restand, restandAfter } from '../core/stands';   // (209's footing follow-up)
import { addCraterlets, type CraterletCause } from '../core/craterlets';
import { VEHICLE_KINDS } from '../core/vehicleStock';
import { stepForestHeld } from '../core/forestHeld';
import { hqFallBites } from '../core/hqFall';
import { stepCapture, respawnSpot, landingCentre, ringSpot, facingObjective, teamAnchors, markUnderFire, type CaptureEvent } from '../core/capturePoints';
import { stepSuppression } from '../core/suppression';
import { stepDigs } from '../core/entrench';
import { stepRubble } from '../core/moduleDamage';
import { stepCollapse } from '../core/collapse';
import { battlePlanetIndex } from '../core/actionSetup';
import { executeRespawn, canRespawn } from '../core/respawn';
import { PROBE_REVEAL_RADIUS, PHYS_DT, CLUSTER_LAUNCH_DELAY_STEPS, OUT_OF_BOUNDS, MAX_AIRBORNE_TIME, ARRIVAL } from '../constants';
import { groundMaterialsOn } from '../core/groundMaterial';
import { stepGrenadeRestock, topUpGrenades, type GrenadeEvent } from '../core/grenadeStock';
import { stepVehicleStock, type VehicleEvent } from '../core/vehicleStock';
import { poolOf, setPoolOrder, startUnpack, syncPoolStacks } from '../core/motorPoolStock';
import { airstrikeBlast, airstrikeHome, airstrikeOwner, callAirstrike, stepAirstrikes, strikePoints } from '../core/airstrike';
import { callMortar, stepMortars } from '../core/mortar';
import { callRescue, stepRescues, noteCrew, type RescueEvent } from '../core/rescue';
import { stepHeliLifts } from '../core/heliLift';
import { padReturn, stepHelipads } from '../core/helipad';
import { callPickup, flightRampFoot, pickupBoarding, pickupHome, pickupUnder, boardingOpen, boardingSortie, leavePadShip, padRampDown, callDropship, containerAt, syncShipContainers, dropshipAboard, inShipWay, padArrivalCentre, padRampFoot, padRunSeconds, padSeat, pinBoarded, queueSortie, riderWalk, shipWalks, stepDropships, stepRampWalks, stepSorties, vehicleAt } from '../core/dropship';
import { stepRocketRestock, issueRockets, carriesRocket, type RocketEvent } from '../core/rocketStock';
import { applyCarry, carriesGl40, carriesPanzerfaust, carriesMissile } from '../core/loadout';
import { armourOf, issueOf } from '../core/armour';
import { respawnGl40, stepGl40Restock, type Gl40Event } from '../core/gl40Stock';
import { stepKitRestock, type KitEvent } from '../core/buildKits';
import { stepLoudness } from '../core/footsteps';
import { countOf, defaultOn } from '../core/defaultOn';

/**
 * 64.10 (decision 116, R11 lane C): the grid a human's intent takes as it enters a Match — sticks to 1/64, aim
 * pitch to 1/1024 rad, a turn to 1/4096 rad; buttons as they are. The values are exact binary fractions, so the
 * input log records exactly what the sim applied, and a frame's float noise never enters the state.
 */
export function quantizeIntent(i: UnitIntent): UnitIntent {
  const q = (x: number, step: number) => Math.round(x / step) * step;
  return { forward: q(i.forward, 1 / 64), right: q(i.right, 1 / 64), turn: q(i.turn, 1 / 4096), aimPitch: q(i.aimPitch, 1 / 1024), fire: !!i.fire, jump: !!i.jump, ...(i.fire2 ? { fire2: true } : {}), ...(i.round ? { round: i.round } : {}), ...(i.stance ? { stance: i.stance } : {}), ...(i.aimYaw ? { aimYaw: q(i.aimYaw, 1 / 4096) } : {}), ...(i.gait && i.gait !== 'walk' ? { gait: i.gait } : {}), ...(i.dig ? { dig: true } : {}), ...(i.cover ? { cover: { pose: i.cover.pose, ...(i.cover.side !== undefined ? { side: i.cover.side } : {}), out: !!i.cover.out } } : {}), ...(i.reload ? { reload: true } : {}), ...(i.weapon ? { weapon: i.weapon } : {}), ...(i.seat ? { seat: i.seat } : {}), ...((i.seat === 'enter' || i.seat === 'up') && i.seatPick ? { seatPick: { structureId: Math.trunc(i.seatPick.structureId), seat: Math.trunc(i.seatPick.seat) } } : {}), ...(i.heal ? { heal: true, ...(i.healTarget !== undefined ? { healTarget: i.healTarget } : {}) } : {}), ...(i.resupply ? { resupply: true } : {}), ...(i.caseDrop ? { caseDrop: true } : {}), ...(i.gun ? { gun: { power: q(i.gun.power, 1 / 64), weaponKey: i.gun.weaponKey } } : {}), ...(i.pilot ? { pilot: i.pilot } : {}), ...(i.fly ? { fly: quantizeFly(i.fly) } : {}) };
}

/** 69.17 step 3: a pilot's controls as they enter the Match (each axis to 1/256; the lift jets a switch). */
function quantizeFly(c: CraftControls): CraftControls {
  const q = (x: number | undefined) => (x === undefined ? undefined : Math.round(Math.max(-1, Math.min(1, x)) * 256) / 256);
  const out: CraftControls = {};
  for (const k of ['forward', 'turn', 'strafe', 'lift', 'throttle', 'yaw', 'pitch', 'roll'] as const) { const v = q(c[k]); if (v !== undefined && v !== 0) out[k] = v; }   // (2b's `turn`, the dropship's A / D, too)
  if (c.jets) out.jets = true;
  return out;
}

/**
 * Version of what a battle resolves to. Bump it whenever a change alters match
 * results on purpose (the same commit that re-records the full headless golden):
 * a stored sector battle pins the version it was resolved under, and re-resolving
 * it under another version may give a different outcome.
 *
 *   1 — 51.4: the harness gains the game's off-map death side effects (respawn
 *       cooldown, cargo home, heal drops) and flyover scans.
 *   2 — 51.5a: round-flow impacts give the harness's bots incoming-fire intel, as
 *       in the game.
 *   3 — 51.5b: the harness steps airborne units during flights and bomblet delays,
 *       and probes scan as they fly, as in the game.
 *   4 — 51.6: in the harness a dead unit's turn passes on through turn order and
 *       hazards act on the unit whose turn begins, as in the game.
 *   5 — 51.7a: the harness builds listening-post antennas (Signal Trace) and buys
 *       heal items in deliveries, as the game; no golden case moved.
 *   6 — landing region: a sector side that arrived by a lane deploys on the side of
 *       the system facing where it came from (BattleSide.arrival → deployRegionByTeam);
 *       the holder still deploys freely. Headless goldens have no arrivals: unchanged.
 *   7 — 51.9b: auto-resolve plays exactly the game's match — match events, rocks and
 *       flora tracking (completeMatchState), the kernel's line of sight and flights
 *       when loaded, and the first turn begun like every later one.
 *   8 — deterministic maths: game logic uses src/core/dmath.ts (fdlibm ports) instead
 *       of the engine's sin/cos/acos/atan2/exp/pow, so a battle resolves the same to the
 *       last bit in Node and every browser; every match shifts by an ulp here and there.
 *   9 — cover (37.18): a rim between a unit and an impact cuts the knockback.
 *  10 — R3 catch-up (lane B): three re-recorded behaviour changes that shipped without a
 *       bump — c6ddcb6 bots fire only what they bought (an absent ammo count was
 *       unlimited), ff18476 K3 the turn bot reads only what its team knows, 7278d84
 *       incoming fire is an anonymous contact, never a named ghost (the mortar key,
 *       5242335, moved state checksums only). R4 (lane A): **one number covers both
 *       clocks** — so 10 also covers the action clock's hop landings (9f511fa) and felt
 *       gravity (19902a8); the action clock is locked by tests/golden/action.json, and
 *       every golden update refuses to record changed results at an unchanged version
 *       (src/ai/goldenVersion.ts).
 *  11 — R5 crater parity: the WASM kernel's crater subtracts in f64 and rounds once, as
 *       damage.ts does (it rounded the depth to f32 and subtracted in f32), so kernel and JS
 *       craters are bit-identical; every crater on the kernel moves in the last bits.
 *  12 — careful jumps by default (decision 90): every bot without a jump profile now survives
 *       its landings and takes the softest one (easy and medium bots were reckless).
 *  13 — biomes on (decision 87, 62.5): a person-scale battle (k_human < 1) is dressed with biome
 *       stands — normal trees and rocks that block feet, bullets and sight. Classic matches unchanged.
 *  14 — R1: on the action clock a unit lost off the map (star, wormhole, space) comes back at the
 *       next respawn wave; it used to get a turn-counted cooldown that only the turn clock counts
 *       down, so it never returned. No golden case loses a unit off the map (all goldens identical);
 *       a real-time battle where one does plays differently. Classic matches unchanged.
 *  15 — 64.9 (decision 115): action bots deliberate on the state at tick T (when they look, 5 Hz) and
 *       their plan applies at T + 60 ticks; reflexes (aim, fire, strafe, walking) carry it out every tick.
 *       Their random draws are keyed by (seed, unit, tick, purpose), with no running generator. A bot's
 *       cover arrival is k-scaled (was 0.15 u). Capture under fire on by default (decision 117): a
 *       unit hit by a bullet cannot capture for 3 s. Every action result moves; classic matches unchanged.
 *  16 — rock fields and mountain rocks (owner, Oct 10): a dressed person-scale battle sometimes gets a
 *       biome's rock field, and rocks on its steep and high ground; both are obstacles. Classic unchanged.
 *  17 — S3 (decision 11's sim half): at person scale a person stands, walks, lands, is hit, shoots and sees on
 *       the fine ground (the mesh plus person-scale hummocks): hit spheres, muzzles and eyes ride it, a bullet
 *       stops in a hummock, a fall lands on one, and sight runs personLineOfSight. Classic matches unchanged
 *       (every person query is exactly the mesh's at classic).
 *  18 — stand shapes and placement with the terrain (owner, Oct 10): a dressed battle's stands come as discs,
 *       belts, rings, scatters and contours, rooted on gentle ground; trees are obstacles. Classic unchanged.
 *  19 — four more biomes from the kit (owner, Oct 10): a birch glade, the burn, dry scrub and sonora's hollow
 *       wood; most planet types grow one more, so the regions a dressed battle draws change. Classic unchanged.
 *  20 — 62.2 (decision 65): crouch and prone. A stance comes in with the intent; it lowers the eye and the
 *       muzzle, shrinks the hit volume (prone: three low spheres along the body), slows the walk (0.55, 0.25),
 *       and only a standing person jumps. Bots crouch while they fight where they stand, at either scale; the turn game is unchanged.
 *  21 — Phase 65 (decision 118): ground weapons. The rifle (today's gun) has a 30-round magazine and a 2.2 s
 *       reload with no firing; the pistol (12 damage, 12 rounds, shorter-lived shots) is drawn in 0.45 s. An empty
 *       weapon reloads as it fires; bots reload when nobody is in sight and they are under half. The turn game is unchanged.
 *  22 — K1 finished (honest knowledge on the action clock): a bot tracks its target live only while its last look
 *       saw it; out of sight it lets it go (its lead and line-of-fire judgement reset), and its fight spots score the
 *       threats where it saw them, not where they are. Every action result moves; the turn game is unchanged.
 *  23 — 65.4 (decision 118): bots use both weapons. Mid-fight, an empty rifle (or a long reload) draws the pistol and an
 *       empty pistol goes back to a rifle with rounds; with nothing left in either, a bot reloads behind cover (the hurt
 *       bot's cover rule) and holds it until the reload is done. Out of sight, a pistol under half is reloaded, then put
 *       away for the rifle. The turn game is unchanged.
 *  24 — 66.2 (decision 119): spacing. An action bot keeps BOT_SPACING (3) person lengths from its teammates, moving or
 *       fighting — a push in its movement, harder the closer (squadmates in formation keep the formation's spacing) — and a
 *       capturing bot stops anywhere within 0.6 of the point's radius instead of walking to its centre, so a team spreads
 *       over a point. The turn game is unchanged.
 *  25 — I3 (decisions 25, 62): the probe is the cheapest drone. A unit with no probe rounds flies a carried drone as
 *       its probe (one per flight), and the turn bot's probe decision counts them, so sector battles, whose squads carry
 *       drones, move. A probe's sightings go into the team's sightings list like a drone's. Classic matches carry no
 *       drones: the headless goldens are unchanged.
 *  26 — 64.11's first half (lane C): seats on the action clock. A person gets into an emplacement's seat in reach and
 *       out again (an intent impulse), lays the gun with its heading and pitch, and fires its shells, which fly the
 *       turn game's own flight a tick at a time and land through the impact resolver; a seat is possessed through apply
 *       (whoever sits in it). 62.8's launch-and-landing rule: a blast's push on a person and a person's landing are
 *       converted into the person family (× k). On the action clock, S6's emplacements stand within a person's reach.
 *       The turn game is unchanged.
 *  27 — 64.11's second half (lane C; R11 step 6 for a gun): a bot in a seat crews the gun with the turn brain's fire
 *       search on what its team knows, deciding at T and entering a `decision` input at T + L; the Match fires the
 *       decision's exact shot when the gun is ready, once per decision. The nearest free bot of a team walks to an
 *       empty gun of its team and gets in, and gets out when an enemy comes close. On the action clock an emplacement's
 *       obstacle is person-scale, cover to the shield's height. The turn game is unchanged.
 *  28 — the prone rule (lane C; lane B's counters found no bot ever went prone): an action bot lies down holding a
 *       sector or a point of its own with nothing in sight, when suppressed (the capture rule's mark, or hurt in the
 *       last 2 s) with no clear shot back, or for a long shot from cover; it stands to move; behind the stances switch.
 *       The turn game is unchanged.
 *  29 — 67.11 (the content loop): at person scale the planet-scale rocks are gone — mapgen's giant rocks (twenty
 *       to fifty people tall at k 1/20) are dropped before the biome stands dress the site, so human-scale cover,
 *       walking and the stands' ground change. The turn game is unchanged.
 *  30 — 65.7 Medical, step 1 (the owner, Oct 10): bandages. A person carries two a life; applying one holds them still for
 *       4 s with no firing (a shot or a jump cancels it, the bandage kept) and heals 35 up to full. Bots bandage when hurt
 *       and out of the fight (in cover, or nobody in sight), holding still for it. Off by `medical: false`. The turn game is unchanged.
 *  31 — R11 step 6, the turn bot: every decision in a bot's turn draws from its own keyed stream (seed, unit, basis
 *       turn, purpose, its count) instead of one generator threaded through the turn and round-tripped through the
 *       worker (ai/botTurnRunner.ts TurnDraws). Every turn match moves; the action game is unchanged.
 *  32 — the gun brain's work budget (lane B, CLOCK_RFC §7): a gun crew's fire search runs one fixed tier
 *       (GUN_SEARCH_TIER: 12×5×4 coarse, top 3, two refinements), whatever its skill, so its worst case fits in about
 *       half of L on the reference machine. Seated battles move; nothing else does.
 *  33 — 65.8 Medical, step 2 (the owner, Oct 10): the medic. A bandage on a hurt teammate within 1.5 people — the patient
 *       carries the timer, both are held, the medic's own bandage is spent (never unlimited: the same two a life). Bots out
 *       of the fight with one to spare patch a hurt teammate in reach. The turn game is unchanged.
 *  34 — guns at their own scale (lane C, with seats under threat): on the action clock a blast reaches a person-scale
 *       structure (an emplacement, a tower) at its own size — blast × 2.2 + its half width — not with the planet-scale
 *       prop bonus that let a gun's first shell wreck both guns at human scale; a gun crew holds a shot aimed inside its own
 *       blast's reach of its gun (danger close); the crew's run for a gun mid-fight is behind a switch (off). No stored
 *       golden case moves (no emplacements in them), but a replay with guns would. The turn game is unchanged.
 *  35 — 65.9 Supply crates (the owner, Oct 10): each team's crate near its first unit on the action clock (6 bandages, 4 spare
 *       rounds); E in reach restocks what is missing; bots out of bandages with nobody in sight walk back and restock. A crate
 *       is a walk obstacle. The turn game is unchanged.
 *  36 — 65.10 (the owner, Oct 10): a crate at each capture point serving whoever holds it and refilling while held; bot
 *       medics walk to a hurt teammate who has no bandage; crates hold spare magazines; a reload takes what the magazine is
 *       missing from the spares (the rocket as before); ammo reserves for the rifle and the pistol behind a switch (off).
 *  37 — R14 (b): the solar flare and the comms blackout are an information outage for people and bots alike. A flare
 *       takes the aim preview offline, and a bot's fire search runs its coarsest tier (no dial-in); a blackout cuts the
 *       bearing cue, and a bot gets no incoming-fire intel. Turn matches with one of those events move.
 *  38 — R14 (c): the bounties pay. The preserve bounty pays every team that has burned no flora when it triggers; a
 *       survey bounty opens for three turns on a planet drawn by its keys, and the first probe over it pays its shooter.
 *       Turn matches with a bounty move (none of the goldens' do); on the action clock only the per-tick trace moves.
 *  39 — 67.13 (the content loop; owner, Oct 10): a person-scale bullet falls by 0.6 of the felt gravity (the profile's
 *       `bulletGravity`; the bots lift for the bullet's own share), and a biome rock's shot and sight cover is its drawn
 *       shape — the measured width at each height, starting where the drawn rock is sunk — not its full cylinder.
 *       Human-scale action matches move; classic and the turn game are unchanged.
 *  40 — R14 (d): the debris field leaves the event pool (it never did anything; a hazard to flights would need the
 *       shot simulation's kernel parity, the asteroid pass's work). Every event schedule shifts, so most turn matches
 *       move.
 *  41 — 67.15 (the content loop; owner, Oct 10: "1.5 or 2 both work better than 1"): a person-scale bullet leaves at
 *       1.5× the speed (the profile's `bulletSpeed`; the pistol too). Human-scale action matches move; classic and the
 *       turn game are unchanged.
 *  42 — Decisions 126 and 128 (the owner, Oct 10: guns behind the lines; "Shell stock + resupply"; "Accuracy penalty
 *       without spotter, and slower reload without spotter"), at a person scale on the action clock: a team's guns stand
 *       a heavy shell's knockback reach behind its people, its shells in a dump beside them; a gun holds 4 standard + 2
 *       heavy, its crew refills it with a load of 6 from the dump; a shot where none of the team sees scatters and waits
 *       twice its reload; the crew walks back to a gun behind it, and fetches a load for a dry one. Human-scale matches
 *       with guns move; classic and the turn game are unchanged (no stored outcome has person-scale guns).
 *  43 — the bot's stake rule (lane B, the every-switch check): a turn bot plans a stake only where the game lets it
 *       (single-stake modes: not where the enemy holds one) and with the movement a stake costs; it used to be refused
 *       there every time (39 refusals to 8 stakes in three dominion matches) and now advances instead.
 *  44 — 65.11 (from lane B's every-switch check): bandages that get finished — while one goes on the weapon is down (a
 *       shot is refused, never an interruption); a step or a jump breaks it off, its progress kept for the next; a medic's
 *       treatment starts only on a patient standing still. The turn game is unchanged.
 *  45 — 63.3 / 63.4 step 1 (lane F; ARCHITECTURE §5.13 "63.3 and 63.4, the design"): decision 130 (d), "Everything
 *       follows the setting" — a bullet passes a teammate by with friendly fire off (by the setting's share between),
 *       and a blast spares its own side's structures, flags and drop cans by the same share; a ruin blocks walking,
 *       shots and sight at its rubble's measured height, not at full size; the beacon is a person's mast (it was a
 *       planet-scale building), the supply crate cover to its own height; the tower's sight samples cover at the
 *       person's step. Action matches move; no classic golden match moves (51/51 identical).
 *  46 — Lane B's brain-rules row: two stuck-hop bugs in the action bot fixed for everyone — a crouched or prone
 *       strafe read as stuck (the test was a fixed 0.01·k a tick; now under a quarter of the step its stance and its push
 *       would make), and a blocked side-step hopped (now it turns around). Behind `botKit` (off for now): bots launch
 *       drones, mark, plant beacons, crew guns under every brain and walk to a crate when dry or hurt with no bandage,
 *       through inputs their brains stage (one mechanism with the gun decisions). A mark never masks a unit's task. Action
 *       matches move; the turn game is unchanged.
 *  47 — 63.3 step 3 (lane F; ARCHITECTURE §5.13 "63.3 and 63.4, the design"): a shot and a line of sight meet the
 *       shapes exactly (core/hits.ts) — a trunk or a rock as an upright cylinder (a profiled rock a frustum per tenth),
 *       a structure as its measured shape from the body table — where they sampled points against cylinders (a shot one
 *       point at the end of each substep, a line every quarter unit; a building was a cylinder 1.75× its height). Cover
 *       stops a shot only if it comes before the person or drone the substep also meets. Action matches move; the turn
 *       game reads none of it.
 *  48 — 67.20 (b) (the owner, playing the Lab: bots "will just stand in place sometimes if they can't see any enemies
 *       but also won't play objective"): with every point its team's, a point is held by the teammate nearest it and the
 *       rest advance — on what they know, else toward the enemy's landing (its home); with no objective at all, a bot
 *       with nothing in sight advances the same way instead of roaming to a random waypoint. Action matches move.
 *  49 — The walking row (lane B's tree analysis): a bot walking somewhere looks a few person widths ahead and goes
 *       round what lies across its way (core/steer.ts) instead of walking into it and hopping; and it hops once a stuck
 *       spot (no second hop within 3 s there; a formation slot it cannot reach, it waits at). The bots use their kit by
 *       default now (`botKit`: drones, marks, beacons, crews under every brain, crates when dry or hurt). Action matches
 *       move; the turn game is unchanged.
 *  50 — 63.4 step 6 (decision 130 (a)): props take damage on the action clock. A tree has health; a blast takes it whole
 *       within its own radius of the trunk through the damage door — a shell fells it in a hit, a rocket's burst takes
 *       three — and a felled tree leaves the walk, the sight and the stands; rocks stand. Action matches move; the
 *       turn game's flora rule is unchanged.
 *  51 — 63.4 step 7: under the clock policy's `blastReach: 'shape'` (the action clock) a blast reaches a structure from
 *       its shape over the damage circle, and a rocket's burst is stopped by what lies between; a gun a blast destroys
 *       settles its gunner by decision 124's hatch rule alone; a burst's covered owner still owns its structure (130 (d)).
 *       Action matches with guns or rockets move; the turn game keeps the centre rule.
 *  52 — the owner's defaults (Oct 10): "let's make bullet velocity 2 the default" — the human person set's bullet speed
 *       1.5 → 2 (every person-scale bullet, the rifle's and the pistol's; the rocket flies on its motor). Classic and the
 *       turn game are unchanged. (The Lab's team picture and weather now default on: Lab rows only, no stored result.)
 *  53 — The last stuck hops (lane C): a bot's walk looks at the ground ahead as well (core/steer.ts steerOffSlopes) —
 *       it goes round a rise too steep to climb (a shell crater's wall) and never walks down a drop it could not climb
 *       back, unless where it is going lies below. Action matches move; the turn game is unchanged.
 *  54 — Scramble (decision 134, the owner: no ground is a trap): ground steeper than a person walks (a shell crater's
 *       wall) is climbed at a share of the pace along the slope (ACTION_SCRAMBLE 0.3; the Lab's Scramble row), for the
 *       player and the bots — `scrambling` on the unit; a bot reads it as progress, not stuck; its look-ahead turns off
 *       steep ground only while the turn keeps more of its way than the scramble would; its routes keep such ground at
 *       the scramble's pace. Action matches move; the turn game is unchanged.
 *  55 — Decision 134 (the owner): at person scale a shell's crater keeps its radius but digs no deeper than two person
 *       heights below the generated ground (each bowl shallower, stacked bowls floored), so craters are foxholes, not
 *       pits. Action matches with guns move; classic unchanged.
 *  56 — Bots shoot at drones (lane B's every-switch check: no bot ever had): an action bot sees enemy drones in the
 *       same look as people (range, line of sight, cover); with no enemy person to engage, its plan takes the nearest
 *       (at T + L, its reaction drawn as for a person) and its reflexes aim at it, led by its flight, and fire — one hit
 *       brings one down. Action matches with drones move; the turn game is unchanged.
 *  57 — 65.11's bot half (lane E's count: every stopped bandage was the bot's own step): in a fight in the open, a bot
 *       with a bandage going on goes prone and finishes it instead of side-stepping off it, unless the threat is inside
 *       the near edge of its fight band (then it breaks off, to fight); on its way to cover it still goes; a bandage it
 *       broke off goes back on once it is out of the fight, whatever its health by then. Action matches move; the turn
 *       game is unchanged.
 *  58 — E5.2: a normal tree stops shots and sight as it is drawn — its drawn variant's measured wood (constants/treeWood.ts:
 *       a leaning trunk, a curved palm, the limbs, a bottle tree's swell) instead of one upright cylinder; a felled kit
 *       tree leaves its stump (walking meets it) and its trunk lying away from the blast. Action matches in woods move.
 *  59 — 68.2a, the read side of 68.0's order forms: the action bots obey go here (to the place at the formation,
 *       fighting as it goes, then hold), hold and take at a place, engage that unit (seen, ahead of a nearer enemy;
 *       unseen, to where the team believes it is) and crew that seat (the squad's nearest member). Matches with these
 *       orders move; none issues them on its own yet, so no stored case moves; the turn game is unchanged.
 *  60 — 63.3 step 4: walking and driving on the shapes' footprints (a measured structure or a profiled rock in a walker's way).
 *  61 — 63.4 step 8: the supply crate's ruin is lane E's broken stack, which stands at 0.55 of the crate (the stack tipped
 *       over stood at 0.85) — and a ruin blocks as its rubble stands (step 1's rule), so a wrecked crate is lower cover.
 *       Matches where a crate is destroyed move; the turn game is unchanged.
 *  62 — 63.3, lane F's review of step 4: a felled tree's trunk lying along the ground blocks as a row of discs where it
 *       stands above the mover's step (a slim birch's stays under a person's step). Action matches with felled trees move.
 *  64 — Squads of three in every skirmish (68.2b's follow-up, the coordinator: commanding needs them wherever the owner
 *       plays): skirmishMatchSetup's squadSize defaults to 3, not only with commanders or a formation — the player's
 *       order menu (F) has a squad to command in the menu skirmish too. Skirmishes without commanders or a formation
 *       move (the kit's squad rules, marks among them, now have squads); the turn game is unchanged.
 *  65 — 62.47: the wind generator. With decision 120's wind switch on, shells fly the planet's gusting wind (its
 *       strength swelling ± 35 % over seconds and its direction drifting ± 0.25 rad, seeded by match, planet and
 *       segment; core/wind.ts) and smoke clouds drift with the wind where they stand (shelter included). The headless
 *       golden's wind cases move; with the switch off nothing does.
 *  68 — The pistol fires the SMG's cartridge (the owner, Oct 10: "make pistol have same ballistic characteristics as smg
 *       for individual bullets"): three-quarters of the rifle's damage a round (12 before), seven-tenths of its muzzle
 *       speed (0.75 before) and six-tenths of its round's life (0.5 before); its rate, spread, magazine and reload are
 *       the pistol's. Action matches where pistols are drawn move; the turn game is unchanged.
 *  69 — 62.54 step 3: the planet's wind field (core/windField.ts). With decision 120's wind switch on, the wind is bands by
 *       latitude about a seeded axis plus the weather timeline's cells (lows and highs drifting with the bands), gusted
 *       and sheltered; shells fly the field at each planet's reference place. The headless golden's wind cases move.
 *  73 — A structure's shape stands where its model is drawn: core/hits.ts lays the measured parts in math.ts uprightFrame
 *       (the renderer's upright seat, the shortest turn from +y) where it used surfaceTangentFrame, turned by a yaw that
 *       depended on the place and mirrored. Off-centre and long parts move — the gun on its mount, the buildings —
 *       so shots, sight, blasts by shape and walking round structures move on the action clock.
 *  75 — 62.51's look slots (the coordinator's ruling (a); after 73, lane F's, and 74, lane A's): an action bot looks and
 *       deliberates on its team rank's slot (rank mod 6, four ticks apart in the 24-tick sight period; ranked by squad,
 *       then place in it, else id; the second team of each pair ranked the other way, as the spawn lines mirror), not
 *       every bot on the same tick — the 25-a-side look no longer lands on one tick. A 1 v 1 is unchanged (slot 0 for both).
 *  76 — 62.54 step 4: shells fly the whole wind field — the kernel reads each planet's grid (the field sampled on 6·7·7
 *       cube-sphere nodes once a second; lib.rs set_wind_grids, a bilinear lookup on the face) with the gust set every
 *       tick (set_wind_gusts), in place of one vector a planet. The headless golden's wind cases move.
 *  78 — 68.16: a bot's cover spot behind a rock (findCover) stands just past the walk's own clearance round the rock (its
 *       body and half again — 68.15's rule for posts), not inside it, where the walk steered round it or stalled short and
 *       the bot never counted as in cover. Measured (lane B's every-switch cell, 6 seeds): the cover runs that reach the
 *       spot 18 % → 30 %; rounds stopped by cover 167 → 188, bandages finished 87 → 117, deaths 267 → 233, stuck hops
 *       5 → 1. Action matches move; the turn game is unchanged.
 *  79 — 62.54 step 5: weather carried by the wind field's cells (core/weatherCells.ts, behind weatherCells: fronts that move,
 *       fog that pools in low ground) — and the field's cells no longer ride one track: each segment has two, each on
 *       its own keyed latitude within ±60° and longitude, so fronts come and go anywhere. With the wind switch on the
 *       field moves: the headless golden's wind cases move. The action golden's ag-7 (cells and wind on) locks the
 *       switch's behaviour (the turn game does not read the weather, so no headless case would).
 *  80 — 62.54 step 8: the first noise layer — a low-frequency curl noise on the sphere (core/windField.ts curlNoise,
 *       0.02 of surface gravity, swirls about a third of a planet across) in the wind field, behind the wind switch.
 *       The headless golden's wind cases and the action golden's ag-7 move.
 *  81 — 63.3's follow-up (lane F): the gun pit as drawn — its measured shape its parts (the plinth, the carriage and its
 *       cheeks, the breech, the shield, the ammo crate), the gun-layer's seat open, where one box round the turret held
 *       its gunner inside it; and its gunner sits where lane E's model seats it (crouched in its chair beside the breech,
 *       its sight eye at the model's sitting eye; it was stood at the pit's centre). The cases that crew a pit move.
 *  82 — decision 144 (143's first flip): fire lanes (66.6) on by default on the action clock — a bot holds its fire while
 *       a teammate stands in its lane, and steps aside rather than walk into a teammate's lane; `fireLanes: false` turns
 *       it off. Measured (the switch map's base, the same seeds, friendly fire on): team kills 2.8 → 0.3 (6 v 6) and
 *       5.5 → 0.3 (12 v 12), deaths 19.5 → 18.0 and 50 → 41, captures 2.3 → 2.7 and 3.5 → 4.0. Action matches move;
 *       the turn game is unchanged.
 *  83 — fire lanes (66.6): a bot in cover mode — hurt and making for a spot behind something, or holding it — is not
 *       stepped aside out of a teammate's lane; stepped aside it turned off its way again and again short of the spot
 *       (68.16's measure fell 30 % → 14 % once the lanes were on by default, 82). It still holds its fire with a
 *       teammate in its own lane. tests/coverSpot.test.ts passes again; teammate hits with the lanes still 34 → 3 of 179.
 *       Action matches move.
 *  84 — fire lanes (66.6): the step aside goes only to a side clear of the walk's obstacles within a short step (away
 *       from the lane's line first, else the other way; both blocked, the bot walks on as its brain meant). It comes after
 *       the brain, so the walk's look-ahead steer never saw it: bots stepped sideways into trunks and hopped (the steer
 *       test's scene: 1 stuck hop with the lanes off, 7 on; 1 now). The 66.6 check: teammate hits 34 → 6 of 177. Action
 *       matches move.
 *  85 — decision 147 (the first flag retirements): weather and wind on by default on the action clock (the Lab, the menu
 *       skirmish and duel; `weather: false` / `wind: false` turn them off; the range stays calm). The other ten retired
 *       switches were on by default already or are presentation. Action matches move; the turn game is unchanged.
 *  86 — 67.44 (the owner): under a teammate's bandage a person fires and reloads (bots too — only the Match refused); a
 *       step still breaks it off; under its own bandage the hands stay on it. Action matches with a medic at work move.
 *  87 — decision 148 (the owner): squad leaders lead by default on the action clock wherever there are squads (`squadLead:
 *       false`, the Lab's row, `?lead=0` turn it off); 66.5's sectors win over the squad's one facing under a leader's hold.
 *       Action matches with squads move; duels and the turn game are unchanged.
 *  88 — 67.41's follow-up (the owner): Auto draws a one-planet action battle's kind from the seed across all nine kinds
 *       (the stars' pick gave thalia 51 of 60 seeds and never bruma); the range keeps the stars' pick; a forced kind
 *       draws the same world as a seed that drew it. Action matches on most seeds move; the turn game is unchanged.
 *  89 — 63.1i (the owner: "bots get stuck in emplacements and can't or won't leave"): a gunner with nothing to shoot at
 *       for 10 s, its squadmates on foot more than 15 people off, and not told to crew that gun, gets out, does not crew it
 *       again for 60 s, and its plans let the seat go (both brains' paths). Four Lab skirmishes with pits and HMGs: sitters
 *       (seated, the squad >15 people off for >20 s) 22 → 12, the HMGs' 10 → 0; the 12 left are gun pits firing (10–12
 *       shells each), the guns behind the lines of 126. Action matches with guns move; the turn game is unchanged.
 *  90 — 63.9 (the owner: buildings "functional as cover and prevention from walking or firing through them"): where
 *       buildings are sized by function, a building's or a ruin's box walls its outline — discs along its sides, not one
 *       disc round it — so feet stop at its walls and corners and the cover map's points stand along them; a respawn
 *       spot inside a building moves out of it. Action matches with a ruin or a building in reach move; the turn game and
 *       the classic scale are unchanged.
 *  91 — 63.5 step 6, decision 155 (the owner: "shift things over to all use ground material system"): the ground's
 *       materials are no switch — the action clock's ground has them (match/clockPolicy.ts `ground`), so every action
 *       match's craters dig and look by the ground and its walkers' pace and footing follow it, and the bots route on firm
 *       ground; the turn game's ground stays plain (the headless golden unchanged). The Lab row, `?groundmaterials`,
 *       `--ground-materials` and `GameConfig.groundMaterials` are gone.
 *  92 — decision 156 (the owner: "the bots kind of suck at shooting the 50 cal, they just miss a ton"): a seated gunner's
 *       eye — the emplaced HMG's sat seat stands on the fine ground (its gunner's eye sat 0.25 people under it: blind, its
 *       rounds into the ground), and a hatch seat's stand is set at mount (the APC cupola's gunner got it only once the
 *       vehicle drove: parked, it looked and fired from inside its hull); the mounted gun is laid as the rifle is aimed
 *       (lead, its own round's drop, wind), fires only on a clear line, and holds past 40 people. Action matches with an
 *       HMG or an APC move; the turn game is unchanged.
 *  93 — E5.4 steps 4–5 (ARCHITECTURE §5.17; decisions 157, 158): the planet forest in the sim, behind GameConfig.planetForest
 *       (the Lab's Planet forest row; off by default): the cells within 40 people of each unit's cell (weighed every 30
 *       ticks) and under every low shell, bullet and grenade (every tick) join at the act phase's start, the forest
 *       function's trees on the setup's ground appended to flora; a cell long out of reach leaves, its felled trees and
 *       their health kept by id. With the switch off nothing moves (every stored case as was).
 *  94 — 62.52, the owner (Oct 11): "we should cut wind bullet influence by half" — ACTION_WIND.bullet 8 → 4: a bullet (a
 *       rifle's, a pistol's, a mounted gun's) drifts half as far in the wind, and the bots' lead, the drop dot and the
 *       seated aim read the same number. Action matches with the wind on move; rockets, grenades, drones and the kernel's
 *       shell wind are unchanged, and the turn game (no bullets) too.
 *  95 — lane B on the Foothold board: "any jump kills the jumper as lostInSpace" — a body was lost past 80 from every
 *       planet's centre, and the megaplanet's ground (radius 78.5) stands at 84–98, so whoever left it (a jump, a blast's
 *       push) died on the first airborne tick. A planet's reach is max(80, 2 × its radius) (core/physics.ts lostInSpace):
 *       megaplanet matches move; classic planets (under 40) keep 80 exactly; no stored case moves.
 *  96 — 63.1f's follow-up, the owner in a gun pit (Oct 12): "I can't figure out how to turn the cannon left / right" — the
 *       sim laid the pit's gun by its gunner's bearing but drew the turret fixed. Now the turret is drawn turning with the
 *       bearing and its gun-layer sits swung with it about the turret's axis (SEAT_POINTS' pivot, the HMG's rule), so a
 *       pit gunner's body and eye move with its gun; and (the coordinator: no false cover) the turret's parts — the shield —
 *       stand turned with it in the shapes, the part damage and the shield's piece (StructureState.turretYaw, partFrame).
 *       Action matches with a crewed gun pit move; the turn game is unchanged.
 *  97 — 69.6c, the tasks' trim (lane D; lane C's Foothold diagnostic: the commanders' mark tasks piled up on attacking
 *       squads, p90 8, at most 65): when a task is added to a squad, its tasks past their expiry go, and a mark goes once
 *       the team's picture no longer reads it (under BELIEF_FLOOR, ~111 s) or once the squad has marked the same enemy again
 *       (core/squads.ts liveTasks). Action matches whose squads mark move (skirmish/classic/ag-4); the turn game is unchanged.
 *  98 — the marks grounded (lane D, the owner's "black squares in the sky"): markPos puts a mark where a person there would be
 *       (the body's centre over the ground), not on the planet's nominal sphere — the bots aimed at a floating point. And
 *       the hold's half (lane C): a mark or a track a holder cannot see from its post no longer pulls it off the post (no
 *       walking peek toward a belief); it holds, peeks from the post, or fires on the believed place from it (area fire).
 *       Action matches with marks or holds move; the turn game is unchanged.
 *  99 — 65.4, the pistol swap by reach (lane B; lane C's Foothold diagnostic: a quarter of the kills with pistols, no rifle
 *       ever dry): a pistol drawn over a slow reload goes back to the still-loaded rifle; with the target past the pistol's
 *       reach (15 people) or in its cover, an empty rifle is reloaded rather than swapped; up close in the open the quick
 *       swap stays. Foothold 0.5, 600 s x 4: pistol kills 23 of 63 -> 0 of 54. Merged un-recorded with #1058; recorded here.
 *       Action matches whose bots swap to the pistol move (skirmish/classic/ag-4); the turn game is unchanged.
 * 100 — 63.8, the gaits (the owner: "some fast jog as a sprint … the limitation being that you can't shoot"): a move
 *       (1.5 × the walk, its fire unaimed) and a sprint (2 ×, no fire), standing on foot, for the player and the bots; the
 *       bots sprint a bound's or a tactic's leg, a withdrawal and a run for cover, move on a walk farther than 6 people with
 *       nothing in sight, walk the last 2 people of any way, a hold and a seat's walk at the walk — and run only out of the
 *       rifle's effective range of every enemy they know (inside it, the shooting walk). Action matches move.
 * 101 — ARCHITECTURE §5.16 step 3, the decide phase's two start-of-tick reads (agreed by lanes B and C): a teammate's fire
 *       lane is read from its brain as the start-of-tick state holds it, each team's lanes built once a tick (62.57 cut 2's
 *       refresh after each decision gone; the bot's own lane from its own fresh brain), and the team's tracks are read as
 *       the start-of-tick state holds them plus the unit's own edits, every unit's edits applied after the decide phase in
 *       the players' order. A decision no longer depends on the order the units decide in. Action matches move.
 * 102 — 63.5's last reader, cover by the ground (the coordinator: a version, no switch): bullets meet the passing ground
 *       by its material (GROUND_BULLETS — soil, rock, ice and salt stop a round; sand, ash, mud and snow let it through
 *       a thin berm, slowed, stopping under GROUND_BULLET_FLOOR of its speed), soil wears to pass rounds as sand after
 *       GROUND_WEAR.hitsToPass rounds a person-sized cell, a crater's lip is a cover post, and the cover map reads the
 *       ground: shelter behind ground that lets a round through is concealment, not cover. Action matches move.
 * 103 — 69.6d, spawn facing the objectives (lane D; the owner, playing: "play should spawn facing objectives"): a unit placed
 *       at the start or on any respawn (the spot, relay drops, beacons, X19 arrivals) faces the nearest objective of
 *       interest to its side — under a push or the tug the nearest in play, otherwise the nearest capture point its side
 *       does not hold, else the enemy's base (core/capturePoints.ts facingObjective). The bots' first look turns with it.
 *       Action matches move; the turn game is unchanged.
 * 104 — E5.4, the planet forest on by default with its density by relief (the owner: "some dense forests where it makes
 *       sense"; on the sheet, "Forest plan pretty good"): an action match at a person's scale holds the planet's forest
 *       round its units (core/forestHeld.ts) unless the setup says `planetForest: false`, and a stand's chance follows the
 *       ground (core/forestDensity.ts: valleys and drainage wooded, ridges open, a dense core) unless `forestDensity:
 *       'flat'`. Action matches at a person's scale move; classic and the turn game don't.
 * 105 — 65.30, cartridges (decision 179): each bullet weapon names its round (constants/ammo.ts); a round's speed bleeds by
 *       quadratic drag (core/ballistics.ts) and its damage with it, quoted at 30 people (the muzzle's more); the .50 60
 *       there (was 36), a two-hit kill; the bots aim on the low arc at the drag's speed. Action matches move.
 * 106 — 67.52, builds on the ground (the owner: the HMG and the FOB "slightly too deeply embedded"): a person-scale
 *       build's base stands on its spot's fine ground (StructureState.baseLift, core/buildSeat.ts) — soft ground the ring's
 *       mean less 0.1 people, rock its highest less 0.03 — in its hit shapes, its position, its seats and eyes. Action
 *       matches at a person's scale move.
 * 107 — 63.11, small craters (the owner: "small shallow craters to rocket impacts and smaller shallower to grenades"):
 *       a rocket's, a charge's or a grenade's burst on the ground and a vehicle's destruction leave a craterlet in the fine
 *       ground (core/craterlets.ts; rock and ice none), merged into a bowl it falls in; GameConfig.smallCraters, on by
 *       default. Action matches at a person's scale move.
 * 108 — 63.11 off by default (the owner, Oct 13: "craterlets don't actually work in testing, going to leave them off for
 *       now but we can add that to a future item"): GameConfig.smallCraters on only when true. Action matches at a
 *       person's scale move back to no craterlets.
 * 109 — 181's bandage gap: a bot's heal and patient gates read its newest look as well as the plan's target, so a bot hit in
 *       the deliberation delay after first seeing an enemy no longer bandages with it in sight. Action matches move.
 * 110 — 181's attention on by default (the owner, Oct 14: "attention seems like a good improvement"): GameConfig.attention
 *       on unless false — a bot sees where it faces, a hit from an enemy it had not seen costs a startle. Action matches move.
 * 111 — 69.3c, an arrival held till its pod lands (the owner: "bots showup on the map before the drop pod lands, can shoot,
 *       then once the drop pod animation happens they can move"): at a person scale a respawned unit has ARRIVAL.fallSeconds
 *       of landingLeft — its intent idle, unseen, not a target, not on a point. Action matches at a person scale move.
 * 112 — 66.19, cover moves on by default (the owner, DECISIONS 187/189: flip now, diagnose later): GameConfig.coverMoves on
 *       unless false; an assault (a take, a play's bound) keeps bounding under fire while the local read favours it; a failed
 *       under-fire cover search waits underFire.retrySeconds. Action matches move.
 * 113 — 65.42, the drone harder to bring down (the owner: "For drone, I think fly higher (16) accept view area change, 2 hits
 *       to bring down"): DRONE_ALTITUDE 8 → 16 people, its sight radius with it (8 → 16 people round its ground point), and
 *       DRONE_HITS 2 bullets (a burst still downs it at once). Matches with drones move.
 * 114 — 65.46, the rocket coasts (the owner: "once the motor runs out would be cool if the rocket stayed the course with a
 *       low speed and relatively quickly decaying ballistic trajectory and still made impact"): past its 2 s burn a rocket falls
 *       by 0.01 of the felt gravity, its speed halving every 30 people, for 6 s more, and bursts where it stops (it was spent at
 *       its burn's end). Action matches with rockets move.
 *  115 — 209's footing follow-up (lane F): the walk's outline for a building sized by function (core/hits.ts outlineDiscs) —
 *       a box walls itself as a row of discs from 1.5 times its width (3 before: a depot's container filed one disc 2.2 wide and
 *       held a body 1.4 people off its sides). The person-scale buildings stand seated on their plinths (baseLift). Action
 *       matches at the person scale with buildings or ruins move (the walks round them).
 *  116 — 63.17 tree feet (lane F): a tree's walk circle is its wood within a person's height of the ground (core/surfaceWalk.ts
 *       woodFootRadius; the widest drawn trunk at any height before — a giant oak's 1.5× its foot), and a giant's shots meet its
 *       upright trunk as drawn (giantTrunk: the bottle tree's belly; the crown no longer stops them). Action matches with
 *       trees move.
 *  117 — 69.14 the motor pool's bugs (lane E): the pool on its own terrace, laid before the base's decks are seated (with the
 *       lighter strip's), its site past the decks by its plinth and band; its stacks, stacker arm and corner robots modules of
 *       its layout (walk, hit and fire lines; StructureState.stacks synced from the stock); the unpack pad along x, rides and
 *       walkers routed by its gates without the modular switch; and the route graph's projection (core/roomGraph.ts localOf:
 *       at the base's radius, where the planet's sent every aim short by the ground's share) — every match with modular
 *       buildings or the pool moves.
 *  118 — 69.14's fix-forward (lane E): lighters stand the motor pool too (src/match/setup.ts; the owner: "the hesco barriers and
 *       barbed wire and gate and stuff became invisible" — since 117 a pool is drawn only where it stands) — a lighter match
 *       carries the compound, and with a stock its lost vehicles unpack on the pool's pad and drive out of its gate.
 *  119 — 69.14's second fix-forward (lane E; the owner's own Lab launch): the pools' ids past the highest (core/motorPoolStock.ts
 *       withMotorPools) — the base planner drops what stands in a base's way first, so the count fell short and a pool took a
 *       standing structure's id (his side's, a drop container's: one mesh an id, the compound's walls never drawn); every match
 *       where a structure was dropped before the pools were made moves.
 *  120 — 69.19 step 1 (lane E; DECISIONS 237 (8), the owner: "we need to be able to control what's in reserve and whats unpacked
 *       for whom at the start of the match"; "A is fine"): GameConfig.startUnwrapped — a side's start fleet partly packed in its
 *       motor pool (core/motorPoolStock.ts packStartFleet), unpacked by kind (bike 8 s … tracked 30) to a ready line outside the
 *       gate; the pools stand where a side packs. The Lab's and the page's default (auto) packs some side; absent, all, as before.
 *  121 — 69.19 step 2 (lane E): the bots wait for a ride due from the pool (ai/apcLoop.ts RIDE_WAIT_S: a squad with no transport
 *       in reach holds by the ready line while one is due within 45 s, one squad a transport, before the spare seats are shared);
 *       the push's attackers start with every vehicle packed (START_UNWRAPPED.autoAttacker none, the owner: "start unpacking fresh").
 *  122 — 69.22 (lane E): the pool sited clear of a steep base — its terrace's coarse apron kept off the base's decks and skirts
 *       (Terrace.apronKeep), a hardstand under the compound and its ready line, the site past the base's skirts and the mesh margin,
 *       the site kept from the terrace to the compound; and a team kept on foot (68.25) crews no vehicle's gun and drives no
 *       technical — every match with a pool moves.
 *  124 — 66.22 (lane C): a squad leader's posts decision asks at most POSTS_LEAN_BUDGET (64) leans (ai/actionPositions.ts
 *       leansToAsk: past it, the posts that would rank highest with a lean) — where more would be asked its chosen posts can
 *       change (lane F: the owner's URL with 62.68's rate, tick 6021); its props read near the place (the same answers). And
 *       69.21's second tube (lane C): the rocket mount's own 0.5 s between rounds (MOUNT_LAUNCHERS.rocket.interval, the
 *       shoulder LAW's 1 s before) — the bot gunners' second tube goes sooner in any match with rocket technicals.
 *  123 — 62.68 (lane F): every bot's brain at 30 a second, staggered by its look rank, on by default (BRAIN_RATE).
 *  125 — 69.28 (lane F; DECISIONS 242): the supply dumps open to all sides; up to 10 technicals a side.
 *  126 — 69.39 (lane F; DECISIONS 253 (1)): the vehicle zones (63.12, 243–248's armour by where a vehicle is hit, 246's
 *       cumulative wear) on by default on the action clock (core/vehicleZones.ts zonesOn: on unless false; the classic turn
 *       game's only when set) — every action match with a vehicle in it.
 *  127 — 69.52 step 1 (lane F; DECISIONS 261, 262 (a)): a ship's route bent round the point defence its side has seen (core/
 *       skyRoute.ts; GameConfig.skyRule `route` by default, `refuse`, `fly`; a call's `hot` straight in) — every called
 *       flight and wave sortie in a match with point defence on and a mount seen.
 *  128 — 69.52 step 2 (lane F; DECISIONS 261 (4), 262 (b)): a pilot's risk (cautious by default: the route's berth past known
 *       cover widened; a hot call 1) and the weave (a ship's track swung SKY_WEAVE.amplitudePeople either side, its heading with
 *       it, eased out at its ends; GameConfig.shipWeave) — every dropship flight and sortie wherever ships fly.
 *  129 — 69.52 step 3 (lane F; DECISIONS 262 (b), (c)): evasion under fire (core/skyEvade.ts) — a ship on its way in that is
 *       locked, chased by a missile or hit answers by its risk: the cautious default breaks off home undelivered, a middle
 *       risk drops low, a bold one or a hot run jinks — every flight that meets point defence or a missile.
 *  130 — 69.52 step 4 (lane F; DECISIONS 262 (d)): the bot commander's hot calls (ai/dropRules.ts hotWanted) — its dropship
 *       called hot when its side trails on points and known cover stands over the landing or across the way in.
 *  131 — 69.15 (lane F): walls block spotting (core/hits.ts wallsAcross, in core/fineTerrain.ts personLineOfSight) — a
 *       standing building, a wreck, a container, a frame or a ruin across a sight line hides what is behind it, game-wide.
 *  132 — 69.43 (lane H; DECISIONS 255 (2), 275): the modern guns' fire rates doubled (ACTION_FIRE_INTERVAL 0.25 → 0.125 and
 *       its multiples — the rifle, the SMG, the LMG, the .50, the coax — and the pistol 0.3 → 0.15), the classic guns on
 *       intervals of their own (the Garand 0.3, the Thompson 0.09, the BAR 0.16), and the bots' aim error halved at every skill.
 *  133 — 69.38 step 2a (lane C; DECISIONS 274 (e)): the let-out slots apart, on by default (GameConfig.letOutSlots) — each
 *       carrier bound for an objective lets out at its own slot, at least 12 people from the others (ai/apcLoop.ts letOutSlot).
 *  134 — 69.66 (lane F; DECISIONS 279): the dropship's path — each leg straight runs joined by one or two banked bends (the
 *       swing 20 people, SKY_BENDS), the way in ending in a hard banked S onto the LZ (a gunship's without it), the cruise
 *       sampled along the bent track; it replaced 69.52 step 2's weave — every dropship flight and sortie wherever ships fly.
 *  135 — 69.65 (lane H; DECISIONS 289, 278): the blast model on by default on the action clock (GameConfig.blastModel;
 *       core/explosions.ts burstModelFor) — a person by two radii from the nearest of its body (kill, wound: half the real
 *       round's in people), a hull and a structure by kind (a shaped charge whole on contact, its blast beyond; HE over its
 *       kill radius), the emplacements' shells as a 75 mm and a 105 mm.
 *  136 — 69.67 (lane F): a cover search's rays read the ground out to the threat's reach (a rifle round's flight at the
 *       scale: TERRAIN_SPOTS.rayReach, core/coverMap.ts), not a person a step to a threat 1,863 people off — a spot judged
 *       against a threat beyond it may read differently (no stored case moved: the golden, 24 seeds at both scales and the
 *       owner's board read the same; marked so a replay at 135 does not run on it).
 *  137 — 69.64 (lane H, DECISIONS 288): the artillery on its leans — a round's dispersion (a CEP of 0.3 % of its range), a
 *       seen fix's circle 3 people, the shells' wind share 0.02, the towed guns' charges (450–960 people a second, fall 0.2),
 *       the mortar laid for the wind and its circle 3; point defence with the guns sees a strike round 3× as far at 3× its
 *       chance, and a mount spares its own side's strike rounds (before, it shot at them). The golden reads the same (no
 *       case of it calls a strike); every called strike and mortar mission moves.
 *  138 — 69.67 step 5 (lane F; ARCHITECTURE §5.32): a strike's lays spread over the ticks after its call — the call, its
 *       refusal and its spend at once, its rounds' aims drawn there; then one lay step a tick on that tick's ground (the plain
 *       solve, a wind pass, the correction; ~15 for the battery's salvo, ~0.13 s), announced, warned and fired once the last is
 *       laid. Laid on ground that did not move, the very rounds as before; the announce and the state between move. The golden
 *       reads the same (no case of it calls a strike).
 *  139 — 69.72 step 1 (a) (lane F; ARCHITECTURE §5.33 item 1): the moving hulls never served stale — the hull memo
 *       (core/hits.ts movingShapes) keeps which driving records it was built from, where, and rebuilds when stepDrive (or
 *       anyone) has written a new record into the array in place; before, a vehicle moved after the memo was taken stood at
 *       its start-of-tick pose for the rest of the tick (a tick's travel). Bullets, walkers, riders and sight against a
 *       moving hull may read differently. The golden reads the same; story-1's fight board ends the same at 180 s.
 *  140 — 69.49 (lane C; lane H's finding from the 300 flips): the gun-seat rule (156, ai/actionBot.ts seatToCrew) sends no bot
 *       its squad has sent to demolish — the gun's run was an errand that put off the board step, and the strike's driver
 *       flipped between its APC's wheel and its walk (point defence on: a free gun seat by the base; the strike walked). On by
 *       default: a person's demolish order reaches it. The golden reads the same (no case of it gives a demolish).
 *  141 — DECISIONS 300 (lane H; 69.70): the eighteen flips on by default on the action clock (core/defaultOn.ts: on unless
 *       false; the turn clock's only when set) — smoke at arm's length, the jump feel, the local steer, real timing, 3
 *       airstrikes, a gunship and a helicopter a side, ship pickups, hit locations, suppression, the GL-40 stock, the motor
 *       pool's stock, the crewman role, flares, point defence, the towed guns, ship turrets; with hit locations the bots aim
 *       at the chest; a forward AD site keeps its own missile count; a goal in a modular building's room left to the room
 *       graph. Every action-clock match moves; classic does not.
 *  142 — 69.42 (lane C, swapped with lane F's 143 by the coordinator; the terraces' planned "vehicle ramp at the gate"; lane
 *       H's finding): a terraced building's vehicle gates take ramps (Terrace.ramps, in the record: core/terraces.ts gateRamps) — across a ramp's lane a cut face at
 *       TERRACE.rampBatter (0.6), not the cut's 1.5. Every motor pool's back gate opened on a cut face (0.1–3.4 people of rise
 *       in 6, at 1.5: a truck climbs 1.08) and no vehicle passed it. The ground at those gates moves; the golden reads the same,
 *       the pool on by default (141). And the commander's relay builds leave a live play's squads, as they left a
 *       mounted play's (a build's planter ended a play the round it formed: story-4 ½, a play every 10 s from 620 s).
 *  143 — 69.74 step 1 (lane G, DECISIONS 302; the coordinator's number, ahead of lane F's claims still integrating): a
 *       helicopter whose flight ends with riders still aboard lets them out of its doors where it ends, and a pickup flown home
 *       (69.17's rejoin) lets its squad out at its landing, not its origin (core/dropship.ts pickupHome). Helicopters are one a
 *       side by default (300), so the bots' own reach it; it fired in none of 6 Story matches of 600 s and the golden reads
 *       the same. The player's calls (Fly there, Helicopter fire support) are inputs only.
 *  144 — 69.77 (lane H, given by the coordinator after G's 143; lane H's Story read): the towed guns'
 *       charges sized to the planet (core/airstrike.ts gunCharges: the top at most GUN_CLASSES' topCircular, 1.12, × the
 *       round's circular speed, the owner's board's own ratio). On the Story's planets every charge was past escape, the
 *       reach 0 and every strike refused; now 5–12 strikes a match (story-1…8, 0 before). The owner's board keeps its
 *       charges; the action golden reads the same. A call no gun lays is refused "Out of range" (callAirstrike's inline
 *       lay dereferenced the dropped strike).
 *  145 — 69.83 (lane H, the coordinator's number after gun-reach 144, ahead of F's claims; the owner's kill feed): a mortar
 *       mission's rounds carry `by: 'mortar'` (Bullet.by; absent on every other round), so the feed names the mortar, not
 *       the guns. Nothing reads it in the sim; the per-tick checksum hashes it, so a match with a mortar mission moves its
 *       checksums, not its outcome. The blast event carries its round's `fromShip` and `by` (an event, not state).
 *  146 — 69.72 step 5 (lane F; DECISIONS 304, the order precedence): one claim a squad. A task's rank (the human, a member's
 *       direct order first; being lifted; a named errand; a play; the story's objectives; the commander's routine) decides
 *       what is in force, the newest within a rank; a lower claim onto a squad or unit held higher is refused ('claimed');
 *       a task replaces the older of its rank or lower, so a lapse falls back to the posture; the stale tasks are cleared
 *       every tick; every order is tagged with its issuer. The bot commander picks among claimable squads in place of its
 *       planners' guards. Bots' orders, plays, strikes, carries and errands move; the golden moves.
 *  147 — DECISIONS 309 (lane G, the coordinator's number after F's 146; the owner: "Story only"): the Story
 *       preset turns the Helipad on (69.74) — each side's helicopter parked on a pad behind its HQ, flown off it and back,
 *       turned round there; the pad a structure the base planner settles. The Story's matches move; the default skirmish
 *       (no board, no bases) and the action golden (no Foothold board) do not.
 *  148 — 69.79 step 1 (lane E, the coordinator's number after G's 147): the search keeps its squads moving.
 *       The base's searcher claims its squad (`search`, rank 5.3) — the replan's posture cleared its move at 10 s — and a
 *       searcher no nearer its cell in INTEL.stallSeconds is let go and another sent. Moves the Story (intel on); the
 *       action golden reads the same.
 *  149 — 69.72 step 5's follow-ups (lane F, the coordinator's number after E's 148): a lift's claim (rank 2) holds past
 *       its ship's lift while a member of its squad is still aboard — the pickup's boarded units, a helicopter lift's
 *       riders not yet out — and lapses when they are out (SquadTask.aboard; core/squads.ts pruneTasks). Both hosts send
 *       a commander's order to its first living member's squad (ai/squadOrders.ts orderTo), so a stray first member under
 *       68.10's fluid squads no longer drops it as a bare 'unknown'; one with nobody in any squad is refused 'noSquad'.
 *       The Story's matches and fluid-squad skirmishes move (a lifted squad, a stray first member); the action golden
 *       reads the same.
 *  150 — 69.85 (lane H, the coordinator's number after E 148 and F 149; lane E's standing squads, story-7's q1001): a
 *       parked wave ship's ramp is down only while its boarding is open or riders wait aboard for take-off (core/dropship.ts
 *       padRampDown, syncShipContainers, re-laid by stepShipBoarding when they differ); shut, its container is the one box,
 *       walked round. It was always open at the back: a squad member walking past went in with boarding shut and stood at
 *       the front wall (story-7: u15 from 67 s to the end at v145; u17 807–866 s at v146; u13 from 1106 s and u19 from
 *       1546 s to the end at v147). Whoever stands in it unboarded when it shuts steps out down the ramp, as the seat key's
 *       step-out. Flight containers stay open. Bots' walks round a parked ship move (story-7: team 0 at the limit, 4–5 →
 *       team 1, 3–5); the action golden reads the same.
 *  151 — 69.61 + 69.86 (lane C; one branch, one number, the coordinator, Oct 27: 69.61's guard quieted story-8 alone).
 *       69.61 (the coordinator's brief after lane C's escort sweep): the story's escort is a column whatever the distance —
 *       planEscort asks only for the combined force (core/plays.ts mountedForce), not mountedReady's walk: on
 *       story-6…8 the escort's point lay 202–283 people from home, inside MOUNTED.walkPeople, and neither side ever formed
 *       it. And 69.38's guard: a column's laggard that goes CONVOY_MARCH.stuckSeconds without closing closePeople on its
 *       place falls out, an APC too, so the head no longer holds for a 6×6 nobody drives (6a5e54db story-1: held two looks
 *       in three, the escort never done). On by default with the Story preset's escort; the golden reads the same.
 *       69.86 (DECISIONS 311, the owner: "Scale + mount window"): a column halts on real contact only — a member
 *       hit, or more than CONVOY_MARCH.contactFew (2) enemies its side knows near it; a scout or two held lab-7's herringbone
 *       halted for minutes — and a column re-formed from its herringbone marches CONVOY_MARCH.mountSeconds (30) before new
 *       contact halts it again. And a vehicle in a mounted play passes a shape near its nose on the side it already lies
 *       (ai/rideBrain.ts noseSide): story-2's escort 6×6, boxed in at home, kept an old side, swept its nose across a parked
 *       carrier and was wedged two minutes. Behind the Story preset's mountedAssault: escorts done on story-1…8 and lab-7
 *       14 → 18 of 18 sides in 900 s.
 *  152 — 69.79 step 2 (lane E, the coordinator's number after F's 149, H's 150 and C's 151): the strike's search falls
 *       back to the base search's guards — when none of the destroy strike's squads is fit to search, any squad nothing
 *       claims above the routine (HE or not, no crew seated, not holding) is sent, claimed for the search. Moves the
 *       Story (intel on); the action golden reads the same.
 *  153 — 69.91 (lane E, the coordinator's number after its 152): (a) on the action clock the capture circles are cleared
 *       at setup of the map's rocks and flora whose walk footprint reaches them (MatchSetup `clearPoints`, default on);
 *       (c) a side's one open objective left on a point draws every squad no rule above gave an order. Moves the Story
 *       (story-1, story-2); the action golden and the smoke read the same.
 *  154 — 69.90 (lane H, the coordinator's number after E's 152 and 153; lane E's story-6 trace): the bot steering dither.
 *       A modular building's route reversed a walk every call (core/roomGraph.ts): a last door whose way out to the goal is
 *       walled ranks after a clear one, and each building sees the goal cut to the walk's great circle at two of its reaches.
 *       And a walk to a place more than two person lengths off is stuck when its net way over walk.stuckWindowSeconds (2)
 *       is under half the stuck count's share (actionBot.ts, the brain's stuckFrom). Bots' walks move: dithering on foot
 *       ≥ 30 s, story-1…8, 42 units 11,114 s → 9 units 957 s; the action golden moves.
 *  156 — 69.87 (lane F, the coordinator's number after E's 155): the bot commander plans over the Match's squads (GameConfig
 *       `commanderSquads`, Lab `cmdsquads`, on by default on the action clock), not its own fixed threes; a drop squad it
 *       called keeps its drop's take till the point is the side's. Moves the Story from the first re-plan after a drop
 *       squad forms; the action golden reads the same.
 *  157 — 69.93 (lane E; DECISIONS 315, the owner: "Take it at 155" — F's 156 landed first, the coordinator's 157): a
 *       hold's keeper at a capture point is the member on foot nearest it, ties to the lower id (ai/actionPositions.ts
 *       chooseCoverPosts) — it was the last member by id, wherever it stood. Holds at points move: the Story's time with
 *       nobody in the circle 20/23 % → 13/9 % (t0/t1).
 *  158 — 69.88 (lane C, the coordinator's number after E's 157): a stuck ride whose nose is on ground too steep for it
 *       backs up with the wheel toward its line (the drive yaws the way the wheel points in reverse too), and a hull beside
 *       it is not behind it (rideBrain.ts hullBehind asks the drive's rear hull test). Behind GameConfig.mountedAssault:
 *       moves the Story's mounted plays (story-3, story-8, story-14); the action golden reads the same.
 *  159 — 69.96 (lane F, the coordinator's number after E's 157 and C's 158; DECISIONS 316, 317): the Story's verdict. At
 *       time, and when a side's tickets run out, the sides rank by story objectives done, then points held, tickets, alive
 *       (ModeRules.timeVerdict, from win conditions naming 'objectives'); all objectives done still wins outright. Moves
 *       the Story's winners (story-1, story-8 at 1,800 s), not its play; the action golden reads the same.
 *  160 — 69.85 (b) (lane C, the coordinator's number after F's 159): the mortar's muzzle speed on a planet is capped at
 *       MORTAR.topCircular × the strike round's circular speed there (core/mortar.ts mortarSpeed, as 69.77 caps the guns'
 *       charges): on the Story's planet every round of 165 left it and every call was refused. Moves the Story (its mortars
 *       fire); the owner's board keeps 165; the action golden reads the same.
 *  161 — 69.85 (a) (lane H; DECISIONS 318, the owner's "smarter throws only"; the coordinator's 161, after C's 158 and 160 and
 *       F's 159): HE at an enemy it cannot shoot only on a belief at most 2 s old (P.grenade.heHiddenFreshSeconds,
 *       ai/strikeRule.ts crewOrCluster hiddenFresh), and a unit's mark dropped once it is dead (core/marks.ts pruneMarks).
 *       The Story at v157, story-1…8: HE at people 375 → 258, at the dead 17 → 9 %, sides run dry 11 → 6 of 16,
 *       destroy objectives 7 → 12 of 16, so the Stories end sooner (9,839 → 7,340 s in all).
 *  162 — 72.1 (lane H; DECISIONS 320; the coordinator's 162, after H's 161): a modular building at 0 is its layout's ruin —
 *       every module destroyed, the ground floor's walls' heaps (core/moduleShapes.ts layoutRuin, read by structureHitbox,
 *       its ruin height 1), drawn so (rendering/moduleLook.ts buildLayoutRuin); it kept the layout's shapes, roof and all,
 *       at the old model's ruin height. Under the step-over, a ruin is walked, seen and shot over. Modular buildings are on
 *       in the Story and lab-38; the action golden reads the same.
 *  163 — 69.79 step 3 (lane E; the coordinator's number after 159 F, 160 C, 161 H, 162 H): the destroy's strike from a squad
 *       with someone crewing — whoever crews a driver's or gun seat stays at it and the squad's people on foot strike, the
 *       demolish naming them (ai/squadOrders.ts planObjectiveStrike); the planner dropped any squad with one crewing. The
 *       Story's destroys done 7 → 10 of 16; several Stories end sooner by objective.
 *  164 — 69.79 step 4 (lane E; the coordinator's number, stacked on 163): once the side owns its hold's point, one squad is
 *       claimed there (`hold`, rank 4.9: over the carry, the strikes and the search) until the hold is done or the point
 *       lost — one on foot first (ai/squadOrders.ts planHold). The Story's holds done 12 → 14 of 16; all objectives 74 → 78.
 *  165 — 63.14 item 48, the door approach (lane F, the coordinator's number after E's 164): a walk whose straight line
 *       through a building's way would meet the way's line outside the lane a body clears its jambs in aims at the way's
 *       front first (1.5 people out), then through (core/roomGraph.ts throughWay) — beside a door along its wall, the walk's
 *       discs slid it to the jamb and it stood there. Behind GameConfig.modularBuildings, on in the Story and lab-38: moves
 *       the Story's walks into buildings; the action golden reads the same, its stamp re-recorded.
 *  166 — 70.3 (lane C, the coordinator's number after F's 165): cover read across the body (GameConfig.coverAcross, Lab
 *       `coveracross`, on by default on the action clock) — a prop hides a stance where the lines to the hit sphere's centre
 *       and either edge all meet it (core/coverMap.ts COVER_ACROSS: the cover map's points, `shelter`, the posts' prop tier),
 *       and a holder at a trunk that hides no standing body kneels to lean round it. Moves the bots' cover (story-1…8:
 *       birches taken about half as often, deaths flat, objectives 81 → 72 of 96); the action golden moves.
 *  167 — 71.2, things on the real ground (lane F, the coordinator's number after C's 166; DECISIONS 324, ARCHITECTURE §5.36
 *       (3)): a rock, a tree, a felled stump and a fallen limb stand on the fine ground — the one ground's `ground` level at
 *       their footprint's lowest point (a rock's walk disc; a tree's trunk) less a sink by its dry material (PROP_SINK_PEOPLE:
 *       snow 0.3 people, sand and ash 0.1, mud 0.08, soil 0.04, salt 0.02, rock and ice none) — in the shapes, the walk's
 *       discs, the crowns, the cover slots, the debris and the drawing alike (core/propSeat.ts); they stood on the coarse
 *       mesh, sunk into the relief (the owner's depressions). Rocks no longer flatten the drawn relief. Moves cover heights
 *       and sight lines wherever the fine ground is (the action clock); classic is the mesh, bit for bit.
 *  168 — 72.3 (a), the module blast in people (lane H, the coordinator's number after F's 167; DECISIONS 333): a blast wore
 *       a modular building's modules over 1/18 of its reach (core/damage.ts read the modules' distances, in people, against
 *       the radius in world units); now over its whole circle. With modular buildings on (the Story, lab-38): a burst or a
 *       shell near a house breaches the walls it reaches, so sight lines and the bullets' way through move.
 */
/** 209 (5): how near the parked wave ship's ramp's foot the seat key boards it (people). */
const SHIP_BOARD_REACH_PEOPLE = 2;

export const BATTLE_VERSION = 168;

/** What one airborne tick did, for the host to present (log, animate, sound) or count. */
export type AirborneEvent =
  | { kind: 'landed'; playerId: number; cause: AirborneCause; planetIndex: number; damage: number; died: boolean }
  | { kind: 'diedInStar'; playerId: number; cause: AirborneCause; starIndex: number }
  | { kind: 'diedInWormhole'; playerId: number; cause: AirborneCause }
  | { kind: 'lostInSpace'; playerId: number; cause: AirborneCause }
  | { kind: 'warped'; playerId: number }
  | { kind: 'geyserBoost'; playerId: number; pos: Vec3 }
  | { kind: 'killCredit'; killerId: number; victimId: number }
  | { kind: 'cargoRespawned'; playerId: number };

/** A point scan (flyover, probe in flight) into the scanner's fog: what it found. */
export interface ScanResult {
  scannerId: number;
  pos: Vec3;
  spotted: number[];
  spottedStructures: number[];
  terrainRevealed: boolean;
}

/** One airborne tick: events in order; the flyover scans it made; whether the match ended. */
export interface AirborneStep {
  events: AirborneEvent[];
  /** Any player changed in a way the host must present (landing, death, warp, boost, credit). */
  playersChanged: boolean;
  gameOver: boolean;
  flyovers: ScanResult[];
}

/** A beam's result (Match.fire with a hitscan weapon). */
export interface HitscanOutcome {
  startPos: Vec3;
  hitPoint: Vec3;
  hitPlayerId: number | null;
  damage: number;
  killed: boolean;
  record: ShotRecord | null;
  alerts: AlertChange[];
}

/** What comes after a turn ends (Match.advanceTurn). */
export interface TurnAdvance {
  /** gameOver; pickup (the end-of-match ferry); round (plan-together flows resolve now); batch (plan-then-resolve fires the queue); turn (the next unit's turn begins). */
  next: 'gameOver' | 'pickup' | 'round' | 'batch' | 'turn';
  /** The turn-start resolution (logs, respawns, eruptions …) for the host to present. */
  r: TurnResolution;
}

/** Hazard effects on the unit whose turn begins (Match.applyProximity). */
export interface ProximityOutcome {
  playerId: number;
  damage: number;
  launched: boolean;
  died: boolean;
}

/**
 * 64.6 step 2a: one hop of the turn policy's boundary — the turn advanced (absent when a window opened without an
 * advance), and the unit whose turn it became: down (`dead`, the turn passes on), or begun, its fog refreshed and the
 * hazards near it applied (`proximity`; a death there passes the turn on too).
 */
export interface TurnHop { advance: TurnAdvance | null; player: PlayerState | null; dead: boolean; proximity: ProximityOutcome | null }

/** 64.6 step 2a: what a boundary came to — a window open for `player`, or the flow's next step — and the hops on the way. */
/**
 * 64.6 step 3: a plan round when it closes — its record, and what its end did: each impact's alert changes (in impact
 * order) and each probe's reveal (the units it spotted), the writes that read the round's final state.
 */
export interface RoundClose {
  record: RoundRecord;
  orders: RoundOrder[];
  round: number;
  alerts: AlertChange[][];
  spotted: Array<{ flightIndex: number; ownerId: number; unitIds: number[] }>;
}

/** A plan round stepping live: the run, the state it began from, its orders, and the impacts already kept in step. */
/** 69.98 (4): fog refreshes noted over a run of impacts, by planet index: the latest colours, the vertices changed (null: all). */
export type FogLater = Map<number, { colors: Float32Array; changed: Set<number> | null }>;
interface LiveRound { run: RoundRun; initial: GameState; orders: RoundOrder[]; opts: RoundOptions; round: number; targets: Map<number, Vec3 | null>; impactsSeen: number }

/** 64.6 step 2b-ii: what the turn policy did when a unit's shot settled (SimPresenter.shotSettled). */
export type ShotSettled = { next: 'gameOver' | 'batch' | 'bot' | 'window' } | { next: 'pass'; pass: TurnPass };

export interface TurnPass { hops: TurnHop[]; next: 'window' | 'gameOver' | 'pickup' | 'round' | 'batch' | 'stalled'; player: PlayerState | null }


/** Action clock (Phase 59): one tick's happenings, for the host to show or count. */
export type ActionEvent =
  | { kind: 'fired'; unitId: number; bullet: Bullet }
  /** 69.17 step 3: a pilot's seat taken or left, a piloted ship touching down (core/piloted.ts) */
  | PilotEvent
  /** 65.49 step 2: an anti-dropship missile launched (its target the ship it locked, or none), and one spent (fallen dead; a burst is a `blast`). */
  | { kind: 'missileFired'; unitId: number; missile: MissileFlight }
  | { kind: 'missileSpent'; missile: MissileFlight }
  | CountermeasureEvent   // 69.58: a flare salvo (taken or not), the gunship's turret at a missile
  | { kind: 'jumped'; unitId: number }
  /** 65.7: a bandage applied (and how much it healed), or interrupted by a shot or a jump. */
  | { kind: 'bandaged'; unitId: number; healed: number; by?: number }
  | { kind: 'bandageStopped'; unitId: number }
  | { kind: 'respawned'; unitId: number; at?: 'beacon' | 'relay' | 'fob' | 'base' | 'dropship' | 'pod'; redeployed?: true }   // (69.4 step 3: 'fob')   // (69.4 step 5: 'base', a driver by its supply truck; `redeployed`, a driver home from its run sent forward without a ticket)
  | BuildEvent
  | StructureCaptureEvent   // 69.4 step 4: a relay or FOB changed hands
  | BeaconLostEvent   // 69.6 (DECISIONS 166 (5)): a beacon lost with its ground
  | ConvoyEvent   // 69.4 step 5: a supply truck loaded, handed a FOB its cargo, or lost it
  | RearmEvent   // 69.30 step 1: a parked tank took a round from a dump
  | BedEvent   // 69.30 step 5: a supply truck's bed loaded at its home
  | GrenadeEvent
  | VehicleEvent
  | RocketEvent
  | Gl40Event
  | KitEvent   // 69.4 step 6a: a squad's relays or its carrier's beacon restocked from the side's kits
  | DriveEvent
  /** 64.11: a seat's gun fired, a shell landed (its resolution: craters, damage, knockback), or flew off and was lost. */
  | { kind: 'shellFired'; unitId: number; shell: ActionShell; unspotted?: boolean }
  | { kind: 'shellImpact'; shell: ActionShell; planetIndex: number; point: Vec3; resolution: ImpactResolution }
  | { kind: 'shellLost'; shell: ActionShell }
  /** 65.28 (GameConfig.suppression): a round came within about a person of this unit — once a round, as it came near, with
   *  the nearest point of that stretch of its flight. Presentation only (a flinch, a crack); the mark itself is
   *  `PlayerState.suppressed`, the seconds left. */
  | { kind: 'nearMiss'; unitId: number; point: Vec3 }
  /** S7: a rocket burst (a person-scale blast, no crater): who fired it, where, and what it did. */
  | { kind: 'blast'; ownerId: number; point: Vec3; planetIndex: number; damages: Array<{ playerId: number; damage: number }>; kills: number[]; launched: number[]; /** 65.35: what burst (constants/explosions.ts): drawn and heard by it */ explosion: ExplosionClass;
      /** 69.83: a round's burst — the ship it was fired from and a mortar mission's tag (the bullet's), for the kill feed */ fromShip?: number; by?: 'mortar' }
  /** 68.39: a side's airstrike called (its scattered aim and the point called, when it comes down, its fix) — the caller's side's to show (fog); and, near its impact, its inbound warning (where its bombs fall). */
  | { kind: 'airstrikeCalled'; team: number; id: number; planetIndex: number; aim: Vec3; called: Vec3; impactTick: number; fix: 'seen' | 'picture' | 'guess' }
  | { kind: 'airstrikeInbound'; team: number; id: number; planetIndex: number; points: Vec3[] }
  /** 69.3b: a side's dropship called (its landing; the caller's side's to show), set down (its container left standing), and gone. */
  | { kind: 'dropshipCalled'; team: number; id: number; planetIndex: number; land: Vec3; landTick: number; riders: number; /** its load on the ground: the troops' touchdown, a vehicle's wheels (69.3b's follow-up: the caller's marker stands until then) */ touchTick: number; /** 200 step 4: a wave's sortie (no marker) */ sortie?: true; /** 69.52: called hot (straight in, pressing on) */ hot?: true; /** 63.13: a gunship (its land is where it joins its orbit) */ gunship?: true }
  | { kind: 'dropshipLanded'; team: number; id: number; planetIndex: number; land: Vec3; containerId?: number }
  /** 69.3b step 2: a vehicle a dropship set down on its cables (new, empty, its side's). */
  | { kind: 'dropshipDelivered'; team: number; id: number; structureId: number; type: VehicleKind }
  | { kind: 'dropshipGone'; team: number; id: number }
  /** 69.32: a supply drone off on a point's run (`site`; a called run is the call's), and a load set down (the crate made or filled; no `site`: a called one). */
  | { kind: 'droneLaunched'; team: number; id: number; site?: number }
  | { kind: 'droneDropped'; team: number; id: number; structureId: number; site?: number }
  /** 69.32 step 3: a supply drone brought down — by a round or a mount's flak (its load lost with it when `carrying`) */
  | { kind: 'droneDown'; team: number; id: number; planetIndex: number; at: Vec3; carrying: boolean }
  /** 63.13: a gunship fired — its flight, the weapon, where the round left and where it was aimed (the look's flash and the sound; the round is a bullet in flight). */
  | { kind: 'gunshipFire'; flight: number; team: number; weapon: GunshipWeapon; from: Vec3; to: Vec3 }
  /** 69.59 step 3: a helicopter's door gun or pods fired (core/heliGuns.ts), for the look and the sound */
  | { kind: 'heliFire'; flight: number; team: number; weapon: HeliWeapon; from: Vec3; to: Vec3 }
  /** 69.10: a lighter's run — called (its strip and when it touches down), down on the strip, lifting into its ascent, gone (core/lighter.ts lighterPose for the look each frame). */
  | { kind: 'lighterCalled'; team: number; id: number; planetIndex: number; strip: Vec3; touchTick: number; frames: number }
  | { kind: 'lighterDown'; team: number; id: number }
  /** 69.10 step 2: a frame rolled out of the lighter onto the pool's stock — its vehicle kind and the kind's frames now. */
  | { kind: 'lighterUnloaded'; team: number; id: number; vehicle: VehicleKind; left: number }
  | { kind: 'lighterLifted'; team: number; id: number }
  | { kind: 'lighterGone'; team: number; id: number }
  /** 69.3b, the ship as a body: a ship lost — a flight (id ≥ 0) ended where it was, or a parked wave ship (−1 − its team) on its pad; the riders who survived and those who did not. */
  | { kind: 'shipLost'; team: number; id: number; planetIndex: number; at: Vec3; survived: number[]; lost: number[] }
  /**
   * 69.89: a vehicle's crew lost with its hull (decision 124: below the hatch line they die with it — core/seats.ts
   * reconcileSeats); `by`, the blow of the tick that wrecked it as the kill feed reads one (its owner, the round's ship or
   * the mortar's tag, the burst's class — 'shell' for a shell), absent when none of the tick's blows reached it; `crashed`,
   * the side whose falling ship wrecked it.
   */
  | { kind: 'crewLost'; structureId: number; vehicle: StructureType; lost: number[]; by?: { ownerId: number; fromShip?: number; by?: 'mortar'; explosion?: ExplosionClass }; crashed?: number }
  /** 69.62 (GameConfig.hotRescue; core/rescue.ts): a hot rescue called (its gunship on the way), its lift let in, the squad's smoke popped, brought out (`brought`: those aboard) or given up (`reason`). */
  | { kind: 'rescueCalled' | 'rescueLift' | 'rescueSmoke' | 'rescueDone' | 'rescueAborted'; team: number; id: number; squadId: number; at: Vec3; brought?: number[]; reason?: string }
  /** 69.3b, the ship's crash (DECISIONS 223): a lost flight came down — where (its first piece's surface direction), in how many pieces (their wrecks' ids), the riders' outcome, and whether it broke up in the air first; its fall is `dropships.crashes` (core/shipCrash.ts crashPose). */
  | { kind: 'shipCrashed'; team: number; id: number; planetIndex: number; at: Vec3; pieces: number; wreckIds: number[]; survived: number[]; lost: number[]; brokeUp: boolean; /** 69.24: each piece's impact as the Match applied it (its crater for the host to draw: the ground was left undrawn) */ impacts: ImpactResolution[]; /** 69.83: what fell, for the kill feed — a helicopter (ShipCrash.craft), a gunship (its payload); absent: a dropship */ craft?: 'heli' | 'gunship' }
  /** 69.9: a point-defence mount fired a burst at what it tracks (`at`: the target's position; `hit`: a round killed or a ship hit), and a round it killed in the air (the look's flash and debris; the round is gone from its list). */
  | { kind: 'pdBurst'; mountId: number; variant: 'ciws' | 'cram'; planetIndex: number; at: Vec3; target: { kind: 'shell' | 'bomb' | 'rocket' | 'grenade' | 'ship'; id: number }; hit: boolean }
  | { kind: 'intercepted'; mountId: number; variant: 'ciws' | 'cram' | 'site'; planetIndex: number; at: Vec3; target: { kind: 'shell' | 'bomb' | 'rocket' | 'grenade'; id: number } }
  /** 69.50 step 2a: a forward air-defence site's missile away at a ship (`target`, the ship's id; `at`, the launcher's cells), its flight in `state.missiles`. */
  | { kind: 'siteLaunch'; mountId: number; planetIndex: number; at: Vec3; target: number; missile: MissileFlight }
  /** 65.15: an HE grenade went off (its damage is the `blast` after it); a smoke grenade made a cloud. */
  | { kind: 'grenadeBurst'; grenadeId: number; ownerId: number; point: Vec3 }
  | { kind: 'smokeCloud'; cloud: SmokeCloud }
  /** 68.10: a unit's squad changed (fluid membership): from / to squad ids (null = no squad), and the rule that did it. */
  | MembershipEvent
  | SeatEvent
  | SupplyEvent
  | SupportEvent   // 69.45: the support's case
  | ChargeEvent   // 69.47 step 1: the engineer's charges
  | BulletEvent
  | CaptureEvent
  | ObjectiveEvent
  | ConsequenceEvent
  | DroneEvent;

/** runUntilSettled's caps: a flight that never ends (never seen), and the airborne phase (a unit lost in orbit: MAX_AIRBORNE_TIME, plus landing time). */
const MAX_FLIGHT_TICKS = 400_000;
export const MAX_AIRBORNE_TICKS = Math.ceil((MAX_AIRBORNE_TIME + 5) / PHYS_DT);

/** R11 step 3: the empty plan and flight records (the state carries none until something is queued or flies). */
const EMPTY_PLAN: PlanState = { queue: [], active: false, moves: [], targets: {} };
const EMPTY_FLIGHT: FlightState = { sim: null, cursor: 0, ended: false, geyserIdx: 0, shot: null, pendingCluster: null, probeTrail: [], probeRevealCounter: 0 };
const EMPTY_INTENTS: Readonly<Record<number, UnitIntent>> = Object.freeze({});
const EMPTY_HEARD: Readonly<Record<number, { pos: Vec3; tick: number; loud?: number; step?: boolean }>> = Object.freeze({});

/** A unit that wants nothing this tick. */
export const IDLE_INTENT: UnitIntent = { forward: 0, right: 0, turn: 0, aimPitch: 0, fire: false, jump: false };

/** How a flight ended. */
export type FlightEnding = 'directHit' | 'impact' | 'star' | 'wormhole' | 'lost' | 'noSim';

/**
 * What the simulation tells its host as it advances (51.5b). The game animates,
 * logs and sounds these; the harness counts them. Every hook is synchronous and
 * must not change match state except through the Match.
 */
export interface SimPresenter {
  /** P2P: hold this flight at its last point — a remote shooter may still detonate it. */
  holdFlightEnd(ownerId: number): boolean;
  /** The flight reached its end without a detonation (the game tells the peer). */
  flightEndReached(ownerId: number): void;
  /** A shot or bomblet was launched and its flight precomputed. */
  launched(projectile: ProjectileState, sim: ProjectileSimResult): void;
  /** A probe in flight scanned the ground below it (every 10th step). */
  probeScan(scan: ScanResult): void;
  /** The shot was deflected by a geyser at this point. */
  geyserDeflect(pos: Vec3): void;
  /** The shot hit a planet (or a unit on it) and the impact resolved. */
  impacted(e: { ending: 'directHit' | 'impact'; point: Vec3; planetIndex: number; projectile: ProjectileState; hitPlayerId: number; r: ImpactResolution; record: ShotRecord | null; alerts: AlertChange[]; probeSpotted: number[] }): void;
  /** The shot ended without an impact: into a star, torn by a wormhole, lost in space. */
  lost(e: { ending: 'star' | 'wormhole' | 'lost' | 'noSim'; point: Vec3 | null; planetIndex: number; projectile: ProjectileState; record: ShotRecord | null; probeSpotted: number[] }): void;
  /** No bomblet follows: the shot is over (the game waits for landings and moves the turn on). */
  flightOver(ownerId: number): void;
  /** One airborne tick happened. */
  airborne(step: AirborneStep): void;
  /** Action clock (Phase 59): what one tick did (shots, hits, jumps); `gameOver` once the match is decided. */
  actionEvents?(events: ActionEvent[], gameOver: boolean): void;
  /**
   * 64.1: around each tick the Match steps for a host. Bookkeeping and inputs only: a host counts here, or
   * sets this tick's human intent (the action host, until 64.10 makes intents inputs stamped by tick).
   */
  beforeTick?(): void;
  afterTick?(): void;
  /**
   * 64.6 step 2 (the turn policy's boundary, Match.passTurn / openWindow): the host's view of each hop, at the points it
   * always presented one — before an advance (its trace), after it (the turn's log), a unit's turn begun (its controls),
   * after that unit's fog, after the hazards near it. Presentation only: none of these may write the match's state.
   */
  /**
   * 64.6 step 3: a plan round steps live, one round step a tick (core/roundSim's run). `roundStepped` after each step
   * (the step just simulated; its impacts' world already kept in step), `roundClosed` when it ends.
   */
  roundStepped?(step: number): void;
  roundClosed?(close: RoundClose): void;
  /**
   * 64.6 step 2b-ii: a unit's shot has settled (the flight and every bomblet over, nobody in the air) and the turn
   * policy applied its rule: `gameOver`; `batch` (the host's in-order batch takes its next order); `bot` (back to
   * aiming — the host wakes the runner awaiting the shot, at its own pace: wakeBotRunner); `window` (a human with
   * movement left keeps the window); `pass` (a human with none — the turn passed, the hops already presented).
   */
  shotSettled?(s: ShotSettled): void;
  turnAdvancing?(): void;
  turnAdvanced?(a: TurnAdvance): void;
  turnBegun?(player: PlayerState): void;
  fogRefreshed?(playerIndex: number): void;
  proximityApplied?(prox: ProximityOutcome | null, player: PlayerState): void;
  /**
   * 64.6 step 4: the turn policy opened the next window itself — after a plan round, once whoever it left in the air has
   * landed — and this is the boundary's answer (as passTurn / openWindow return it). Hosts present; they no longer open
   * the window after a round themselves.
   */
  windowOpened?(pass: TurnPass): void;
}

/** 65.37: × the fire rate of the player's own guns in hand (GameConfig.playerFireRate; 1 = as the gun's). */
export function playerFireRate(config: { playerFireRate?: number }): number {
  const r = config.playerFireRate;
  return r !== undefined && r > 0 ? r : 1;
}

/**
 * An action that can be refused: `reason` is what the game says (empty = say nothing),
 * `code` the short form the harness records.
 */
export type PlanResult<T = object> = ({ ok: true } & T) | { ok: false; reason: string; code?: string };
const refuse = (reason: string, code: string) => ({ ok: false as const, reason, code });

type CrewLostBy = NonNullable<Extract<ActionEvent, { kind: 'crewLost' }>['by']>;
/** How far from a wrecked hull a tick's burst or shell can be the blow that wrecked it (people). */
const HULL_BLOW_PEOPLE = 20;

/**
 * 69.89: the crews `reconcileSeats` took with their hulls between `before` and `after` (decision 124) — each wrecked
 * vehicle's occupants, alive and seated before and dead after — one event a vehicle, its blow by `blowOf`.
 */
function crewLostEvents(before: GameState, after: GameState, blowOf: (s: StructureState) => CrewLostBy | undefined, crashed?: number): ActionEvent[] {
  const out: ActionEvent[] = [];
  for (const s of before.structures) {
    if (s.health > 0 || !(VEHICLE_KINDS as readonly string[]).includes(s.type)) continue;
    const lost = before.players.filter(p => p.alive && p.mount?.bodyId === s.id && !after.players.find(q => q.id === p.id)?.alive).map(p => p.id);
    if (!lost.length) continue;
    const by = blowOf(s);
    out.push({ kind: 'crewLost', structureId: s.id, vehicle: s.type, lost, ...(by ? { by } : {}), ...(crashed !== undefined ? { crashed } : {}) });
  }
  return out;
}

/**
 * 69.89: the blow among a tick's events that wrecked hull `s`, as the kill feed reads one — the last round that struck it
 * (its owner, ship, tag and, for a burst round, its class), else the nearest burst or shell within HULL_BLOW_PEOPLE.
 */
export function hullBlow(state: GameState, s: StructureState, events: readonly ActionEvent[]): CrewLostBy | undefined {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.kind === 'hitCover' && e.hit?.kind === 'structure' && e.hit.id === s.id) {
      const b = e.bullet;
      return { ownerId: b.ownerId, ...(b.fromShip !== undefined ? { fromShip: b.fromShip } : {}), ...(b.by ? { by: b.by } : {}), ...(b.blast && b.explosion ? { explosion: b.explosion } : {}) };
    }
  }
  const at = getStructureWorldPosition(s, state.planets), reach = HULL_BLOW_PEOPLE * PERSON_BODY_HEIGHT * personSetOf(state).k;
  let best: CrewLostBy | undefined, bestD = reach;
  for (const e of events) {
    if ((e.kind !== 'blast' && e.kind !== 'shellImpact') || e.planetIndex !== s.planetIndex) continue;
    const d = vecLength(vecSub(e.point, at));
    if (d > bestD) continue;
    bestD = d;
    best = e.kind === 'blast'
      ? { ownerId: e.ownerId, ...(e.fromShip !== undefined ? { fromShip: e.fromShip } : {}), ...(e.by ? { by: e.by } : {}), explosion: e.explosion }
      : { ownerId: e.shell.ownerId, explosion: 'shell' };
  }
  return best;
}

/** 69.62: a rescue's step as the Match's event. */
function rescueEvent(e: RescueEvent): ActionEvent {
  const r = e.rescue;
  return { kind: e.kind, team: r.team, id: r.id, squadId: r.squadId, at: r.at.surfaceDir, ...(r.brought ? { brought: r.brought } : {}), ...(r.reason ? { reason: r.reason } : {}) };
}

export class Match {
  /** The match state (core/ functions take it in and return the next one). */
  state!: GameState;
  /** Turn-flow clock: every pause, wait and bot pacing runs on it, never on setTimeout. */
  clock = new FrameScheduler();

  // ---- Turn flow (Phase 40) ------------------------------------------------------
  turnFlow: TurnFlow = 'perUnit';
  /** Queued orders of the planning team(s). */
  // R11 step 3: the plan queues and the flight live in the state (state.plan, state.flight); these accessors keep the hosts' names
  private get plan(): PlanState { return this.state.plan ?? EMPTY_PLAN; }
  private setPlan(patch: Partial<PlanState>): void { this.state = { ...this.state, plan: { ...this.plan, ...patch } }; }
  private get flight(): FlightState { return this.state.flight ?? EMPTY_FLIGHT; }
  private setFlight(patch: Partial<FlightState>): void { this.state = { ...this.state, flight: { ...this.flight, ...patch } }; }
  get batchQueue(): QueuedOrder[] { return this.plan.queue; }
  set batchQueue(v: QueuedOrder[]) { this.setPlan({ queue: v }); }
  /** A batch (plan-then-resolve) is mid-playback. */
  get batchActive(): boolean { return this.plan.active; }
  set batchActive(v: boolean) { this.setPlan({ active: v }); }
  /** Round flows: movement is planned (charged now, walked in the round), one plan per unit. */
  /** The round's planned walks and jumps, in the order planned (replacing a unit's plan keeps its place). */
  get plannedMoves(): readonly PlannedMove[] { return this.plan.moves; }
  plannedMove(playerId: number): PlannedMove | undefined { return this.plan.moves.find(m => m.playerId === playerId); }
  private setPlannedMove(m: PlannedMove): void {
    const i = this.plan.moves.findIndex(x => x.playerId === m.playerId);
    this.setPlan({ moves: i >= 0 ? this.plan.moves.map((x, k) => k === i ? m : x) : [...this.plan.moves, m] });
  }
  clearPlannedMoves(): void { if (this.plan.moves.length) this.setPlan({ moves: [] }); }
  /** Bots' intended targets for their queued shots (round flows) — the round's shot records measure the miss. */

  /** Guards nextTurn against double-advance from overlapping waits. */
  turnAdvancing = false;
  hasFiredThisTurn = false;
  /** handleGameOver has run for this match (advanceTurn may set the gameover phase first, so the phase cannot be the guard). */
  gameOverHandled = false;

  // ---- The shot in flight -----------------------------------------------------------
  /** For the shot record built when the flight ends (core/shotRecord.ts). */
  get currentShot(): CurrentShot | null { return this.flight.shot; }
  set currentShot(v: CurrentShot | null) { this.setFlight({ shot: v }); }
  /** The precomputed flight (WASM or JS) and the playback cursor into it. */
  get precomputedSim(): ProjectileSimResult | null { return this.flight.sim; }
  set precomputedSim(v: ProjectileSimResult | null) { this.setFlight({ sim: v }); }
  get projCursor(): number { return this.flight.cursor; }
  set projCursor(v: number) { this.setFlight({ cursor: v }); }
  /** The shot and its bomblets are over (flightOver was told); the flight steps no more until the next launch. */
  private get flightEnded(): boolean { return this.flight.ended; }
  private set flightEnded(v: boolean) { this.setFlight({ ended: v }); }
  /** Next geyser event index to process in the precomputed flight. */
  get precomputedGeyserIdx(): number { return this.flight.geyserIdx; }
  set precomputedGeyserIdx(v: number) { this.setFlight({ geyserIdx: v }); }
  get pendingClusterLaunch(): FlightState['pendingCluster'] { return this.flight.pendingCluster; }
  set pendingClusterLaunch(v: FlightState['pendingCluster']) { this.setFlight({ pendingCluster: v }); }
  /** Fixed-step accumulator shared by the projectile, airborne units and the bomblet delay. */
  simAccumulator = 0;
  get probeTrailPoints(): Vec3[] { return this.flight.probeTrail; }
  set probeTrailPoints(v: Vec3[]) { this.setFlight({ probeTrail: v }); }
  get probeRevealCounter(): number { return this.flight.probeRevealCounter; }
  set probeRevealCounter(v: number) { this.setFlight({ probeRevealCounter: v }); }
  /** Geysers that already boosted the current projectile / each airborne player. */
  boostedGeyserIds = new Set<number>();
  playerBoostedGeysers = new Map<number, Set<number>>();
  /** Throttles LOS updates while units are airborne. */
  airborneRevealCounter = 0;

  // ---- Bots --------------------------------------------------------------------------
  /** Where incoming fire came from, per bot, for its next turn's intel. */

  /** Wakes the bot runner once its shot has fully settled (impact, landings, bomblets). */
  botFlightWaiter: (() => void) | null = null;
  botFlightSettled = false;

  // ---- Physics kernel and terrain ---------------------------------------------------
  /** Kernel loaded on this peer. */
  wasmReady = false;
  /** Game-logic use permitted (P2P: only if BOTH peers have it). */
  wasmAllowed = true;
  /** Use the WASM kernel for game logic? Both peers must agree, or results could diverge. */
  get gameWasm(): boolean { return this.wasmReady && this.wasmAllowed; }
  /**
   * This match's kernel world (wasmPhysics createKernelWorld). The game and the bot worker keep the default world;
   * a host running several matches in one realm (the harness, auto-resolve) gives each its own.
   */
  kernelWorld = DEFAULT_KERNEL_WORLD;
  /** Select this match's kernel world and say whether game logic uses the kernel. Call before any kernel use. */
  enterKernel(): boolean {
    if (!this.gameWasm) return false;
    selectKernelWorld(this.kernelWorld);
    this.syncWinds();
    return true;
  }
  /** Decision 120 / 62.54 step 4: the grid and the gust the kernel's world holds (null = none set yet). */
  private windsGrid: string | null = null;
  private windsGust: string | null = null;
  /**
   * Give the kernel this match's wind: the field's grid (core/windField.ts windGrids), resampled once a
   * WIND_FIELD.gridSeconds on the action clock and once a round on the turn clock, and the gust every tick (three
   * numbers a planet). The flight and the fire search both read them.
   */
  private syncWinds(): void {
    if (!this.state.config.wind) return;
    const seconds = this.actionTick * PHYS_DT;
    const at = gridSecond(seconds);
    const gridKey = `${at}:${this.state.turnNumber}:${this.state.planets.length}`;
    if (gridKey !== this.windsGrid) { this.windsGrid = gridKey; this.windsGust = null; setWindGrids(windGrids(this.state, at)); }
    const gustKey = `${this.actionTick}:${this.state.turnNumber}`;
    if (gustKey !== this.windsGust) { this.windsGust = gustKey; setWindGusts(windGusts(this.state, seconds)); }
  }
  /** True (unfogged) per-vertex planet colours: craters recolour them, fog snapshots from them. */
  truePlanetColors = new Map<number, Float32Array>();

  /**
   * Done with this match: every pending wait and timer dropped, nothing left to wake.
   * (The WASM kernel's world is module state — the next match re-uploads it.)
   */
  dispose(): void {
    this.clock.clear();
    this.botFlightWaiter = null;
    this.state = { ...this.state, plan: undefined, flight: undefined };
    // a world of its own is freed with the match; the default world stays
    if (this.kernelWorld !== DEFAULT_KERNEL_WORLD) { dropKernelWorld(this.kernelWorld); this.kernelWorld = DEFAULT_KERNEL_WORLD; }
  }

  /** Plan & resolve flows whose orders fly through the round simulator together. */
  get roundFlow(): boolean { return this.turnFlow === 'simultaneous' || this.turnFlow === 'allPlan'; }

  // ===========================================================================
  // Planning (51.2): queue, un-queue, planned walks and jumps
  // ===========================================================================
  // Logic only. The host records/broadcasts the input, draws markers and ghosts,
  // updates the HUD and logs; these return what it needs for that.

  /**
   * Round flows: hold fire as a prepaid order. The shot leaves from where the unit
   * stands now, whatever it plans next; ammo and the firing cost are spent now.
   * Returns the projectile as it will launch (the host draws its planned path).
   */
  queueRoundOrder(player: PlayerState, params: FireParams): ProjectileState {
    const proj = createProjectile(player, this.state.planets, params, this.state.config);
    this.batchQueue = [...this.batchQueue, { playerId: player.id, teamIndex: player.teamIndex, params, launch: { pos: proj.pos, vel: proj.vel } }];
    this.state = applyFireCost(this.state, player.id, params);
    this.setFacing(player.id, facingFromAzimuth(player, params.azimuth));
    return proj;
  }

  /** Plan, then resolve (in order): hold the order; ammo and movement are spent when it fires. */
  queueBatchOrder(player: PlayerState, params: FireParams): void {
    this.batchQueue = [...this.batchQueue, { playerId: player.id, teamIndex: player.teamIndex, params }];
  }

  /** Take back a unit's queued shot and refund it. The removed order, or null if it had none. */
  unqueue(playerId: number): QueuedOrder | null {
    const i = this.batchQueue.findIndex(o => o.playerId === playerId);
    if (i < 0) return null;
    const order = this.batchQueue[i];
    this.batchQueue = this.batchQueue.filter((_, k) => k !== i);
    this.state = refundFireCost(this.state, playerId, order.params);
    this.hasFiredThisTurn = false;
    return order;
  }

  /**
   * Round flows: plan a walk — charged now, walked during the round at walking speed
   * (the unit can still be hit on the way). Replacing a planned walk refunds it first.
   */
  planMove(player: PlayerState, targetDir: Vec3): PlanResult<{ distance: number }> {
    const existing = this.plannedMove(player.id);
    if (existing?.jump) return { ok: false, reason: 'Jump already planned this turn' };
    const planet = this.state.planets[player.planetIndex];
    const budgetPlayer = existing?.walkCost ? { ...player, movementRemaining: player.movementRemaining + existing.walkCost } : player;
    const { distance, effectiveCost, affordable, blocked } = computeMoveCost(budgetPlayer, planet, targetDir, this.state.carryables, this.state.config.modeConfig);
    if (!affordable) return { ok: false, reason: blocked ?? `Too far! (need ${distance.toFixed(1)}, have ${budgetPlayer.movementRemaining.toFixed(1)})` };
    if (existing?.walkCost) this.state = spendMovement(this.state, player.id, -existing.walkCost);
    this.state = spendMovement(this.state, player.id, effectiveCost);
    this.setPlannedMove({ playerId: player.id, teamIndex: player.teamIndex, walkTo: vecNormalize(vecClone(targetDir)), walkCost: effectiveCost });
    return { ok: true, distance };
  }

  /** Round flows: plan a jump — charged now, flown during the round by the simulator. Replaces a planned walk. */
  planJump(player: PlayerState, launch: JumpLaunch): PlanResult {
    const existing = this.plannedMove(player.id);
    if (existing?.jump) return { ok: false, reason: 'Jump already planned this turn' };
    if (existing?.walkCost) this.state = spendMovement(this.state, player.id, -existing.walkCost);
    this.state = chargeJump(this.state, player.id, launch);
    this.setPlannedMove({ playerId: player.id, teamIndex: player.teamIndex, jump: { launchPos: { ...launch.launchPos }, launchVel: { ...launch.launchVel } } });
    return { ok: true };
  }

  // ===========================================================================
  // Rounds (51.3; 64.6 step 3): the plan-together flows step a whole round live, one round step a tick
  // ===========================================================================

  /**
   * 64.6 step 3: the round flows step live. The turn boundary begins a round when the planning order wraps (everyone
   * plans) or control leaves the planning teams (plan-resolve-together): everything they queued — shots, walks,
   * jumps — goes into the round simulator's run (core/roundSim), which the clock steps one round step a tick until it
   * settles. Each impact's world is kept in step as it lands (the kernel, colours, fog colours, intel: nothing the round
   * reads); the writes that read the round's final state — shot records, each impact's alert noise, the probes' reveals
   * — wait for its close, in the order the one-call resolve made them. `intendedTargets` (playerId → aim point) feed the
   * records' miss distance; the harness knows its bots' targets, the game does not.
   */
  private liveRound: LiveRound | null = null;
  /** A plan round is stepping (the phase is 'firing' until it closes). */
  get roundRunning(): boolean { return this.liveRound !== null; }

  private beginPlanRound(): void {
    this.shotSettling = false;
    const before = this.state;
    const targets = new Map(Object.entries(this.plan.targets).map(([id, pos]) => [Number(id), pos] as [number, Vec3 | null]));
    const merged = buildRoundOrders(this.batchQueue, this.plannedMoves);
    const orders = this.turnFlow === 'allPlan'
      ? roundInitiativeOrder(merged, before.config.seed, before.turnNumber)   // fresh initiative each round
      : merged;
    const opts: RoundOptions = { launchStaggerSteps: 0, prepaid: true };
    const run = beginRoundRun(before, orders, opts);
    this.liveRound = { run, initial: before, orders, opts, round: before.turnNumber, targets, impactsSeen: 0 };
    this.state = { ...run.state, phase: 'firing', projectile: null };
    this.setPlan({ queue: [], moves: [], active: true, targets: {} });   // the orders are flying
  }

  /**
   * The running round, whole, from the state it began in — the same record the live steps will make (the round
   * simulator is a pure function of that state and the orders). For a host that plans its presentation ahead (the
   * game's director); it changes nothing.
   */
  roundPreview(): { record: RoundRecord; orders: RoundOrder[]; round: number; before: GameState } | null {
    const lr = this.liveRound;
    return lr ? { record: simulateRound(lr.initial, lr.orders, lr.opts), orders: lr.orders, round: lr.round, before: lr.initial } : null;
  }

  /** The running round to its close now, tick by tick (a host that has shown what it wants of it). */
  runRoundOut(p: SimPresenter): void {
    while (this.liveRound) this.stepOnce(p);
  }

  /** One round step: the run takes the state as it stands, steps, and hands it back; then the impacts that landed. */
  private stepLiveRound(p: SimPresenter): void {
    const lr = this.liveRound!;
    const step = lr.run.step;
    lr.run.state = this.state;
    const more = stepRoundRun(lr.run);
    this.state = lr.run.state;
    for (; lr.impactsSeen < lr.run.impacts.length; lr.impactsSeen++) this.applyImpactWorld(lr.run.impacts[lr.impactsSeen].resolution);
    if (more) p.roundStepped?.(step);
    else this.closeLiveRound(p);
  }

  private closeLiveRound(p: SimPresenter): void {
    const lr = this.liveRound!;
    this.liveRound = null;
    const record = finishRoundRun(lr.run);
    const before = lr.initial;
    let state: GameState = { ...this.state, phase: 'firing', projectile: null };
    for (const f of record.flights) {
      if (f.outcome === 'skipped') continue;
      const order = lr.orders.find(o => o.playerId === f.ownerId);
      const ev = record.impacts.find(i => i.flightIndex === f.index);
      const shooter = before.players.find(p => p.id === f.ownerId);
      state = appendShotRecord(state, buildShotRecord({
        turnNumber: state.turnNumber, shooterId: f.ownerId, weaponKey: f.weaponKey,
        params: order?.fire?.params ?? { azimuth: 0, elevation: 0, power: 0, weaponKey: f.weaponKey },
        submunition: f.submunitionOf !== null, outcome: f.outcome === 'playerHit' ? 'playerHit' : f.outcome === 'impact' ? 'impact' : 'lost',
        launchPlanetIndex: shooter?.planetIndex ?? -1, points: f.points, planets: before.planets,
        impactPlanetIndex: f.hitPlanetIndex, impactPoint: f.hitPoint,
        ...(lr.targets.has(f.ownerId) ? { intendedTarget: lr.targets.get(f.ownerId) ?? null } : {}),
        damages: ev?.damages ?? [], kills: ev?.kills.length ?? 0,
      }));
    }
    this.state = state;
    // back to aiming; then what reads the round's final state: each impact's noise, the probes' reveals
    this.batchActive = false;
    this.state = { ...this.state, phase: 'aiming', projectile: null };
    const alerts = record.impacts.map(i => this.noise(i.point, i.planetIndex, WEAPONS[record.flights[i.flightIndex].weaponKey].reveals ? 'quiet' : 'loud'));
    const spotted = record.flights
      .filter(f => f.outcome !== 'skipped' && WEAPONS[f.weaponKey].reveals)
      .map(f => ({ flightIndex: f.index, ownerId: f.ownerId, unitIds: this.revealProbePath(f.ownerId, f.points) }));
    p.roundClosed?.({ record, orders: lr.orders, round: lr.round, alerts, spotted });
    // 64.6 step 4: the policy opens the next window once the round's airborne have landed (stepOnce); the debt is state
    this.setPlan({ windowOwed: true });
  }


  // ===========================================================================
  // Unit actions (51.7a): fire, move, jump, melee, heal, shop, build
  // ===========================================================================
  // Logic only: the host records/broadcasts the input, animates, logs, and moves
  // the turn on where an action ends it (a shot's flight, a delivery).

  /**
   * Fire: the checks, the cost (ammo, movement, the aim remembered), the unit faces
   * the shot, the shot tracked for its record — then a beam resolves at once, a
   * projectile launches (its flight plays through advance()).
   */
  fire(player: PlayerState, params: FireParams, p: SimPresenter): PlanResult<{ hitscan: HitscanOutcome | null }> {
    // S6: a shot from a seat is the gun's — the unit's own ammunition is neither checked nor spent
    const seatShot = isSeatShot(player, params.weaponKey, this.state);
    const check = checkFire(fireCheckSubject(player, params.weaponKey, this.state), params.weaponKey);
    if (!check.ok) return { ok: false, reason: check.reason };
    const ammoBefore = player.loadout.ammo[params.weaponKey];
    this.state = applyFireCost(this.state, player.id, params);
    if (seatShot && ammoBefore !== null && ammoBefore !== undefined) {
      this.state = { ...this.state, players: this.state.players.map(q => q.id === player.id ? { ...q, loadout: { ...q.loadout, ammo: { ...q.loadout.ammo, [params.weaponKey]: ammoBefore } } } : q) };
    }
    this.setFacing(player.id, facingFromAzimuth(player, params.azimuth));
    const shooter = this.state.players.find(q => q.id === player.id) ?? player;
    // Bots fire at a known point; the record measures the miss against it
    this.currentShot = {
      params, ownerId: player.id, launchPlanetIndex: player.planetIndex,
      targetPos: shooter.isBot ? (shooter.botLastFireTargetPos ?? null) : null, submunition: false,
    };
    this.shotSettling = false;   // 64.6 2b-ii: a new shot — the one before it has settled, or never will now
    if (WEAPONS[params.weaponKey].hitscan) {
      const hitscan = this.fireHitscan(player, params);
      this.shotSettling = true;   // a beam is over at once: it settles on the next tick, by the rule
      return { ok: true, hitscan };
    }
    this.launchShot(createProjectile(player, this.state.planets, params, this.state.config), p);
    return { ok: true, hitscan: null };
  }

  /** A beam: one terrain-blocked ray, damage on whoever it meets, the shot's record, and a quiet noise at the shooter. */
  private fireHitscan(player: PlayerState, params: FireParams): HitscanOutcome {
    const weapon = WEAPONS[params.weaponKey];
    const ray = raycastHitscan(player, params, this.state.players, this.state.planets, this.state.config.friendlyFire);
    let damages: Array<{ playerId: number; damage: number }> = [];
    let killed = false;
    if (ray.hitPlayerId !== null) {
      const hit = applyHitscanDamage(this.state, player.id, ray.hitPlayerId, weapon.damage);
      this.state = hit.state;
      damages = [{ playerId: ray.hitPlayerId, damage: weapon.damage }];
      killed = hit.killed;
    }
    const record = this.recordShot('hitscan', damages, killed ? 1 : 0, -1, ray.hitPoint, []);
    this.currentShot = null;
    const shooter = this.state.players.find(q => q.id === player.id) ?? player;
    const alerts = this.noise(getPlayerWorldPosition(shooter, this.state.planets), shooter.planetIndex, 'quiet');
    return { startPos: ray.startPos, hitPoint: ray.hitPoint, hitPlayerId: ray.hitPlayerId, damage: damages[0]?.damage ?? 0, killed, record, alerts };
  }

  /**
   * Walk: in the plan-together flows the walk is planned (planMove); otherwise the
   * unit moves now — movement spent, it faces the way it walked, then what it walks
   * into: a friendly dropped core returns, heal items are picked up, a carrier at
   * home captures.
   */
  move(player: PlayerState, targetDir: Vec3): PlanResult<{ distance: number; planned: boolean; pickedUp: GroundItemState[]; captured: number | null }> {
    if (player.mount) return { ok: false, reason: 'Dismount first' };
    const planet = this.state.planets[player.planetIndex];
    if (this.roundFlow) {
      const plan = this.planMove(player, targetDir);
      if (!plan.ok) return plan;
      this.setFacing(player.id, facingAfterWalk(planet, player.surfaceDir, targetDir));
      return { ok: true, distance: plan.distance, planned: true, pickedUp: [], captured: null };
    }
    const { distance, affordable, blocked } = computeMoveCost(player, planet, targetDir, this.state.carryables, this.state.config.modeConfig);
    if (!affordable) return { ok: false, reason: blocked ?? `Too far! (need ${distance.toFixed(1)}, have ${player.movementRemaining.toFixed(1)})` };
    this.setFacing(player.id, facingAfterWalk(planet, player.surfaceDir, targetDir));
    this.state = movePlayer(this.state, player.id, targetDir);
    this.state = checkTouchReturn(this.state, player.id);
    const pickup = checkGroundItemPickup(this.state, player.id);
    this.state = pickup.state;
    this.refreshFlagStatus();
    const captured = this.captureIfHome(player.id);
    return { ok: true, distance, planned: false, pickedUp: pickup.pickedUp, captured };
  }

  /** Jump: in the plan-together flows the jump is planned (planJump); otherwise the unit launches now. */
  jump(player: PlayerState, aim: JetpackParams): PlanResult<{ launch: JumpLaunch; planned: boolean }> {
    if (player.mount) return { ok: false, reason: 'Dismount first' };
    const launch = computeJumpLaunch(player, this.state.planets, aim, this.state.config.modeConfig, this.state.carryables);
    if (!launch.ok) return { ok: false, reason: launch.reason };
    if (this.roundFlow) {
      const plan = this.planJump(player, launch);
      if (!plan.ok) return plan;
      return { ok: true, launch, planned: true };
    }
    this.state = applyJump(this.state, player.id, launch);
    return { ok: true, launch, planned: false };
  }

  /** Melee: a punch on a unit in reach. */
  melee(player: PlayerState, targetId: number): PlanResult<{ killed: boolean; damage: number }> {
    if (!canSpendMovement(this.state, player, MELEE_MOVEMENT_COST)) return { ok: false, reason: 'Not enough movement' };
    const target = this.state.players.find(q => q.id === targetId);
    if (!target || !target.alive) return { ok: false, reason: 'No target' };
    const hit = applyDamage(this.state, { unit: targetId }, MELEE_DAMAGE, 'melee', player.id);   // the one damage door
    const killed = hit.killed;
    this.state = {
      ...hit.state,
      players: hit.state.players.map(q => q.id === player.id ? { ...q, movementRemaining: q.movementRemaining - MELEE_MOVEMENT_COST } : q),
    };
    return { ok: true, killed, damage: MELEE_DAMAGE };
  }

  /** Heal: use one item (healing scaled by the unit's kit); costs movement. */
  heal(player: PlayerState, itemKey: ItemKey): PlanResult<{ healed: number }> {
    if (!player.alive || player.health >= 100) return { ok: false, reason: 'Nothing to heal' };
    const count = player.loadout.items?.[itemKey] ?? 0;
    if (count <= 0 || !canSpendMovement(this.state, player, HEAL_MOVEMENT_COST)) return { ok: false, reason: 'Cannot heal' };
    const healed = applyHeal(player, HEAL_ITEMS[itemKey].healPercent * (player.modifiers?.healMult ?? 1));
    const updated: PlayerState = {
      ...healed,
      loadout: { ...healed.loadout, items: { ...player.loadout.items, [itemKey]: count - 1 } },
      movementRemaining: healed.movementRemaining - HEAL_MOVEMENT_COST,
    };
    this.state = { ...this.state, players: this.state.players.map(q => q.id === player.id ? updated : q) };
    return { ok: true, healed: updated.health - player.health };
  }

  /** Drone delivery: ammo, fuel and heal items (items at 3× the shop price). The host ends the turn when it lands. */
  shop(player: PlayerState, params: ShopParams): { ok: false; reason: 'empty' | 'unaffordable' } | { ok: true; total: number } {
    const fuelQty = params.fuelQty ?? 0;
    const items = params.items ?? {};
    let itemCost = 0;
    for (const key of Object.keys(items) as ItemKey[]) itemCost += (items[key] ?? 0) * HEAL_ITEMS[key].cbpCost * 3;
    const total = calculateShopTotalWithFuel(params.purchases, fuelQty) + itemCost;
    if (total <= 0) return { ok: false, reason: 'empty' };
    if (total > player.cbp) return { ok: false, reason: 'unaffordable' };
    let updated = applyShopPurchasesWithFuel(player, params.purchases, fuelQty);
    if (itemCost > 0) {
      const current = { ...(updated.loadout.items ?? {}) };
      for (const key of Object.keys(items) as ItemKey[]) {
        const n = items[key] ?? 0;
        if (n > 0) current[key] = (current[key] ?? 0) + n;
      }
      updated = { ...updated, cbp: updated.cbp - itemCost, loadout: { ...updated.loadout, items: current } };
    }
    this.state = { ...this.state, players: this.state.players.map(q => q.id === player.id ? updated : q) };
    return { ok: true, total };
  }

  /**
   * Build: an ecotourism centre (one per unit, income per turn) or a listening-post
   * antenna (1 movement; in Signal Trace it becomes an activatable post).
   */
  build(player: PlayerState, structureType: StructureType, surfaceDir: Vec3): PlanResult<{ structureId: number }> {
    const isAntenna = structureType === 'antenna';
    const cost = isAntenna ? 1.0 : BUILD_MOVEMENT_COST;
    if (!isAntenna && !canBuildEcotourism(player.id, this.state.structures)) return { ok: false, reason: 'Build limit reached!' };
    if (!canSpendMovement(this.state, player, cost)) return { ok: false, reason: `Not enough movement! (need ${cost}, have ${player.movementRemaining.toFixed(1)})` };
    this.state = { ...this.state, players: this.state.players.map(q => q.id === player.id ? { ...q, movementRemaining: q.movementRemaining - cost } : q) };
    this.state = addStructure(this.state, player.id, structureType, player.planetIndex, surfaceDir);
    { const last = this.state.structures.length - 1, seated = seatBuild(this.state, this.state.structures[last], this.actionTick * PHYS_DT); if (seated !== this.state.structures[last]) this.state = { ...this.state, structures: this.state.structures.map((x, i) => (i === last ? seated : x)) }; }   // 67.52
    this.state = restand(this.state);   // 209's footing follow-up: a built plinth kind stands (core/stands.ts restand: the same state when none is new)
    const structureId = this.state.structures[this.state.structures.length - 1].id;
    if (isAntenna && this.state.modeState.signalTrace) {
      const ruins = this.state.ruins;
      const post: RuinState = {
        id: ruins.length > 0 ? Math.max(...ruins.map(r => r.id)) + 1 : 0,
        ruinType: 'antenna', planetIndex: player.planetIndex, surfaceDir: { ...surfaceDir },
        interactable: true, interactTurns: this.state.config.modeConfig.postActivationTurns ?? 2, interactEffect: 'listeningPost',
      };
      this.state = { ...this.state, ruins: [...ruins, post] };
    }
    return { ok: true, structureId };
  }

  /** S6: take an emplacement's seat (core/seats.ts) — the unit stands at it, fires its weapons, and the emplacement is the unit's now. */
  mount(player: PlayerState, structureId: number): PlanResult<{ captured: boolean }> {
    const check = canMount(player, structureId, this.state);
    if (!check.ok) return refuse(check.reason, check.code);
    const before = this.state.structures.find(x => x.id === structureId)!;
    const captured = before.ownerId !== player.id && this.state.players.find(q => q.id === before.ownerId)?.teamIndex !== player.teamIndex;
    this.state = mountUnit(this.state, player.id, structureId);
    return { ok: true, captured };
  }

  /**
   * I2: start tasking a drone — the unit stands still while it sets the course (the host's tablet),
   * until it launches or cancels. Bots can launch without tasking first.
   */
  beginDroneTasking(player: PlayerState): PlanResult {
    const unit = this.state.players.find(q => q.id === player.id) ?? player;
    const check = canTaskDrone(unit, this.state, this.actionTick * PHYS_DT);   // 62.19 (e): the tablet says why the weather grounds it
    if (!check.ok) return refuse(check.reason, check.code);
    this.state = beginDroneTasking(this.state, unit.id);
    return { ok: true };
  }

  /** I2: put the tablet away without launching. */
  cancelDroneTasking(player: PlayerState): PlanResult {
    this.state = cancelDroneTasking(this.state, player.id);
    return { ok: true };
  }

  /**
   * I2: launch a drone along `waypoints` (unit directions on the operator's planet). One drone is
   * used up; the operator stands still for the launch (droneLaunchTime), then is free — the drone
   * flies its route on its own until the route or its battery ends. Action clock only.
   */
  launchDrone(player: PlayerState, waypoints: Vec3[], strike?: StrikeTarget, payload?: DronePayload): PlanResult<{ droneId: number }> {
    const unit = this.state.players.find(q => q.id === player.id) ?? player;
    // 65.19: the payload — explosive with a strike (a charge, spent), else the scanner; a strike-drone kit (65.16) is explosive throughout
    const load: DronePayload = unit.droneKind === 'strike' ? 'explosive' : payload ?? (strike ? 'explosive' : 'scanner');
    if (load === 'explosive' && unit.droneKind !== 'strike' && !((unit.charges ?? 0) > 0)) return refuse(unit.payloadIssue ? 'No charges left' : 'Scout drones carry no charge', unit.payloadIssue ? 'no charge' : 'not strike');
    // 65.16: a strike drone may be launched with its strike — a known unit or a point; with no waypoints it flies straight there
    let task: StrikeTask | undefined;
    if (strike) {
      if (load !== 'explosive') return refuse('A scanner carries no charge', 'not strike');
      const t = strikeTaskFor(this.state, unit, strike, this.actionTick);
      if (!t.ok) return refuse(t.reason, t.code);
      task = t.task;
      const planet = this.state.planets[unit.planetIndex];
      if (!waypoints.length && planet) waypoints = [vecNormalize(vecSub(task.aim, planet.position))];
    }
    const check = canLaunchDrone(unit, waypoints, this.state, this.actionTick * PHYS_DT);   // 62.19 (e): not into a storm or a gale
    if (!check.ok) return refuse(check.reason, check.code);
    const r = launchDrone(this.state, unit.id, waypoints, task, load);
    this.state = r.state;
    return { ok: true, droneId: r.drone.id };
  }

  /**
   * 65.15: throw a grenade (HE or smoke) along `dir` — a world direction; the speed is the throw's (core/grenades.ts).
   * One is spent; the next waits GRENADES.throwSeconds. Action clock only.
   */
  /** 63.1u: the driver pops its vehicle's smoke (core/vehicleSmoke.ts): a ring of smoke grenades, one charge spent. */
  popVehicleSmoke(player: PlayerState, toward?: Vec3): PlanResult<{ thrown: number }> {
    const unit = this.state.players.find(q => q.id === player.id) ?? player;
    const check = canPopSmoke(this.state, unit, this.actionTick);
    if (!check.ok) return refuse(check.reason, check.code);
    const before = this.state.grenades?.length ?? 0;
    this.state = popVehicleSmoke(this.state, unit, check.vehicle, this.actionTick, toward);
    return { ok: true, thrown: (this.state.grenades?.length ?? 0) - before };
  }

  /** 69.47 step 1 (GameConfig.charges): an engineer starts its kneel to place a charge on `fuse` (core/charges.ts); placed when the kneel runs out. Action clock only. */
  placeCharge(player: PlayerState, fuse: ChargeFuse): PlanResult<{ kneel: number }> {
    if (this.state.config.clock !== 'action') return refuse('Charges are placed on the action clock', 'clock');
    const unit = this.state.players.find(q => q.id === player.id) ?? player;
    const why = chargeRefusal(this.state, unit);
    if (why) return refuse(why, 'charge');
    this.state = startCharge(this.state, unit.id, fuse);
    return { ok: true, kneel: CHARGE.kneelSeconds };
  }

  /** 69.47 step 1: an engineer pulls its wire — its charges on the wire within reach go off this tick. */
  blowCharges(player: PlayerState): PlanResult<{ charges: number }> {
    const r = blowWire(this.state, player.id, this.actionTick);
    if (!r) return refuse('No charge of yours on the wire within reach', 'none');
    this.state = r.state;
    return { ok: true, charges: r.charges };
  }

  /** 69.47 step 1: a placed charge goes off — the `charge` class's burst where it sits, its engineer's. */
  private blowCharge(c: PlacedCharge, events: ActionEvent[]): void {
    const blast = explosionBlast(this.state, 'charge')!;
    const r = rocketBlast(this.state, c.ownerId, c.point, c.planetIndex, blast, 'charge');
    this.state = r.state;
    events.push({ kind: 'blast', ownerId: c.ownerId, point: c.point, planetIndex: c.planetIndex, damages: r.damages, kills: r.kills, launched: r.launched, explosion: 'charge' });
  }

  throwGrenade(player: PlayerState, kind: GrenadeKind, dir: Vec3): PlanResult<{ grenadeId?: number }> {
    const unit = this.state.players.find(q => q.id === player.id) ?? player;
    if (kind !== 'he' && kind !== 'smoke') return refuse('No such grenade', 'kind');
    if (!(vecLength(dir) > 0)) return refuse('No direction', 'no dir');
    const check = canThrow(unit, kind, this.actionTick);
    if (!check.ok) return refuse(check.reason, check.code);
    // 216 (GameConfig.realTiming): wound up first — the grenade leaves the hand REAL_TIMING.throwWindSeconds on (no id yet)
    if (defaultOn(this.state.config, 'realTiming')) { this.state = windGrenade(this.state, unit, kind, dir, this.actionTick); return { ok: true }; }
    this.state = throwGrenade(this.state, unit, kind, dir, this.actionTick);
    return { ok: true, grenadeId: this.state.grenades![this.state.grenades!.length - 1].id };
  }

  /**
   * 65.16: send this unit's strike drone already in the air (its newest not yet diving) at `target` — a unit the team
   * knows, or a point: it leaves its route on the next tick and dives (core/strikeDrone.ts). Action clock only.
   */
  strikeDrone(player: PlayerState, target: StrikeTarget | undefined): PlanResult<{ droneId: number }> {
    const unit = this.state.players.find(q => q.id === player.id) ?? player;
    if (!target) return refuse('No target', 'no target');
    const drone = strikeDroneInFlight(this.state, unit.id);
    if (!drone) return refuse('No strike drone in the air', 'none in air');
    const t = strikeTaskFor(this.state, unit, target, this.actionTick);
    if (!t.ok) return refuse(t.reason, t.code);
    this.state = sendStrike(this.state, drone.id, t.task);
    return { ok: true, droneId: drone.id };
  }

  /** S6: leave the seat; the unit stays where the emplacement stands. */
  dismount(player: PlayerState): PlanResult {
    const check = canDismount(player, this.state);
    if (!check.ok) return refuse(check.reason, check.code);
    this.state = dismountUnit(this.state, player.id);
    return { ok: true };
  }

  // ===========================================================================
  // Objective actions (51.7b): stakes, carry, interact, collect, deliver, claim, orders
  // ===========================================================================

  /** Plant a stake (Dominion / Sovereignty) where the unit stands. */
  plantStake(player: PlayerState, surfaceDir: Vec3): PlanResult<{ flagId: number }> {
    if (!canPlantFlag(player)) return refuse('', 'cannot plant');
    const before = this.state.plantedFlags.length;
    this.state = plantFlag(this.state, player.id, surfaceDir);
    if (this.state.plantedFlags.length === before) return refuse('Cannot plant a stake here', 'blocked on planet');
    return { ok: true, flagId: this.state.plantedFlags[this.state.plantedFlags.length - 1].id };
  }

  /** Pull an enemy stake. */
  removeStake(player: PlayerState, stakeId: number): PlanResult {
    if (!this.state.plantedFlags.some(f => f.id === stakeId)) return refuse('', 'no such stake');
    const before = this.state.plantedFlags.filter(f => f.alive !== false).length;
    this.state = removeStake(this.state, player.id, stakeId);
    if (this.state.plantedFlags.filter(f => f.alive !== false).length === before) return refuse('Cannot remove that stake', 'not removed');
    return { ok: true };
  }

  /** Pick up a carryable (a core, a fragment, salvage); a carrier at home captures at once. */
  pickup(player: PlayerState, objectId: number): PlanResult<{ object: CarryableObjectState; captured: number | null }> {
    const obj = this.state.carryables.find(c => c.id === objectId);
    if (!obj) return refuse('', 'no such object');
    const next = pickupObject(this.state, player.id, objectId);
    if (next === this.state) return refuse('Cannot pick that up', 'cannot pick up');
    this.state = next;
    this.refreshFlagStatus();
    return { ok: true, object: obj, captured: this.captureIfHome(player.id) };
  }

  /** Drop what the unit carries. */
  drop(player: PlayerState, carryableId: number): PlanResult<{ object: CarryableObjectState }> {
    const carrying = getPlayerCarrying(player.id, this.state.carryables);
    if (!carrying || carrying.id !== carryableId) return refuse('', 'not carrying it');
    this.state = dropObject(this.state, player.id);
    this.refreshFlagStatus();
    return { ok: true, object: carrying };
  }

  /**
   * Interact: in Excavation, build the drill; otherwise work a ruin (the one named, or
   * the nearest in reach) — start, continue (0.5 movement a turn), and activate it.
   */
  interact(player: PlayerState, ruinId?: number): PlanResult<
    | { kind: 'drill'; drillId: number }
    | { kind: 'started'; turns: number }
    | { kind: 'continued'; turnsLeft: number }
    | { kind: 'activated'; ruin: RuinState; logs: string[]; signalRevealed: boolean }
  > {
    const ms = this.state.modeState;
    const inProgress = ms.interactionProgress;
    if (!inProgress) return refuse('No interactable structures in this mode', 'no interactions in this mode');
    if (ms.excavation) {
      if (!canBuildDrill(player, this.state.planets, ms.excavation.artifactPlanetIndex, ms.excavation.artifactSurfaceDir)) return refuse('Cannot build drill here', 'cannot build drill here');
      this.state = buildDrillSite(this.state, player.id, player.surfaceDir);
      const sites = this.state.modeState.excavation?.drillSites ?? [];
      return { ok: true, kind: 'drill', drillId: sites[sites.length - 1]?.id ?? -1 };
    }
    if (ruinId !== undefined && !canSpendMovement(this.state, player, 0.5)) return refuse('', 'no movement');
    const ruin = ruinId !== undefined
      ? this.state.ruins.find(r => r.id === ruinId && r.interactable && r.planetIndex === player.planetIndex)
      : this.state.ruins.find(r => r.interactable && canInteract(player, r, this.state.planets));
    if (!ruin) return refuse(ruinId !== undefined ? '' : this.whyNoRuin(player), 'no ruin');
    const spend = (s: GameState): GameState => ({
      ...s, players: s.players.map(q => q.id === player.id ? { ...q, movementRemaining: q.movementRemaining - 0.5 } : q),
    });
    const existing = inProgress.find(ip => ip.playerId === player.id && ip.ruinId === ruin.id);
    if (existing) {
      const { progress, activated } = tickInteraction(inProgress, player.id, ruin.id);
      this.state = spend({ ...this.state, modeState: { ...ms, interactionProgress: progress } });
      if (!activated) return { ok: true, kind: 'continued', turnsLeft: existing.turnsRemaining - 1 };
      const act = activateInteraction(this.state, this.state.players.find(q => q.id === player.id) ?? player, ruin);
      this.state = act.state;
      return { ok: true, kind: 'activated', ruin, logs: act.logs, signalRevealed: !!act.signalRevealed };
    }
    const turns = ruin.interactTurns ?? 2;
    this.state = spend({ ...this.state, modeState: { ...ms, interactionProgress: startInteraction(inProgress, player.id, player.teamIndex, ruin.id, turns) } });
    return { ok: true, kind: 'started', turns };
  }

  private whyNoRuin(player: PlayerState): string {
    const ruins = this.state.ruins.filter(r => r.interactable);
    if (ruins.length === 0) return 'No interactable structures in this mode';
    let min = Infinity;
    for (const r of ruins) if (r.planetIndex === player.planetIndex) min = Math.min(min, arcDistance(player.surfaceDir, r.surfaceDir, this.state.planets[player.planetIndex].radius));
    return min < Infinity ? `No interactable structure in range. Nearest: ${min.toFixed(1)} units away` : 'No interactable structure on this planet';
  }

  /** Collect: heal items at the unit's feet, or a named collectible (Salvage, Uplink …) within the carry limit. */
  collect(player: PlayerState, params: CollectActionParams): PlanResult<{ kind: 'ground'; pickedUp: GroundItemState[] } | { kind: 'collectible'; collectible: CollectibleNode }> {
    if (params.collectibleId === undefined) {
      const pickup = checkGroundItemPickup(this.state, player.id);
      if (pickup.pickedUp.length === 0) return refuse('', 'nothing here');
      this.state = pickup.state;
      return { ok: true, kind: 'ground', pickedUp: pickup.pickedUp };
    }
    const collectible = this.state.collectibles.find(c => c.id === params.collectibleId && !c.collected);
    if (!collectible) return refuse('', 'no such collectible');
    if (!canCarryMore(player.id, this.state.carryables, this.state.config.modeConfig)) return refuse('Carrying too much! Deliver items first.', 'carrying too much');
    this.state = collectItem(this.state, player.id, collectible.id);
    return { ok: true, kind: 'collectible', collectible };
  }

  /** Deliver: upload a data fragment at the antenna (Uplink), or salvage at the team's depot. */
  deliver(player: PlayerState, carryableId?: number): PlanResult<{ kind: 'fragment' | 'salvage'; object: CarryableObjectState }> {
    const ms = this.state.modeState;
    if (ms.uplink) {
      const antenna = this.state.ruins.find(r => r.id === ms.uplink!.antennaRuinId);
      const near = carryableId !== undefined
        ? antenna !== undefined && antenna.planetIndex === player.planetIndex
        : antenna !== undefined && canInteract(player, antenna, this.state.planets);
      if (!near) return refuse('Must be near the antenna to upload', 'not at antenna');
      const fragment = this.state.carryables.find(c => c.carriedBy === player.id && c.type === 'dataFragment' && (carryableId === undefined || c.id === carryableId));
      if (!fragment) return refuse('No data fragments to upload', 'no fragment');
      this.state = processFragmentUpload(this.state, player.id, fragment.id);
      return { ok: true, kind: 'fragment', object: fragment };
    }
    if (ms.salvage) {
      const depot = ms.salvage.depotPlanets[player.teamIndex];
      if (depot === undefined || player.planetIndex !== depot) return refuse('Must be at your team depot to deliver salvage', 'not at depot');
      const salvage = this.state.carryables.find(c => c.carriedBy === player.id && c.type === 'salvageNode' && (carryableId === undefined || c.id === carryableId));
      if (!salvage) return refuse('No salvage to deliver', 'no salvage');
      this.state = processSalvageDelivery(this.state, player.id, salvage.id);
      return { ok: true, kind: 'salvage', object: salvage };
    }
    return refuse('Nothing to deliver in this mode', 'nothing to deliver in this mode');
  }

  /** Start claiming a ruin on the unit's planet. */
  claim(player: PlayerState, ruinId: number): PlanResult<{ ruin: RuinState }> {
    const ruin = this.state.ruins.find(r => r.id === ruinId);
    if (!ruin || ruin.planetIndex !== player.planetIndex) return refuse('', 'no ruin');
    const next = startClaim(this.state, player.id, ruinId);
    if (next === this.state) return refuse('', 'cannot claim');
    this.state = next;
    return { ok: true, ruin };
  }

  /** A leader's order to a bot teammate (influence, not override). */
  command(player: PlayerState, cmd: CommandParams): PlanResult {
    const { botId, commandType, ...rest } = cmd;
    const next = issueCommand(this.state, player.id, botId, commandType, rest as Parameters<typeof issueCommand>[4]);
    if (!next) return refuse('Cannot issue order — not enough movement or limit reached.', 'cannot order');
    this.state = next;
    return { ok: true };
  }

  // ---- state the browser used to write in place (R11 step 2): phase, the death cooldown, exposure markers ----

  /** The match's phase (game over, the ferry pickup): the host says when, the Match writes it. */
  setPhase(phase: GamePhase): void { this.state = setPhase(this.state, phase); }

  /** Back to aiming with no shot in the air (a batch resolved, a remote shooter's post-fire wait, the deploy's end). */
  resumeAiming(): void { this.state = { ...this.state, phase: 'aiming', projectile: null }; }

  /** The respawn layer's cooldown after a death (turn clock; the action clock's is the wave). */
  deathCooldown(killId: number): void { this.state = applyDeathCooldown(this.state, killId); }

  /**
   * An exposure marker in one player's fog: "an enemy saw me here" (viewer-relative knowledge the game shows
   * the local player; drawn, never read by the sim — a candidate to leave the state with R11 step 3).
   */
  markExposure(playerIndex: number, by: 'los' | 'probe', pos: Vec3, maxAge: number): void {
    const fog = [...this.state.fog];
    if (!fog[playerIndex]) return;
    fog[playerIndex] = updateExposureMarkers(fog[playerIndex], true, by, pos, knowledgeTurn(this.state), maxAge);
    this.state = { ...this.state, fog };
  }

  /** The leadership panel's hold-fire switch for a unit (R11: an input; the game used to flip it in place). */
  toggleHoldFire(unitId: number): PlanResult<{ holdFire: boolean }> {
    const u = this.state.players.find(p => p.id === unitId);
    if (!u) return refuse('No such unit', 'unit');
    const holdFire = !u.holdFire;
    this.state = { ...this.state, players: this.state.players.map(p => p.id === unitId ? { ...p, holdFire } : p) };
    return { ok: true, holdFire };
  }

  /** Cancel a unit's pending order (the leadership panel). */
  cancelOrder(unitId: number): PlanResult {
    const u = this.state.players.find(p => p.id === unitId);
    if (!u) return refuse('No such unit', 'unit');
    if (!u.pendingCommand) return refuse('No order to cancel', 'no order');
    this.state = cancelCommand(this.state, unitId);
    return { ok: true };
  }

  /** CTF: per-team flag status follows the carryables (no-op without the layer). */
  refreshFlagStatus(): void {
    const ctf = this.state.modeState.ctf;
    if (!ctf) return;
    this.state = { ...this.state, modeState: { ...this.state.modeState, ctf: { ...ctf, flagStatus: computeFlagStatus(this.state.carryables) } } };
  }

  /** CTF: a carrier standing at home captures. The captured object's id, or null. */
  captureIfHome(playerId: number): number | null {
    const capture = checkCapture(this.state, playerId);
    if (!capture) return null;
    this.state = executeCapture(this.state, playerId, capture.objectId);
    this.refreshFlagStatus();
    return capture.objectId;
  }

  /** Which way a unit faces (guard perception reads it; nothing else does). */
  private setFacing(playerId: number, facing: Vec3 | undefined): void {
    if (!facing) return;
    this.state = { ...this.state, players: this.state.players.map(q => q.id === playerId ? { ...q, facing } : q) };
  }

  // ===========================================================================
  // The bot host's shared half (51.8)
  // ===========================================================================
  // The turn runner (ai/botTurnRunner.ts) drives bots through a BotHost; each host
  // supplies the actions (with its presentation), waits and chatter, and these.

  /** What a bot may know this turn: its team's fog and ghosts, incoming-fire intel (consumed once), and in round flows the round context. */
  botContext(player: PlayerState): BotFogContext | undefined {
    const intel = this.state.botIntel?.[player.id];
    const ctx = buildBotHostContext(this.state, player, intel, this.enterKernel(), this.batchQueue, this.plannedMoves);
    if (intel && intel.length > 0) { const { [player.id]: _used, ...rest } = this.state.botIntel!; this.state = { ...this.state, botIntel: rest }; }
    return ctx;
  }

  /**
   * A bot's shot in the plan-together flows: a prepaid order that flies in the round,
   * its intended target remembered for the shot record; nothing to wait for.
   */
  queueBotShot(player: PlayerState, params: FireParams): PlanResult {
    const check = checkFire(player, params.weaponKey);
    if (!check.ok) return { ok: false, reason: check.reason, code: check.reason };
    this.queueRoundOrder(player, params);
    this.setPlan({ targets: { ...this.plan.targets, [player.id]: player.botLastFireTargetPos ?? null } });
    this.botFlightSettled = true;
    return { ok: true };
  }

  /** A bot's shot is about to fly: the runner will wait for it to resolve. */
  expectBotFlight(): void { this.botFlightSettled = false; }

  /** Resolves once the current bot's shot has fully resolved (settleBotFlight). */
  waitForFlightResolution(): Promise<void> {
    if (this.botFlightSettled) return Promise.resolve();
    return new Promise(resolve => { this.botFlightWaiter = resolve; });
  }

  /** A bot's shot resolved: back to aiming, and wake the runner awaiting it. False when nothing was waiting. */
  settleBotFlight(): boolean {
    this.state = { ...this.state, phase: 'aiming', projectile: null };
    this.botFlightSettled = true;
    const wake = this.botFlightWaiter;
    this.botFlightWaiter = null;
    if (wake) wake();
    return !!wake;
  }

  /** The shot (and every bomblet) is over: the resolving phase. True when the match is over. */
  endShot(): boolean {
    this.state = startResolvingPhase(this.state);
    return isGameOver(this.state);
  }

  /** Once every knocked-back unit is down (on the match clock): `then(gameOver)` — landings can kill. */
  afterLandings(then: (gameOver: boolean) => void): void {
    void this.clock.waitUntil(() => !hasAirbornePlayers(this.state.players)).then(() => then(isGameOver(this.state)));
  }

  // ===========================================================================
  // Turn advance (51.6)
  // ===========================================================================

  /**
   * The current turn is over: resolve the next turn's start (core/resolveTurn.ts —
   * advance, mode ticks, eruptions, respawns, alert decay) and decide what comes
   * next. The host guards re-entry (turnAdvancing) before calling; beginTurn()
   * clears it.
   */
  advanceTurn(): TurnAdvance {
    this.turnAdvancing = true;
    this.shotSettling = false;
    const turnBefore = this.state.turnNumber;
    const r = resolveTurnStart(this.state);
    this.state = reconcileSeats(r.state);
    if (this.enterKernel()) for (const pi of r.destroyedPlanets) setPlanetAlive(pi, false);
    if (r.gameOver || this.state.phase === 'gameover') return { next: 'gameOver', r };
    if (this.state.phase === 'pickup') return { next: 'pickup', r };
    const nextPlayer = this.state.players[this.state.currentPlayerIndex];
    if (this.turnFlow === 'allPlan') {
      // Everyone plans: the planning order wrapped — the whole round flies now
      if (this.state.turnNumber > turnBefore && (this.batchQueue.length > 0 || this.plannedMoves.length > 0)) return { next: 'round', r };
    } else if (this.turnFlow === 'simultaneous'
      ? controlLeavesPlanners([...this.batchQueue.map(o => o.teamIndex), ...this.plannedMoves.map(m => m.teamIndex)], nextPlayer)
      : shouldResolveBatch(this.batchQueue, nextPlayer)) {
      // Plan-then-resolve: control is leaving the planning team — fire what they queued first
      return { next: 'batch', r };
    }
    return { next: 'turn', r };
  }

  /**
   * 64.6 step 2a (ARCHITECTURE §5.4 "64.6, the design"): the turn policy's boundary, one verb for every host — advance
   * the turn, then open the next unit's window: its turn begins, its fog is refreshed, the hazards near it act. A unit
   * that is down, or killed by a hazard at its turn's start, passes the turn on; the flow can stop the hops instead (game
   * over, the pickup prompt, a round or a batch to resolve). The order of every write is the one both hosts kept. Hosts
   * present the hops through the presenter's turn members.
   */
  passTurn(p: SimPresenter = SILENT_PRESENTER): TurnPass { return this.turnHops(true, p); }

  /** As passTurn, without the first advance: after a round or a batch, the current unit's window opens (64.6 step 2a). */
  openWindow(p: SimPresenter = SILENT_PRESENTER): TurnPass { return this.turnHops(false, p); }

  /** 64.6 step 2a: whose commit window is open — the current unit's while the match aims; null while the clock runs. */
  get turnWindow(): { unitId: number } | null {
    if (this.state.phase !== 'aiming' || isGameOver(this.state)) return null;
    const u = this.state.players[this.state.currentPlayerIndex];
    return u?.alive ? { unitId: u.id } : null;
  }

  private turnHops(advanceFirst: boolean, p: SimPresenter): TurnPass {
    this.clearOwedWindow();   // 64.6 step 4: whoever opens a window answers the one owed after a round
    const hops: TurnHop[] = [];
    let advance = advanceFirst;
    for (let hop = 0; hop <= this.state.players.length * 2; hop++) {
      let a: TurnAdvance | null = null;
      if (advance) {
        p.turnAdvancing?.();
        a = this.advanceTurn();
        p.turnAdvanced?.(a);
        if (a.next === 'gameOver' || isGameOver(this.state)) { hops.push({ advance: a, player: null, dead: false, proximity: null }); return { hops, next: 'gameOver', player: null }; }
        if (a.next === 'round' || (a.next === 'batch' && this.roundFlow)) {
          // 64.6 step 3: a plan round begins here, and the clock steps it (the batch in order stays the host's)
          hops.push({ advance: a, player: null, dead: false, proximity: null });
          this.beginPlanRound();
          return { hops, next: 'round', player: null };
        }
        if (a.next !== 'turn') { hops.push({ advance: a, player: null, dead: false, proximity: null }); return { hops, next: a.next, player: null }; }
      }
      advance = true;
      const begun = this.beginTurn();
      if (begun.kind === 'dead') { hops.push({ advance: a, player: null, dead: true, proximity: null }); continue; }
      p.turnBegun?.(begun.player);
      this.refreshFog(this.state.currentPlayerIndex);
      p.fogRefreshed?.(this.state.currentPlayerIndex);
      const prox = this.applyProximity();
      p.proximityApplied?.(prox, begun.player);
      hops.push({ advance: a, player: begun.player, dead: false, proximity: prox });
      if (prox?.died) continue;
      return { hops, next: 'window', player: this.state.players[this.state.currentPlayerIndex] };
    }
    return { hops, next: 'stalled', player: null };
  }

  /**
   * A unit's turn begins. 'dead' when the current unit is down — the host advances
   * the turn again (turn order, not array order, decides who is next).
   */
  beginTurn(): { kind: 'dead' } | { kind: 'ready'; player: PlayerState } {
    this.turnAdvancing = false;
    const player = this.state.players[this.state.currentPlayerIndex];
    if (!player.alive) return { kind: 'dead' };
    this.hasFiredThisTurn = false;
    return { kind: 'ready', player };
  }

  /**
   * A unit's own view: line of sight into its fog (blackout-aware), ghost markers
   * on the enemies it — or its datalinked team — can see (guard perception narrows
   * that with the alert layer on; line of sight alone otherwise).
   */
  refreshFog(playerIndex: number): void {
    const s = this.state;
    const player = s.players[playerIndex];
    if (!player || !player.alive) return;
    // A match built without fog (the action-clock fixtures, some mode tests) has nothing to refresh.
    if (!s.fog[playerIndex]) return;
    const colors = s.planets.map(p => this.truePlanetColors.get(p.id)!);
    const fog = [...s.fog];
    fog[playerIndex] = updateFogForPlayer(
      fog[playerIndex], player, s.planets, colors, s.turnNumber, this.enterKernel(),
      s.modeState.blackout?.darkenedPlanets, s.config.modeConfig.visibilityOnDark,
    );
    const visible = s.config.datalinkedLOS
      ? getTeamVisibleEnemies(player, s.players, s.planets, this.enterKernel(), s)
      : getVisibleEnemies(player, s.players, s.planets, this.enterKernel(), s);
    fog[playerIndex] = updateGhostMarkers(fog[playerIndex], visible, s.players, s.planets, knowledgeTurn(s));
    this.state = { ...s, fog };
  }

  /** Hazards near the unit whose turn begins (vents, geysers …): damage, a launch, possibly death. Null when nothing happened. */
  applyProximity(): ProximityOutcome | null {
    const s = this.state;
    const player = s.players[s.currentPlayerIndex];
    const prox = processProximityEffects(player, s.planets, s.hazards, s.config.seed, s.turnNumber);
    if (prox.damage <= 0 && !prox.launched) return null;
    this.state = { ...s, players: s.players.map(p => p.id === player.id ? prox.player : p) };
    return { playerId: player.id, damage: prox.damage, launched: prox.launched, died: !prox.player.alive };
  }

  // ===========================================================================
  // The simulation clock and flights (51.5b)
  // ===========================================================================

  // ===========================================================================
  // Inputs (R11 step 1; ARCHITECTURE §5.5 "R11, the design")
  // ===========================================================================

  /** Every input applied to this match, in order, refusals included: the battle's input log. */
  readonly inputLog: LoggedInput[] = [];
  /** 69.72 step 5 (DECISIONS 304): the plays' own re-tasks this Match refused, in order (core/plays.ts stepPlaysLogged) — beside the state, never in it. */
  readonly playRefusals: PlayRefusal[] = [];
  private readonly actorSeq = new Map<number, number>();

  /** Stamp an input body for now: this action tick and turn, the actor's next sequence number. */
  /** 68.4 (lane D): a `reserves` input — hold the team's reserve, or call it in (due at the next tick) where it chose (core/reserves.ts). */
  callReserves(body: Extract<MatchInputBody, { kind: 'reserves' }>): PlanResult {
    const r = applyReserveCall(this.state, body.team, body.call, body.at, this.actionTick);
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'reserves' };
    this.state = r.state;
    return { ok: true };
  }

  /** 69.23: a `mortarCall` input — the side's nearest set mortar in reach fires a mission (core/mortar.ts callMortar). */
  callMortar(body: Extract<MatchInputBody, { kind: 'mortarCall' }>): PlanResult {
    const r = callMortar(this.state, body.team, body.at, this.actionTick);
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'mortar' };
    this.state = r.state;
    return { ok: true };
  }

  /** 68.39: an `airstrike` input — one of the side's spent, the strike queued from its home base (core/airstrike.ts). */
  callAirstrike(body: Extract<MatchInputBody, { kind: 'airstrike' }>): PlanResult {
    const r = callAirstrike(this.state, body.team, body.at, this.actionTick, { ...(body.rounds !== undefined ? { rounds: body.rounds } : {}), ...(body.radiusPeople !== undefined ? { radiusPeople: body.radiusPeople } : {}), spread: true });   // (68.39b: a call may name its rounds and its circle; 69.67, §5.32: its lays a step a tick)
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'airstrike' };
    this.state = r.state;
    return { ok: true };
  }

  /** 69.3b: a `dropship` input — one of the side's spent, the flight in the air with its riders (core/dropship.ts). */
  callDropship(body: Extract<MatchInputBody, { kind: 'dropship' }>, by?: 'human' | 'commander'): PlanResult {
    const r = callDropship(this.state, body.team, body.at, this.actionTick, { payload: body.payload, ...(body.craft ? { craft: body.craft } : {}), ...(body.suppressor !== undefined ? { suppressor: body.suppressor } : {}), ...(body.vehicle ? { vehicle: body.vehicle } : {}), ...(by ? { by } : {}), ...(body.order ? { order: body.order } : {}), ...(body.hot ? { hot: true } : {}) });
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'dropship' };
    this.state = r.state;
    return { ok: true };
  }

  /** 69.10: a `lighter` input — a run called down to the side's strip (core/lighter.ts callLighter). */
  callLighter(body: Extract<MatchInputBody, { kind: 'lighter' }>, by?: 'human' | 'commander'): PlanResult {
    if (body.mode) {   // step 2: the standing order set; with no frames asked, that is the whole input
      if (!this.state.config.lighters) return { ok: false, reason: 'No lighters this match', code: 'lighter' };
      this.state = setLighterOrder(this.state, body.team, body.mode);
      if (body.frames === undefined) return { ok: true };
    }
    const r = callLighter(this.state, body.team, this.actionTick, { ...(body.frames !== undefined ? { frames: body.frames } : {}), ...(by ? { by } : {}) });
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'lighter' };
    this.state = r.state;
    return { ok: true };
  }

  /** 69.18: a `pickup` input — the side's wave ship sent to fetch a squad home (core/dropship.ts callPickup), the squad told to its ramp's foot till it lifts. */
  callPickup(body: Extract<MatchInputBody, { kind: 'pickup' }>, by?: 'human' | 'commander'): PlanResult {
    const r = callPickup(this.state, body.team, body.squad, body.at, this.actionTick, body.priority, body.craft, body.suppressor, body.to);
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'pickup' };
    const foot = flightRampFoot(r.state, r.flight);
    const o = applyOrderInput(r.state, { kind: 'order', team: body.team, to: { squad: body.squad }, tier: 'task', order: { kind: 'hold', planetIndex: foot.planetIndex, surfaceDir: foot.surfaceDir }, expires: r.flight.liftTick, ...(by ? { by } : {}), claim: 'lift' }, this.actionTick, { useWasm: this.enterKernel() });   // (69.72 step 5, DECISIONS 304: being lifted, rank 2)
    this.state = 'refused' in o ? r.state : o.state;
    return { ok: true };
  }

  /** 69.62: a `rescue` input — the side's gunship called hot over the threat, its wave ship to follow under it (core/rescue.ts callRescue). */
  callRescue(body: Extract<MatchInputBody, { kind: 'rescue' }>, by?: 'human' | 'commander'): PlanResult {
    const r = callRescue(this.state, body.team, body.squad, body.at, body.threat, this.actionTick, by ?? 'commander', this.enterKernel(), body.cause);
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'rescue' };
    this.state = r.state;
    return { ok: true };
  }

  /** 69.32 step 2: a `supplyDrop` input — a supply drone of the side's onto ground it holds or contests (core/supplyDrone.ts callSupplyDrop). */
  callSupplyDrop(body: Extract<MatchInputBody, { kind: 'supplyDrop' }>): PlanResult {
    const r = callSupplyDrop(this.state, body.team, body.at, body.load, this.actionTick);
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'supplyDrop' };
    this.state = r.state;
    return { ok: true };
  }

  /** DECISIONS 200 step 5: a `podDrop` input — one of the side's spent, a squad of its waiting coming down at the point (core/pods.ts). */
  callPods(body: Extract<MatchInputBody, { kind: 'podDrop' }>, by?: 'human' | 'commander'): PlanResult {
    const r = callPods(this.state, body.team, body.at, this.actionTick, by, body.order);
    if ('refused' in r) return { ok: false, reason: r.refused, code: 'podDrop' };
    this.state = r.state;
    return { ok: true };
  }

  /** 69.6h (lane D): a `respawnAt` input — the seat's respawn pick, kept for the match (core/respawnPick.ts). */
  setRespawnPick(actor: number, team: number, body: Extract<MatchInputBody, { kind: 'respawnAt' }>): PlanResult {
    if (body.at !== null) {
      const why = respawnPickRefusal(this.state, team, body.at);
      if (why) return { ok: false, reason: why, code: 'respawnAt' };
    }
    this.state = applyRespawnPick(this.state, actor, body.at);
    return { ok: true };
  }

  /** DECISIONS 200 step 3: a `spawnAt` input — the side's standing order on where its waves land (core/spawnAt.ts). */
  setSpawnAt(body: Extract<MatchInputBody, { kind: 'spawnAt' }>): PlanResult {
    const why = spawnAtRefusal(this.state, body.team, body.at);
    if (why) return { ok: false, reason: why, code: 'spawnAt' };
    this.state = applySpawnAt(this.state, body.team, body.at);
    return { ok: true };
  }

  /** 68.25 (lane D): a `vehicles` input — the team kept on foot, or free again (core/vehicleUse.ts). */
  setVehicleUse(body: Extract<MatchInputBody, { kind: 'vehicles' }>): PlanResult {
    this.state = applyVehicleUse(this.state, body.team, body.use);
    return { ok: true };
  }

  /** 63.1n with 67.75: a `motorPool` input — the side's standing order (auto / by hand), then an unpack by hand (core/motorPoolStock.ts). */
  orderMotorPool(body: Extract<MatchInputBody, { kind: 'motorPool' }>): PlanResult {
    if (!poolOf(this.state, body.team)) return { ok: false, reason: 'No motor pool', code: 'motorPool' };
    let next = body.mode ? setPoolOrder(this.state, body.team, body.mode) : this.state;
    if (body.unpack === 'next') {
      // the side's oldest loss still to replace (the queue a hand-held pool keeps): its kind, its owner and home, the loss taken off the queue
      const i = (next.vehicleRefits ?? []).findIndex(r => r.team === body.team);
      if (i < 0) return { ok: false, reason: 'Nothing lost to replace', code: 'motorPool' };
      const lost = next.vehicleRefits![i];
      const r = startUnpack(next, body.team, lost.type, this.actionTick, lost.ownerId, lost.home);
      if ('refused' in r) return { ok: false, reason: r.refused, code: 'motorPool' };
      next = { ...r.state, vehicleRefits: r.state.vehicleRefits!.filter((_, k) => k !== i) };
    } else if (body.unpack) {
      const owner = next.players.filter(p => p.teamIndex === body.team).sort((a, b) => a.id - b.id)[0];
      const r = owner ? startUnpack(next, body.team, body.unpack, this.actionTick, owner.id) : { refused: 'No one on the side' };
      if ('refused' in r) return { ok: false, reason: r.refused, code: 'motorPool' };
      next = r.state;
    }
    this.state = next;
    return { ok: true };
  }

  /** §0.11 step D: an `efforts` input — the side's efforts now (core/efforts.ts). */
  setEfforts(body: Extract<MatchInputBody, { kind: 'efforts' }>): PlanResult {
    const r = applyEfforts(this.state, body, this.actionTick);
    if ('refused' in r) return { ok: false, reason: r.refused, code: r.code };
    this.state = r.state;
    return { ok: true };
  }

  /** 68.22 (lane D): a `coordinate` input — a play over several squads (core/plays.ts). */
  coordinate(body: Extract<MatchInputBody, { kind: 'coordinate' }>): PlanResult {
    const r = applyCoordinate(this.state, body, this.actionTick);
    if ('refused' in r) return { ok: false, reason: r.refused, code: r.code };
    this.state = r.state;
    return { ok: true };
  }

  /** R11 step 2 (lane D): an `order` input onto the state's squads (core/squads.ts). */
  order(body: Extract<MatchInputBody, { kind: 'order' }>): PlanResult<{ squadIds: number[] }> {
    // (69.18's guard — a squad being fetched home keeps its pickup's task — is 304's lift claim now, rank 2: core/squads.ts)
    const r = applyOrderInput(this.state, body, this.actionTick, { useWasm: this.enterKernel() });   // 68.0: the knowledge limit reads sight through the kernel
    if ('refused' in r) {
      // X11: a refused call-in still goes into the state's call-in log (the panel and the log say why)
      const logged = takeRefusalLog();
      if (logged) this.state = logged;
      return { ok: false, reason: r.refused, code: r.code };
    }
    this.state = r.state;
    return { ok: true, squadIds: r.squadIds };
  }

  stamp(actor: number, body: MatchInputBody): MatchInput {
    const seq = this.actorSeq.get(actor) ?? 0;
    this.actorSeq.set(actor, seq + 1);
    return { tick: this.actionTick, turn: this.state.turnNumber, actor, seq, body };
  }

  /**
   * Apply one input: the one way a host is meant to change match state (step 2 moves the hosts here).
   * An input stamped for another tick or turn is refused (scheduling ahead comes with brains, step 6).
   * Applied or refused, it goes into `inputLog`, so a replay refuses what the original refused.
   */
  apply(input: MatchInput, p: SimPresenter = SILENT_PRESENTER): PlanResult<object> {
    const r: PlanResult<object> = input.tick !== this.actionTick || input.turn !== this.state.turnNumber
      ? refuse(`Input stamped for tick ${input.tick}, turn ${input.turn}; the match is at tick ${this.actionTick}, turn ${this.state.turnNumber}`, 'stamp')
      : dispatchInput(this, input.actor, input.body, p);
    this.inputLog.push(r.ok ? input : { ...input, refused: r.code ?? (r.reason || 'refused') });
    return r;
  }

  // ===========================================================================
  // Moving time (64.1): the three ways a host advances a Match — and the only ways
  // ===========================================================================

  /**
   * Wall-time hosts: advance the simulation by `dt` seconds of (scaled) time in fixed PHYS_DT
   * steps — the delay before the next cluster bomblet, round playback or the flight in the air,
   * then airborne units — in that order every step, whatever the frame rate. At most `maxSteps`
   * per call (default 120, one second): the backlog beyond it is dropped, never spirals, and
   * dropping wall time never changes a tick. `until` stops early (the match is over). Returns the
   * ticks stepped.
   */
  advance(dt: number, p: SimPresenter, opts: { maxSteps?: number; until?: () => boolean } = {}): number {
    const maxSteps = opts.maxSteps ?? 120;
    this.simAccumulator += dt;
    if (this.simAccumulator > PHYS_DT * maxSteps) this.simAccumulator = PHYS_DT * maxSteps;
    let n = 0;
    while (this.simAccumulator >= PHYS_DT && !opts.until?.()) {
      this.simAccumulator -= PHYS_DT;
      this.stepOnce(p);
      n++;
    }
    return n;
  }

  /** Fixed-tick hosts (the action harnesses, auto-resolve): step up to `ticks` ticks, stopping once `until` says so. Returns the ticks stepped. */
  runTicks(ticks: number, p: SimPresenter, until?: () => boolean): number {
    let n = 0;
    while (n < ticks && !until?.()) { this.stepOnce(p); n++; }
    return n;
  }

  /** A shot or one of its bomblets is still in the air (the turn clock). */
  get inFlight(): boolean {
    return !!this.pendingClusterLaunch || (this.state.phase === 'firing' && !!this.state.projectile && !this.flightEnded);
  }

  /**
   * The turn-clock harness's settle (what the game does over wall time): step the shot and its
   * bomblets to their end, then everyone the round left in the air until they land — stopping, as
   * the game does, once the match is decided. The airborne phase is capped (a unit lost in orbit),
   * and the host hears about it through `timedOut`.
   */
  runUntilSettled(p: SimPresenter, maxAirborneTicks = MAX_AIRBORNE_TICKS): { airborneTicks: number; timedOut: boolean } {
    this.runRoundOut(p);   // a plan round to its close (its own cap bounds it)
    for (let guard = 0; this.inFlight && guard < MAX_FLIGHT_TICKS; guard++) this.stepOnce(p);
    let airborneTicks = 0;
    while (hasAirbornePlayers(this.state.players) && airborneTicks < maxAirborneTicks) {
      this.stepOnce(p);
      airborneTicks++;
      if (isGameOver(this.state)) break;
    }
    return { airborneTicks, timedOut: airborneTicks >= maxAirborneTicks && hasAirbornePlayers(this.state.players) };
  }

  /** One PHYS_DT step of everything that moves. */
  /**
   * The per-tick checksum (src/match/tickChecksum.ts): the state plus the decision state this Match
   * holds outside it — the action tick, pending intents, heard shots, a cluster waiting to launch.
   */
  tickChecksum(): number { return tickChecksum(this.state); }

  /** The tick (state.tick): the action tick on the action clock, the ticks stepped on the turn clock. */
  get tick(): number { return this.state.tick ?? 0; }
  /** When set, stepOnce records tickChecksum() every `traceEvery` steps (harness, goldens, smoke). */
  tickTrace: number[] | null = null;
  traceEvery = 1;

  /** The per-tick primitive. Hosts don't call it: they move time through advance, runTicks or runUntilSettled (64.1). */
  stepOnce(p: SimPresenter): void {
    p.beforeTick?.();
    const policy = this.clockPolicy;
    // 64.6 step 4: the turn boundary after a round is the policy's — once the round has closed and everyone it threw has
    // landed, the next window opens at the top of the following tick: the point in time the hosts opened it (after the
    // settling tick had counted and been traced), so nothing at the boundary sees the tick a step early
    if (!policy.windowAlwaysOpen && this.plan.windowOwed && !this.liveRound && !this.inFlight && !hasAirbornePlayers(this.state.players)) {
      this.clearOwedWindow();
      if (!isGameOver(this.state)) p.windowOpened?.(this.turnHops(false, p));
    }
    this.stepOnceInner(p, policy);
    if (!policy.ownTick) this.state = { ...this.state, tick: this.tick + 1 };   // (the continuous step counts its own tick, mid-step)
    if (this.tickTrace && this.tick % this.traceEvery === 0) this.tickTrace.push(this.tickChecksum());
    p.afterTick?.();
  }

  /** 64.6 step 1: the clock policy this match runs under (match/clockPolicy.ts) — the tick asks it, never `config.clock`. */
  get clockPolicy(): ClockPolicy { return clockPolicyFor(this.state.config); }

  /**
   * 64.6 step 6: one system order for both policies, and what differs is policy data — no branch on the clock's kind.
   * 1. What commits set going plays out (a bomblet's countdown, a live round, a precomputed flight): nothing is ever
   *    pending on a clock whose window never closes, so this is a no-op there, not a branch.
   * 2. Every unit acts, when the policy's window is always open (the continuous clock's tick).
   * 3. The airborne step (a round steps its own; what it leaves flying steps from the next tick).
   * 4. The commit window's rules, when the policy has one: a settled shot hands the turn on.
   * (5. The window owed after a round opens at the top of the next tick — stepOnce.)
   */
  private stepOnceInner(p: SimPresenter, policy: ClockPolicy): void {
    const rounding = this.liveRound !== null;
    this.stepTurnClock(p);
    if (policy.windowAlwaysOpen) this.stepAction(p);
    if (!rounding) {
      const air = this.stepAirborne();
      if (air) p.airborne(air);
      // 64.6 2b-ii: the turn policy's rule once a unit's shot has settled
      if (!policy.windowAlwaysOpen && this.shotSettling && !this.hostSettlesShots && !this.inFlight && !hasAirbornePlayers(this.state.players)) this.settleShot(p);
    }
  }

  /** 64.6 step 4: whether the policy still owes a window after a round (state: `plan.windowOwed`; a host that must open one itself asks this first). */
  get windowPending(): boolean { return this.plan.windowOwed === true; }
  private clearOwedWindow(): void {
    if (!this.plan.windowOwed) return;
    const { windowOwed: _owed, ...rest } = this.plan;
    this.state = { ...this.state, plan: rest };
  }

  /**
   * 64.6 step 2b-ii: a classic shot does not end its turn. When it settles, the turn goes on by this rule (it was the
   * hosts', twice — Game.continueAfterShot and the harness's fire returning to its runner): a batch running takes its
   * next order (the host's); a bot goes back to aiming and its runner takes its next step; a human with movement left
   * keeps the window; a human with none passes the turn. A network peer's match keeps the host's path (`hostSettlesShots`).
   */
  private shotSettling = false;
  /** P2P (frozen, decisions 28/41): the host settles shots itself, as before 2b-ii. */
  hostSettlesShots = false;

  private settleShot(p: SimPresenter): void {
    this.shotSettling = false;
    if (isGameOver(this.state)) { p.shotSettled?.({ next: 'gameOver' }); return; }
    if (this.batchActive) { p.shotSettled?.({ next: 'batch' }); return; }
    const unit = this.state.players[this.state.currentPlayerIndex];
    if (unit?.isBot) {
      this.state = { ...this.state, phase: 'aiming', projectile: null };
      this.botFlightSettled = true;
      p.shotSettled?.({ next: 'bot' });
      return;
    }
    if (unit?.alive && unit.movementRemaining > 0) {
      this.resumeAiming();
      p.shotSettled?.({ next: 'window' });
      return;
    }
    p.shotSettled?.({ next: 'pass', pass: this.passTurn(p) });
  }

  /** 64.6 step 2b-ii: wake the bot runner awaiting the settled shot (the host's pace). False when nothing was waiting. */
  wakeBotRunner(): boolean {
    const wake = this.botFlightWaiter;
    this.botFlightWaiter = null;
    if (wake) wake();
    return !!wake;
  }

  /**
   * 64.6 step 2b-ii: the end of a turn, as an input (`skip`; decision 16, inputs only). It passes the turn when it
   * comes from the unit whose turn it is — its brain (a bot; the actor is its id) or its own team's seat (a human) —
   * while it aims; never from another seat. The answer carries the pass for the host to walk.
   */
  endTurnBy(actor: number, unit: PlayerState, p: SimPresenter): PlanResult<{ pass: TurnPass }> {
    // (a match already decided still passes: the pass answers 'gameOver', as the hosts' passTurn always did)
    const current = this.state.players[this.state.currentPlayerIndex];
    if (!current || current.id !== unit.id) return refuse(`It is not ${unit.name}'s turn`, 'window');
    if (this.state.phase === 'firing' || this.state.phase === 'resolving' || this.turnAdvancing || this.roundRunning) return refuse('Not while the turn plays out', 'window');
    const brain = seatTeam(actor) === null && actor === unit.id && unit.isBot;
    const seat = seatTeam(actor) !== null && seatTeam(actor) === unit.teamIndex && !unit.isBot;
    if (!brain && !seat) return refuse('Only the unit\'s own seat or brain ends its turn', 'actor');
    return { ok: true, pass: this.passTurn(p) };
  }

  /** The turn policy's tick: what a commit set going plays out — a bomblet's countdown, a plan round's step, a flight. */
  private stepTurnClock(p: SimPresenter): void {
    if (this.pendingClusterLaunch) {
      const left = this.pendingClusterLaunch.stepsRemaining - 1;
      this.pendingClusterLaunch = { ...this.pendingClusterLaunch, stepsRemaining: left };
      if (left <= 0) {
        const next = this.pendingClusterLaunch.projectile;
        this.pendingClusterLaunch = null;
        // Bomblets are recorded under the parent's params, flagged as submunitions
        if (this.currentShot) this.currentShot = { ...this.currentShot, submunition: true };
        this.launchShot(next, p);
      }
    } else if (this.liveRound) {
      this.stepLiveRound(p);
    } else if (this.state.phase === 'firing' && this.state.projectile && !this.flightEnded) {
      this.stepFlight(p);
    }
  }

  // ===========================================================================
  // The action clock (Phase 59): real time — every unit acts every tick
  // ===========================================================================

  /**
   * 64.8 (R11b): bots' memory, staged by the driver while every unit decides from the start-of-tick state,
   * committed into the state once they all have — so the state holds what the bots know, and a bot rebuilt
   * from it decides the same.
   */
  private stagedBrains: Map<number, ActionBrainState> | null = null;
  private stagedTracks: Array<{ team: number; id: number; entry: { pos: Vec3; tick: number } | null }> | null = null;
  /** A driver stages a unit's memory after deciding for it. */
  stageBrain(unitId: number, brain: ActionBrainState): void { (this.stagedBrains ??= new Map()).set(unitId, brain); }
  /** A driver stages a team's shared picture (L1) after a unit has used it. */
  stageTrackEdit(team: number, id: number, entry: { pos: Vec3; tick: number } | null): void { (this.stagedTracks ??= []).push({ team, id, entry }); }
  /**
   * R11 step 6 / 64.11, one mechanism for every brain input (lane B's brain-rules row): a brain stages an input that is
   * due this tick (decided at its basis, L ticks ago) — a gun decision (a `decision` input), a drone launch, an order to
   * its squad or itself — and it enters through `apply` with the brain as its actor once every unit has decided, in
   * staging order; refused like any input, and the input log keeps it — the battle's decision log (62.12).
   */
  private stagedInputs: Array<{ unitId: number; body: MatchInputBody }> | null = null;
  stageInput(unitId: number, body: MatchInputBody): void { (this.stagedInputs ??= []).push({ unitId, body }); }

  /** 64.11: the gun decision in force for a seated unit (a `decision` input's body). Refused when it no longer sits in that seat. */
  setGunDecision(u: PlayerState, d: BotDecision): PlanResult {
    if (d.kind !== 'gun') return refuse('Not a gun decision', 'kind');
    const seated = u.mount?.bodyId === d.structureId ? mountedSeat(u, this.state) : null;
    if (!seated) return refuse('Not in that seat', 'seat');
    if (!seated.seat.weapons.includes(d.shot.weaponKey)) return refuse('Not one of the seat\'s weapons', 'weapon');
    this.state = { ...this.state, gunOrders: { ...(this.state.gunOrders ?? {}), [u.id]: { ...d } } };
    return { ok: true };
  }
  private commitBotMemory(): void {
    this.commitBrains();
    // the brains' inputs due this tick (after the memory that produced them is in the state)
    const due = this.stagedInputs;
    this.stagedInputs = null;
    if (due) for (const d of due) this.apply(this.stamp(d.unitId, d.body));
  }
  private commitBrains(): void {
    if (!this.stagedBrains && !this.stagedTracks) return;
    let next = this.state;
    if (this.stagedBrains) {
      const brains = { ...(next.actionBrains ?? {}) };
      for (const [id, b] of this.stagedBrains) brains[id] = b;
      next = { ...next, actionBrains: brains };
    }
    if (this.stagedTracks) {
      const tracks = { ...(next.teamTracks ?? {}) };
      // §5.16 step 3: every unit's edits, in the players' order, onto the start-of-tick tracks (a Map keeps their order: a
      // sighting of one already tracked stays in its place, a new one goes last, a witnessed death leaves)
      const work = new Map<number, Map<number, { pos: Vec3; tick: number }>>();
      for (const e of this.stagedTracks) {
        let m = work.get(e.team);
        if (!m) { m = new Map(next.teamTracks?.[e.team] ?? []); work.set(e.team, m); }
        if (e.entry) m.set(e.id, e.entry); else m.delete(e.id);
      }
      for (const [team, m] of work) tracks[team] = [...m.entries()];
      next = { ...next, teamTracks: tracks };
    }
    this.state = next;
    this.stagedBrains = null;
    this.stagedTracks = null;
  }

  /** The intent in force per actor-held unit (state.intents; 64.10). */
  get actionIntents(): Readonly<Record<number, UnitIntent>> { return this.state.intents ?? EMPTY_INTENTS; }
  /** A bot's intent this tick (ai/actionBot.ts); without one, bots stand still. */
  /**
   * The bots' brain (ai/actionBot.ts). 68.26: its `observe`, when it has one, is a held unit's eyes — the Match calls it for
   * each unit a person holds, so what the person sees is told to the team as a bot's look is.
   */
  actionDriver: DecideDriver | null = null;   // (§5.16 step 1: a brain sees a DecideView — the Match is one)
  private actionOverSent = false;
  /**
   * 67.14: the match record (core/scoreboard.ts) — shots, hits, kills, deaths, captures, holds — kept beside the state,
   * never in it: presentation reads it (the scoreboard, the end card, the sector's battle report); nothing in play does.
   */
  private record_: MatchRecord | null = null;
  get record(): MatchRecord { return this.record_ ?? (this.record_ = emptyRecord(this.state)); }

  /** 62.43: the team's seat orders a fire mission for one gun (an `order` input through apply). True when applied. */
  seatFireMission(team: number, gunId: number, planetIndex: number, surfaceDir: Vec3, by?: OrderIssuer): boolean {   // (69.72 step 5, DECISIONS 304: tagged with its giver)
    return this.apply(this.stamp(seatActor(team), { kind: 'order', team, to: { units: [gunId] }, tier: 'task', order: { kind: 'fireMission', planetIndex, surfaceDir, spread: CALL_IN_SPREAD }, ...(by ? { by } : {}) })).ok;
  }

  /** 62.43 / X2: the team's seat marks something for the whole team (a task on every squad). The count applied. */
  seatMark(team: number, mark: Omit<Extract<SquadOrder, { kind: 'mark' }>, 'kind' | 'turn'>): number {
    let n = 0;
    for (const b of markBodies(this.state, team, mark)) if (this.apply(this.stamp(seatActor(team), b)).ok) n++;
    return n;
  }
  /** Ticks of the action clock so far (bots pace their perception by it): state.tick. */
  get actionTick(): number { return this.tick; }
  /** Each unit's last shot: where and on which tick — what others can hear (state.heard). */
  get lastShot(): Readonly<Record<number, { pos: Vec3; tick: number; loud?: number; step?: boolean }>> { return this.state.heard ?? EMPTY_HEARD; }

  /** A human's intent for a unit, on the grid (64.10, 116: `quantizeIntent`); it holds until the next — its `turn` and `jump` for one tick. */
  setIntent(playerId: number, intent: UnitIntent): void { this.state = { ...this.state, intents: { ...this.state.intents, [playerId]: quantizeIntent(intent) } }; }

  /**
   * Possession (59.6c, WORKBOARD §8.4): the commander of `team` takes `unitId` — its bot stops and
   * the host's intents drive it — and every other unit of the team goes back to its bot (which
   * resumes where it is). `null` releases them all. A unit of that team only; returns
   * whether the switch happened. A downed unit stays yours through its respawn. An input like any other
   * (replays and netcode carry it).
   */
  possess(team: number, unitId: number | null): boolean {
    return this.takeUnit(seatActor(team), team, unitId).ok;
  }

  /**
   * 64.10 (R11, lane C): the seat `actor` (of `team`) takes a unit, or lets go (null). Who holds a unit is state
   * (`state.controllers`): a held unit follows its holder's intents and its brain rests; let go, the brain drives it
   * again. Refused: a unit of another team, a dead one, one another seat holds. A hold outlives death (the unit
   * respawns as the holder's). The team's other units are bots, as before (`isBot`, which presentation reads).
   */
  takeUnit(actor: number, team: number, unitId: number | null): PlanResult<object> {
    if (unitId !== null) {
      const u = this.state.players.find(p => p.id === unitId);
      if (!u || u.teamIndex !== team) return { ok: false, reason: 'Not a unit of that team', code: 'team' };
      if (!u.alive) return { ok: false, reason: 'That unit is down', code: 'dead' };
      const holder = this.state.controllers?.[unitId];
      if (holder !== undefined && holder !== actor) return { ok: false, reason: 'Another seat holds that unit', code: 'held' };
    }
    const controllers: Record<number, number> = {};
    for (const [id, a] of Object.entries(this.state.controllers ?? {})) if (a !== actor) controllers[Number(id)] = a;
    if (unitId !== null) controllers[unitId] = actor;
    this.state = {
      ...this.state,
      controllers,
      players: this.state.players.map(p => p.teamIndex !== team ? p : { ...p, isBot: p.id !== unitId, ...(p.id !== unitId && !p.botConfig ? { botConfig: { skill: 0.5, personality: BOT_PRESETS.balanced } } : {}) }),
    };
    if (this.state.intents) {
      const intents = { ...this.state.intents };
      for (const p of this.state.players) if (p.teamIndex === team && p.id !== unitId) delete intents[p.id];
      this.state = { ...this.state, intents };
    }
    return { ok: true };
  }


  /** 69.4 step 5: where a side's units are sent forward — its arrival anchor under Operations (69.4 step 3), else its standing beacon. */
  private forwardAnchor(team: number, bi: number): { s: StructureState; at: 'fob' | 'relay' | 'beacon' } | null {
    const ops = this.state.modeState.points;
    const beacon = this.state.config.beacons ? standingBeacon(this.state, team, bi) : null;
    if (ops?.front && ops.push) {
      // (DECISIONS 200 step 2: the ranking's point nearest the sector in play, the beacon among them)
      const home = this.state.board?.bases.find(b => b.team === team);
      const landing = landingCentre(this.state, team, home ? home.dir : this.state.actionHomes?.[team], !!home);
      const toward = opsTarget(this.state, team);
      const anchor = landing || toward ? sideSpawn(this.state, team, toward ? toward.surfaceDir : landing, null, beacon, true, closedSpawns(this.state, team, this.actionTick)) : null;   // (200 step 3: by its side's order, with the hold-back)
      if (anchor) return { s: anchor, at: anchor.type === 'fob' ? 'fob' : anchor.type === 'beacon' ? 'beacon' : 'relay' };
    }
    return beacon ? { s: beacon, at: 'beacon' } : null;
  }

  /** Dead units with tickets come back (executeRespawn spends the ticket) beside their team's nearest owned point. */
  /**
   * DECISIONS 200 step 5: the pod drops due — each unit still down comes back in a pod in a ring at the point (a respawn, its
   * ticket, the lander's fall), its kit as a wave's, and the drop's units make one drop squad holding there by the caller's order.
   */
  private stepPodDrops(): ActionEvent[] {
    const out: ActionEvent[] = [];
    const r = stepPods(this.state, this.actionTick);
    this.state = r.state;
    for (const d of r.due) {
      let k = 0;
      for (const id of d.units) {
        const p = this.state.players.find(q => q.id === id);
        if (!p || p.alive || hqFallBites(this.state, p.teamIndex)) continue;
        const before = this.state;
        this.state = executeRespawn(this.state, id);
        if (this.state === before) continue;   // (no ticket left: it stays down)
        const spot = beaconArrival(this.state, { planetIndex: d.planetIndex, surfaceDir: d.at }, k++);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === id
          ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: facingObjective(this.state, p.teamIndex, spot), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q), ...(personSetOf(this.state).k < 1 ? { landingLeft: ARRIVAL.fallSeconds } : {}) } : q) };
        out.push({ kind: 'respawned', unitId: id, at: 'pod' });
        this.state = joinDropSquad(this.state, `pod:${d.id}`, d.team, id, { planetIndex: d.planetIndex, surfaceDir: d.at }, d.by, d.order);
      }
    }
    this.kitArrivals(out);
    return out;
  }

  /**
   * DECISIONS 208 (the owner: "mark those re-deploying via dropship as technically alive once the flight is up ... leaning yes
   * so you could wait the n seconds in the can before it takes off"): each side's parked wave ship takes its waiting riders
   * aboard — the sortie it boards for (core/dropship.ts boardingSortie), its fallen in their order up to the container's seats:
   * each back alive (its ticket spent, its kit as a wave's), seated in the bay on the pad, `boarded` (still, not seen, not hit,
   * not on a point) till its walk down the ramp. The sortie's wait runs from its first boarded. Bots and the player alike.
   */
  private boardShips(): ActionEvent[] {
    const out: ActionEvent[] = [], came: number[] = [];
    for (const team of Object.keys(this.state.shipPads ?? {}).map(Number).sort((a, b) => a - b)) {
      const q = boardingSortie(this.state, team);
      if (!q || hqFallBites(this.state, team)) continue;
      let seated = q.units.filter(id => this.state.players.find(x => x.id === id)?.boarded).length;
      const first = seated === 0;
      for (const id of q.units) {
        if (seated >= DROPSHIP_PAYLOADS.troops.seats) break;
        const p = this.state.players.find(x => x.id === id);
        if (!p || p.alive) continue;
        const before = this.state;
        this.state = executeRespawn(this.state, id);
        if (this.state === before) continue;   // (no ticket left: it stays down)
        const seat = padSeat(this.state, team, seated++), facing = this.state.shipPads![team].facing;
        this.state = { ...this.state, players: this.state.players.map(u => u.id === id
          ? { ...u, boarded: true as const, planetIndex: seat.planetIndex, surfaceDir: seat.surfaceDir, heading: facing, aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(u), ...freshCharges(u) } : u) };
        out.push({ kind: 'respawned', unitId: id, at: 'dropship' });
        came.push(id);
      }
      if (first && seated > 0) this.state = { ...this.state, sorties: this.state.sorties!.map(x => (x === q ? { ...x, since: this.actionTick } : x)) };   // (its wait from its first aboard)
    }
    this.kitArrivals(out);
    if (came.length && this.state.config.fluidSquads && this.state.squads?.length) {
      const r = squadsOnRespawn(this.state, came, this.state.config.fluidSquads);
      this.state = r.state;
      out.push(...r.events);
    }
    return out;
  }

  /**
   * DECISIONS 209 (5): where each side's wave ship flies its next run — its side's forward point as a wave's arrival was sent
   * before (the push's ranking toward the sector in play, else the ranking toward the enemy; spawnAt, the beacon, relays and
   * FOBs among them; core/spawnAt.ts sideSpawn), none within walkPeople of its pad (the riders walk); with the seconds its run
   * takes (padRunSeconds). Worked out once a second. 69.37 step 4: while one of its side's mounted plays has an air element
   * for it, that play's landing behind its objective instead (`play`; hot by the commander's rule, core/hotCall.ts).
   */
  private shipDestination(team: number): { at: number; planetIndex: number; dir: Vec3; runSeconds: number; play?: number; hot?: true } | null {
    const bi = battlePlanetIndex(this.state), pad = this.state.shipPads?.[team];
    if (bi < 0 || !pad || pad.planetIndex !== bi) return null;
    const air = airDest(this.state, team);
    if (air && air.planetIndex === bi) {
      const at = { planetIndex: bi, surfaceDir: air.land };
      return { at: -1, planetIndex: bi, dir: vecNormalize(air.land), runSeconds: padRunSeconds(this.state, team, bi, air.land), play: air.play, ...(hotWanted(this.state, team, at) ? { hot: true as const } : {}) };
    }
    const beacon = this.state.config.beacons ? standingBeacon(this.state, team, bi) : null;
    const closed = closedSpawns(this.state, team, this.actionTick), ops = this.state.modeState.points;
    let to: { at: number; dir: Vec3 } | null = null;
    if (ops?.front && ops.push) {
      const home = this.state.board?.bases.find(b => b.team === team);
      const landing = landingCentre(this.state, team, home ? home.dir : this.state.actionHomes?.[team], !!home);
      const toward = opsTarget(this.state, team);
      const anchor = sideSpawn(this.state, team, toward ? toward.surfaceDir : landing, landing, beacon, true, closed);
      to = anchor ? { at: anchor.id, dir: anchor.surfaceDir } : landing ? { at: -1, dir: landing } : null;
    } else {
      const enemy = Object.entries(this.state.actionHomes ?? {}).filter(([t]) => Number(t) !== team).sort((a, b) => Number(a[0]) - Number(b[0]))[0]?.[1] ?? null;
      const ranked = sideSpawn(this.state, team, enemy, null, beacon, false, closed);
      to = ranked ? { at: ranked.id, dir: ranked.surfaceDir } : null;
    }
    if (!to || shipWalks(this.state, team, bi, to.dir)) return null;
    return { at: to.at, planetIndex: bi, dir: vecNormalize(to.dir), runSeconds: padRunSeconds(this.state, team, bi, to.dir) };
  }

  /** 209 (5): unit `id` aboard its side's parked ship — seated in the bay (`boarded`, as 208's), the sortie to its destination joined (its wait from its first aboard). */
  private boardShip(id: number): void {
    const u = this.state.players.find(p => p.id === id)!, team = u.teamIndex, pad = this.state.shipPads![team], dest = pad.dest!;
    const q = boardingSortie(this.state, team);
    const seated = q ? q.units.filter(x => this.state.players.find(p => p.id === x)?.boarded).length : 0;
    const sorties = q
      ? this.state.sorties!.map(x => (x === q ? { ...x, at: dest.at, dir: dest.dir, units: [...x.units, id], ...(seated === 0 ? { since: this.actionTick } : {}) } : x))
      : [...(this.state.sorties ?? []), { team, at: dest.at, planetIndex: dest.planetIndex, dir: dest.dir, units: [id], since: this.actionTick }];
    const seat = padSeat(this.state, team, seated);
    this.state = { ...this.state, sorties, players: this.state.players.map(p => (p.id === id ? { ...p, boarded: true as const, planetIndex: seat.planetIndex, surfaceDir: seat.surfaceDir, heading: pad.facing, stance: undefined } : p)) };
  }

  /** 209 (5): once a second each side's ship's destination (its waiting sortie's too); every tick, who stands in its way in boards. */
  private stepShipBoarding(): void {
    const teams = Object.keys(this.state.shipPads!).map(Number).sort((a, b) => a - b);
    if (this.actionTick % Math.round(1 / PHYS_DT) === 0) {
      for (const t of teams) {
        const dest = this.shipDestination(t), pad = this.state.shipPads![t];
        const { dest: _d, ...rest } = pad;
        this.state = { ...this.state, shipPads: { ...this.state.shipPads, [t]: dest ? { ...rest, dest } : rest } };
        const q = dest ? boardingSortie(this.state, t) : null;
        if (q && dest && (q.at !== dest.at || q.dir !== dest.dir)) this.state = { ...this.state, sorties: this.state.sorties!.map(x => (x === q ? { ...x, at: dest.at, dir: dest.dir } : x)) };
      }
    }
    for (const t of teams) {
      if (!boardingOpen(this.state, t)) continue;
      for (const u of this.state.players) {
        if (u.teamIndex !== t || !u.alive || u.boarded || u.mount || u.airborne || (u.landingLeft ?? 0) > 0 || u.rampWalk || !inShipWay(this.state, t, u.planetIndex, u.surfaceDir)) continue;
        if (!boardingOpen(this.state, t)) break;
        this.boardShip(u.id);
      }
    }
    // 69.85: the parked ship's ramp down only while its boarding is open or riders wait aboard (core/dropship.ts padRampDown,
    // syncShipContainers) — re-laid when they differ (the sync otherwise runs only while a ship flies)
    const ramps = this.state.structures.filter(s => s.type === 'dropContainer' && s.ship && 'team' in s.ship);
    if (ramps.some(c => !!c.rampOpen !== padRampDown(this.state, (c.ship as { team: number }).team))) this.state = syncShipContainers(this.state, this.actionTick);
  }

  /** 69.3b, the ship's crash (DECISIONS 223): the crashes whose impact tick is reached come down (core/shipCrash.ts landCrash) — each piece's impact through the crater door, the wrecks, the load, the riders — out as events. */
  private stepShipCrashes(): ActionEvent[] {
    const out: ActionEvent[] = [];
    for (const c of crashesDue(this.state, this.actionTick)) {
      const r = landCrash(this.state, c, this.actionTick, { createCraterImpl: this.enterKernel() ? this.wasmCrater : undefined, personScale: personSetOf(this.state).k });
      this.state = reconcileSeats(r.state);
      out.push(...crewLostEvents(r.state, this.state, () => undefined, r.crashed.team));   // 69.89: a hull the ship came down on, its crew with it
      // (69.98 (4): the fog's colour memory refreshed once for the crash's pieces, not once a piece — the same colours)
      const fogLater: FogLater = new Map();
      for (const im of r.impacts) this.applyImpactWorld(im, fogLater);
      this.refreshFogLater(fogLater);
      this.state = noteCrew(this.state, r.crashed.team, r.crashed.survived, this.actionTick);   // 69.62: a crew to rescue
      const craft = c.craft === 'heli' ? 'heli' as const : c.payload === 'gunship' ? 'gunship' as const : undefined;   // (a helicopter's fire support carries payload 'gunship' too: the craft first)
      out.push({ kind: 'shipCrashed', team: r.crashed.team, id: r.crashed.id, planetIndex: r.crashed.planetIndex, at: r.crashed.at, pieces: r.crashed.pieces, wreckIds: r.crashed.wreckIds, survived: r.crashed.survived, lost: r.crashed.lost, brokeUp: r.crashed.brokeUp , impacts: r.impacts, ...(craft ? { craft } : {}) });
    }
    if (this.state.dropships?.crashed?.length) { const { crashed: _c, ...rest } = this.state.dropships; this.state = { ...this.state, dropships: rest }; }
    return out;
  }

  /** 69.3b: the dropships' tick (core/dropship.ts stepDropships), with the Match's half — the container set down, the riders' respawns. */
  private stepDropships(): ActionEvent[] {
    const out: ActionEvent[] = [];
    const d = stepDropships(this.state, this.actionTick);
    this.state = d.state;
    for (const f of d.announced) out.push({ kind: 'dropshipCalled', team: f.team, id: f.id, planetIndex: f.planetIndex, land: f.land, landTick: f.landTick, riders: f.riders.length, touchTick: f.payload === 'vehicle' ? f.exitTick : f.landTick, ...(f.sortie ? { sortie: true as const } : {}), ...(f.hot ? { hot: true as const } : {}), ...(f.gunship ? { gunship: true as const } : {}) });
    for (const f of d.landed) {
      let containerId: number | undefined;
      if (DROPSHIP.containerStays && !f.sortie && f.craft !== 'heli') {   // (200 step 4: a sortie carries its container away; 69.59: a helicopter has none — its riders step out of its cabin)
        containerId = this.state.structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
        const c = { ...containerAt(this.state, f, containerId), ownerId: f.riders[0] };
        // the side's containers over the cap: its oldest taken away (accumulation; the owner: "need some plan to get rid of them")
        const mine = this.state.structures.filter(s => s.type === 'dropContainer' && structureTeam(this.state, s) === f.team).map(s => s.id).sort((a, b) => a - b);
        const drop = new Set(mine.slice(0, Math.max(0, mine.length + 1 - DROPSHIP.containerCap)));
        this.state = { ...this.state, structures: [...this.state.structures.filter(s => !drop.has(s.id)), c],
          dropships: { ...this.state.dropships!, flights: this.state.dropships!.flights.map(x => (x.id === f.id ? { ...x, containerId } : x)) } };
      }
      out.push({ kind: 'dropshipLanded', team: f.team, id: f.id, planetIndex: f.planetIndex, land: f.land, ...(containerId !== undefined ? { containerId } : {}) });
    }
    const came: number[] = [];
    for (const e of d.exits) {
      const p = this.state.players.find(q => q.id === e.unitId);
      if (p?.alive && p.boarded) {
        // 208: a wave ship's rider, alive since it boarded at the pad — out of its seat and down the ramp (its ticket spent then)
        const spot = riderWalk(this.state, e.flight, e.index);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id ? { ...q, boarded: undefined, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, rampWalk: spot.path, heading: facingObjective(this.state, p.teamIndex, spot) } : q) };
        if (e.flight.sortie && e.flight.sortie.at >= 0) this.state = noteSpawnArrivals(this.state, [{ unitId: p.id, at: e.flight.sortie.at }], this.actionTick);
        continue;
      }
      if (!p || p.alive || hqFallBites(this.state, p.teamIndex)) continue;   // (69.5: its HQ fallen, no arrivals)
      const before = this.state;
      this.state = executeRespawn(this.state, p.id);
      if (this.state === before) continue;   // no ticket left: it stays down
      // (69.3b's follow-up, the owner: "shift to the actual loaded container and have them walk out": in its seat, down the ramp)
      const spot = riderWalk(this.state, e.flight, e.index);
      this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
        ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, rampWalk: spot.path, heading: facingObjective(this.state, p.teamIndex, spot), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };   // out on their feet, the rifle full
      out.push({ kind: 'respawned', unitId: p.id, at: 'dropship' });
      if (e.flight.sortie) came.push(p.id);
      else this.state = joinDropSquad(this.state, `ship:${e.flight.id}`, e.flight.team, p.id, { planetIndex: e.flight.planetIndex, surfaceDir: e.flight.land }, e.flight.by, e.flight.order);   // (200 step 5: one drop squad of their own, holding where they landed)
      if (e.flight.sortie && e.flight.sortie.at >= 0) this.state = noteSpawnArrivals(this.state, [{ unitId: p.id, at: e.flight.sortie.at }], this.actionTick);   // (200 step 4: watched for the hold-back, as a wave's arrival there)
    }
    this.kitArrivals(out);   // (their kit, as a wave's)
    // 68.10: they find their squads as any arrival does
    if (came.length && this.state.config.fluidSquads && this.state.squads?.length) {
      const r = squadsOnRespawn(this.state, came, this.state.config.fluidSquads);
      this.state = r.state;
      out.push(...r.events);
    }
    // step 2: a vehicle's wheels on the ground — made there, empty, its side's (owned by its side's first unit, as a parked vehicle
    // is someone's); with 63.1n's stock on its home is its side's base, so its replacement comes there when it is lost
    for (const f of d.touched) {
      const owner = this.state.players.filter(p => p.teamIndex === f.team).sort((x, y) => x.id - y.id)[0];
      if (!owner || !f.vehicle) continue;
      const id = this.state.structures.reduce((m, s) => Math.max(m, s.id), -1) + 1;
      const v = vehicleAt(this.state, f, id, owner.id), home = this.state.vehicleStocks ? airstrikeHome(this.state, f.team) : null;
      const made = home ? { ...v, home: { surfaceDir: home.surfaceDir, heading: v.drive!.heading } } : v;
      this.state = { ...this.state, structures: [...this.state.structures, made],
        dropships: { ...this.state.dropships!, flights: this.state.dropships!.flights.map(x => (x.id === f.id ? { ...x, vehicleId: id } : x)) } };
      out.push({ kind: 'dropshipDelivered', team: f.team, id: f.id, structureId: id, type: f.vehicle });
    }
    for (const f of d.gone) {
      if (f.pickup || f.craft === 'heli') this.state = pickupHome(this.state, f);   // (69.18: a pickup back on its pad: those it fetched come out; 69.74 step 1: a helicopter's riders still aboard, where it ends)
      if (f.padSpot !== undefined) this.state = padReturn(this.state, f, this.actionTick);   // (69.74 step 2: back on its spot on the helipad, turning round)
      out.push({ kind: 'dropshipGone', team: f.team, id: f.id });
    }
    this.state = syncShipContainers(this.state, this.actionTick);   // 69.3b's follow-up: the ships' containers as obstacles while they stand, the set-down one's ramp open while its ship is at it
    return out;
  }

  /**
   * Every unit that came back (`respawned` in `out`) gets its kit as a wave's arrival does — its grenades and rockets from its
   * side's stock, the player's own loadout, its armour whole, its 40 mm rounds; their events pushed onto `out`. (69.3b: out of
   * respawnWave's tail, so a dropship's riders get theirs too.)
   */
  private kitArrivals(out: ActionEvent[]): void {
    // 65.23: every unit that came back is topped up to its grenade issue from its side's stock, in id order
    if (this.state.config.grenadeStock) {
      for (const e of [...out]) {
        if (e.kind !== 'respawned') continue;
        const r = topUpGrenades(this.state, e.unitId, 'respawn');
        if (r.event) { this.state = r.state; out.push(r.event); }
      }
    }
    // 65.29 (2): and draws its rockets (the tube's round, then the spare) from its side's stock, in id order
    if (this.state.rocketStocks) {
      for (const e of [...out]) {
        if (e.kind !== 'respawned') continue;
        const r = issueRockets(this.state, e.unitId);
        this.state = r.state;
        if (r.event) out.push(r.event);
      }
    }
    // 65.32: the player's own loadout comes back with its unit (its rockets past the first); 65.31: the 40 mm's rounds
    for (const e of out) {
      if (e.kind !== 'respawned') continue;
      const q = this.state.players.find(x => x.id === e.unitId);
      if (q?.carry) this.state = { ...this.state, players: this.state.players.map(x => (x.id === q.id ? applyCarry(x, q.carry!) : x)) };
      // 185: its armour whole again (the draw-down from kit is later)
      if (q?.armour) { const whole = armourOf(issueOf(q.armour)); this.state = { ...this.state, players: this.state.players.map(x => (x.id === q.id ? { ...x, armour: whole } : x)) }; }
      // 65.31: and the 40 mm's rounds a life — from the side's stock where there is one, else whole
      if (q && carriesGl40(q)) { const r = respawnGl40(this.state, q.id); this.state = r.state; if (r.event) out.push(r.event); }
    }
  }

  private respawnWave(only?: ReadonlySet<number>): ActionEvent[] {
    const bi = battlePlanetIndex(this.state);
    if (bi < 0) return [];
    if (!this.state.actionHomes) this.state = { ...this.state, actionHomes: Object.fromEntries(teamAnchors(this.state, bi)) };   // (set on the first tick; kept as a fallback)
    const out: ActionEvent[] = [];
    const k = new Map<number, number>();
    const dropped = new Set<number>();   // 69.3a: the relays this wave landed at (each spends one drop)
    const aboard = this.state.dropships?.flights.length || this.state.sorties?.length || this.state.pods?.drops.length ? dropshipAboard(this.state) : null;   // (200 step 4: and those waiting for a sortie; 69.3b step 3's check: and a pod drop's, called and not yet down)
    // DECISIONS 200 step 3, the hold-back: an arrival watched lost soon after landing makes its point hot; each side's closed
    // points (contested by its picture, or hot) read once a wave, when a ranking needs them; the arrivals at spawn points logged
    if (this.state.spawnLog || this.state.hotSpawns) this.state = stepHotSpawns(this.state, this.actionTick, respawnWaveRate(this.state).period);
    this.state = stepDropSquads(this.state);   // (200 step 5: a drop squad's fallen back to their squads, into the pool; an empty one dissolved)
    const closedSets = new Map<number, Set<number>>();
    const closedFor = (t: number): Set<number> => { let c = closedSets.get(t); if (!c) { c = closedSpawns(this.state, t, this.actionTick); closedSets.set(t, c); } return c; };
    const atPoints: Array<{ unitId: number; at: number }> = [];
    const crewed = new Map<number, number>();   // 69.35: the arrivals this wave put in a base vehicle's crew seats, by side
    const truckCrewed = new Set<number>();   // 69.42 (g): the sides whose truck's wheel this wave took an arrival
    for (const p of [...this.state.players].sort((a, b) => a.id - b.id)) {
      const respawn = this.state.modeState.respawn;
      if (p.alive || !respawn || !canRespawn(p.id, p.teamIndex, respawn)) continue;
      if (only && !only.has(p.teamIndex)) continue;
      if (this.state.reserveCalls?.[p.teamIndex]?.held) continue;   // 68.4: the commander holds the reserve back
      if (aboard?.has(p.id)) continue;   // 69.3b: aboard a dropship — it comes in at its ramp
      if (hqFallBites(this.state, p.teamIndex)) continue;   // 69.5 (decision 165): its HQ fallen, the side gets no arrivals
      const n = k.get(p.teamIndex) ?? 0;
      k.set(p.teamIndex, n + 1);
      // 69.42 (g) (DECISIONS 268, the owner's "1a"): the side's supply truck standing at home with nobody at its wheel — the
      // side's next dead bot comes back in its driver's seat, its standing crew (one a side a wave; before the base crew)
      const truckAt = this.state.controllers?.[p.id] === undefined && p.isBot && !this.state.reserveArrivals && !truckCrewed.has(p.teamIndex) ? truckCrewSeat(this.state, p.teamIndex, this.actionTick) : null;
      if (truckAt) {
        const v = this.state.structures.find(x => x.id === truckAt.structureId)!;
        this.state = executeRespawn(this.state, p.id);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
          ? { ...q, planetIndex: v.planetIndex, surfaceDir: { ...v.surfaceDir }, heading: facingObjective(this.state, p.teamIndex, v), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined } : q) };
        this.state = mountUnit(this.state, p.id, v.id, 0, truckAt.seat);
        truckCrewed.add(p.teamIndex);
        out.push({ kind: 'respawned', unitId: p.id, at: 'base' });
        continue;
      }
      // 69.35 (core/baseCrew.ts): a fire-support vehicle unpacked at the side's base with its driver's or gun's seat empty —
      // the side's next dead bots come back in those seats, BASE_CREW.perWave a side a wave (the unwrap with a pool only; before
      // the wave ships' base arrival, which lands it by its pad)
      const crewAt = this.state.controllers?.[p.id] === undefined && p.isBot && !this.state.reserveArrivals && (crewed.get(p.teamIndex) ?? 0) < BASE_CREW.perWave ? baseCrewSeat(this.state, p.teamIndex) : null;
      if (crewAt) {
        const v = this.state.structures.find(x => x.id === crewAt.structureId)!;
        this.state = executeRespawn(this.state, p.id);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
          ? { ...q, planetIndex: v.planetIndex, surfaceDir: { ...v.surfaceDir }, heading: facingObjective(this.state, p.teamIndex, v), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };
        this.state = mountUnit(this.state, p.id, v.id, 0, crewAt.seat);
        crewed.set(p.teamIndex, (crewed.get(p.teamIndex) ?? 0) + 1);
        out.push({ kind: 'respawned', unitId: p.id, at: 'base' });
        continue;
      }
      // DECISIONS 209 (5) (the owner: "Let's do respawn at base with ship as option"): with wave ships every arrival comes back
      // at its side's base, on foot by its parked ship (core/dropship.ts padArrivalCentre) — the ship its ride forward, by choice
      if (this.state.config.waveShips && !this.state.reserveArrivals && this.state.shipPads?.[p.teamIndex]) {
        const spot = beaconArrival(this.state, padArrivalCentre(this.state, p.teamIndex), n);
        this.state = executeRespawn(this.state, p.id);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
          ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: facingObjective(this.state, p.teamIndex, spot), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };   // back on their feet, the rifle full
        out.push({ kind: 'respawned', unitId: p.id, at: 'base' });
        continue;
      }
      // 69.6h: a unit a human seat holds comes back where the seat picked, while that place stands (else by the rule below)
      const seat = this.state.controllers?.[p.id];
      const picked = seat !== undefined ? pickedSpot(this.state, seat, p, n) : null;
      // DECISIONS 200 step 4 (A1, GameConfig.waveShips): an arrival bound for a forward point — a beacon, relay or FOB, or under
      // Operations the push's landing — waits for its side's sortie there (core/dropship.ts queueSortie); the base stays on foot.
      // (Not with X19's reserves, whose arrivals are logged by their region.)
      const ships = !!this.state.config.waveShips && !this.state.reserveArrivals;
      const pick = seat !== undefined ? this.state.respawnPicks?.[seat] : undefined;
      if (ships && picked && pick && pick !== 'base' && !shipWalks(this.state, p.teamIndex, picked.planetIndex, picked.surfaceDir)) {   // (207's follow-up: near its pad, on foot)
        const s = this.state.structures.find(x => x.id === pick.structureId)!;
        this.state = queueSortie(this.state, p.teamIndex, s.id, s.planetIndex, s.surfaceDir, p.id, this.actionTick);
        continue;
      }
      if (picked) {
        this.state = executeRespawn(this.state, p.id);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
          ? { ...q, planetIndex: picked.planetIndex, surfaceDir: picked.surfaceDir, heading: facingObjective(this.state, p.teamIndex, picked), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };   // back on their feet, the rifle full
        if (picked.relayId !== undefined) { dropped.add(picked.relayId); out.push({ kind: 'respawned', unitId: p.id, at: 'relay' }); }
        else out.push({ kind: 'respawned', unitId: p.id });
        continue;
      }
      // 69.4 step 5 (DECISIONS 166): a supply run wanted with no free bot near its truck — the side's first dead bot comes
      // back beside the truck to drive it (core/convoys.ts; on the ring's outer step, clear of the hull)
      const called = seat === undefined && p.isBot && this.state.convoys ? baseSpawnFor(this.state, p.teamIndex) : null;
      if (called) {
        const spot = beaconArrival(this.state, called.truck, 2 + 3 * n);
        this.state = executeRespawn(this.state, p.id);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
          ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: facingObjective(this.state, p.teamIndex, spot), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };
        this.state = noteBaseSpawn(this.state, p.teamIndex, p.id, this.actionTick);
        out.push({ kind: 'respawned', unitId: p.id, at: 'base' });
        continue;
      }
      // DECISIONS 200 step 2: one ranking of the side's open spawn points — its standing beacon (125a), under Operations its
      // linked FOBs and its relays that can take a drop, without it those relays (69.3a), and where it lands anyway — by their
      // distance to where the arrival is headed; the kind only a tie-break (a beacon outranked the push's landing however far
      // behind it stood: the owner's quirk). core/drops.ts rankedSpawn
      const beacon = this.state.config.beacons ? standingBeacon(this.state, p.teamIndex, bi) : null;
      // 69.4 step 3 (decision 163(h), the owner: "for now we can say relay or FOB"): under Operations (a push or the tug of
      // war), every arrival — a respawn, an X19 arrival, a drop — comes down at the ranking's point, headed for the sector in
      // play next to its side's (opsTarget), else the push's landing itself
      const ops = this.state.modeState.points;
      if (ops?.front && ops.push && (!this.state.reserveArrivals || this.state.exits?.[p.teamIndex])) {
        const home = this.state.board?.bases.find(b => b.team === p.teamIndex);
        const landing = landingCentre(this.state, p.teamIndex, home ? home.dir : this.state.actionHomes?.[p.teamIndex], !!home);
        if (!landing) continue;
        const toward = opsTarget(this.state, p.teamIndex);
        const anchor = sideSpawn(this.state, p.teamIndex, toward ? toward.surfaceDir : landing, landing, beacon, true, closedFor(p.teamIndex));   // (200 step 3: by its side's order, with the hold-back)
        if (ships && !shipWalks(this.state, p.teamIndex, bi, anchor ? anchor.surfaceDir : landing)) { this.state = queueSortie(this.state, p.teamIndex, anchor ? anchor.id : -1, bi, anchor ? anchor.surfaceDir : landing, p.id, this.actionTick); continue; }
        const spot = anchor ? beaconArrival(this.state, anchor, n) : ringSpot(this.state, p.teamIndex, landing, n);
        if (!spot) continue;
        this.state = executeRespawn(this.state, p.id);
        if (this.state.reserveArrivals) {
          const placed = placeArrivalAt(this.state, p.id, this.actionTick, spot);
          this.state = { ...placed, players: placed.players.map(q => q.id === p.id ? { ...q, heading: facingObjective(placed, q.teamIndex, q), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reloadLeft: undefined, reloadTimer: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined } : q) };
        } else {
          this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
            ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: facingObjective(this.state, p.teamIndex, spot), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };
        }
        if (anchor?.type === 'relay') dropped.add(anchor.id);
        if (anchor) atPoints.push({ unitId: p.id, at: anchor.id });
        out.push({ kind: 'respawned', unitId: p.id, ...(anchor ? { at: anchor.type === 'fob' ? 'fob' as const : anchor.type === 'beacon' ? 'beacon' as const : 'relay' as const } : {}) });
        continue;
      }
      // without Operations: the beacon, a relay that can take a drop, or the base, by their distance to where the unit's squad
      // is sent (69.3a's aim); with nowhere it is sent, the beacon as before
      const base = this.state.board?.bases.find(b => b.team === p.teamIndex);
      const baseSpot = base ? respawnSpot(this.state, p.teamIndex, base.dir, n, true) : respawnSpot(this.state, p.teamIndex, this.state.actionHomes?.[p.teamIndex], n);   // 69.6a: on the Foothold board, the side's base
      const aim = arrivalAim(this.state, p.id, this.actionTick);
      const ranked = sideSpawn(this.state, p.teamIndex, aim ? aim.surfaceDir : null, baseSpot ? baseSpot.surfaceDir : null, beacon, false, closedFor(p.teamIndex));   // (200 step 3: by its side's order, with the hold-back)
      if (ships && ranked && !shipWalks(this.state, p.teamIndex, ranked.planetIndex, ranked.surfaceDir)) { this.state = queueSortie(this.state, p.teamIndex, ranked.id, ranked.planetIndex, ranked.surfaceDir, p.id, this.actionTick); continue; }
      if (ranked?.type === 'beacon') {
        // 125a: the side's wave lands around its standing beacon
        const spot = beaconArrival(this.state, ranked, n);
        this.state = executeRespawn(this.state, p.id);
        this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
          ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: facingObjective(this.state, p.teamIndex, spot), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };
        out.push({ kind: 'respawned', unitId: p.id, at: 'beacon' });
        atPoints.push({ unitId: p.id, at: ranked.id });
        continue;
      }
      // X19: with reserves on, the arrival comes in at the side's arrival region, not beside a point
      if (this.state.reserveArrivals) {
        if (!this.state.exits?.[p.teamIndex]) continue;
        const region = this.state.config.drops ? arrivalRegionFor(this.state, p.teamIndex) : undefined;   // 69.3a: a call may have named a relay
        this.state = executeRespawn(this.state, p.id);
        const placed = placeArrival(this.state, p.id, this.actionTick);
        if (placed) this.state = { ...placed, players: placed.players.map(q => q.id === p.id ? { ...q, heading: facingObjective(placed, q.teamIndex, q), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reloadLeft: undefined, reloadTimer: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined } : q) };
        if (placed && region?.relayId !== undefined) { dropped.add(region.relayId); out.push({ kind: 'respawned', unitId: p.id, at: 'relay' }); }
        else out.push({ kind: 'respawned', unitId: p.id });
        continue;
      }
      // 69.6a: the side's base; 69.3a: or the ranking's relay, nearer where its squad is sent (a drop)
      const relay = ranked?.type === 'relay' ? ranked : null;
      const spot = relay ? beaconArrival(this.state, relay, n) : baseSpot;
      if (!spot) continue;
      this.state = executeRespawn(this.state, p.id);
      this.state = { ...this.state, players: this.state.players.map(q => q.id === p.id
        ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: facingObjective(this.state, p.teamIndex, spot), aimPitch: 0, fireCooldown: 0, stance: undefined, weapon: undefined, magazines: undefined, reserves: undefined, reloadLeft: undefined, reloadTime: undefined, switchLeft: undefined, bandages: undefined, healLeft: undefined, healTime: undefined, healBy: undefined, treating: undefined, plantLeft: undefined, planting: undefined, healSaved: undefined, ...freshCase(q), ...freshCharges(q) } : q) };   // back on their feet, the rifle full
      if (relay) { dropped.add(relay.id); atPoints.push({ unitId: p.id, at: relay.id }); out.push({ kind: 'respawned', unitId: p.id, at: 'relay' }); }
      else out.push({ kind: 'respawned', unitId: p.id });
    }
    // 69.4 step 5 (DECISIONS 166, the owner: "so they don't have to leg the entire distance"): a driver home from its supply
    // run is sent forward without a ticket — to the side's arrival anchor under Operations (69.4 step 3), else its beacon;
    // with neither it stays at the base (core/convoys.ts)
    for (const r of redeploysDue(this.state)) {
      if (only && !only.has(r.team)) continue;
      const u = this.state.players.find(q => q.id === r.unitId);
      if (!u || !u.alive || u.mount) continue;   // (still in the cab: the next wave, once it is out)
      const fwd = this.forwardAnchor(r.team, bi);
      if (!fwd) { this.state = endRun(this.state, u.id, this.actionTick, 'stayed'); continue; }
      const n = k.get(r.team) ?? 0;
      k.set(r.team, n + 1);
      const spot = beaconArrival(this.state, fwd.s, n);
      this.state = { ...this.state, players: this.state.players.map(q => q.id === u.id ? { ...q, planetIndex: spot.planetIndex, surfaceDir: spot.surfaceDir, heading: facingObjective(this.state, q.teamIndex, spot), aimPitch: 0, stance: undefined } : q) };
      if (fwd.at === 'relay') dropped.add(fwd.s.id);
      this.state = endRun(this.state, u.id, this.actionTick, 'redeployed', fwd.at);
      out.push({ kind: 'respawned', unitId: u.id, at: fwd.at, redeployed: true });
    }
    if (dropped.size) this.state = { ...this.state, structures: spendDrops(this.state.structures, dropped) };   // 69.3a: one drop a relay a wave
    this.state = noteSpawnArrivals(this.state, atPoints, this.actionTick);   // (200 step 3: watched for the hold-back)
    this.kitArrivals(out);
    return out;
  }

  /**
   * One PHYS_DT tick of the action clock: every living unit decides (from the same state),
   * then each turns, aims, walks (or jumps) and fires as its intent says; then every bullet flies. Airborne units step
   * after this (stepOnce), as in the turn game.
   */
  /** 63.11: a burst at `point` on the ground (within half a person of the fine ground there) — a craterlet's; not one in the air. */
  private onGround(planetIndex: number, point: Vec3): boolean {
    const planet = this.state.planets[planetIndex];
    if (!planet) return false;
    const ps = personSetOf(this.state), rel = vecSub(point, planet.position), d = vecLength(rel);
    return d > 0 && d - personSurfaceRadius(planet, vecScale(rel, 1 / d), ps) < 0.5 * PERSON_BODY_HEIGHT * ps.k;
  }

  private stepAction(p: SimPresenter): void {
    const shipsAtStart = this.state.dropships?.flights;   // (69.52 step 3: the hulls as the tick began, for the evasion's hits)
    const dt = PHYS_DT;
    const events: ActionEvent[] = [];
    // 63.11: the tick's craterlets — bursts on the ground and vehicles destroyed — land together at its end (core/craterlets.ts)
    const craters: CraterletCause[] = [];
    const cratering = this.state.config.smallCraters === true && personSetOf(this.state).fineAmplitude > 0;   // (off unless true: the owner, Oct 13)
    // (65.35 step 5: the vehicles up at the tick's start — a wreck bursts, whether or not craterlets are on)
    const vehiclesUp = this.state.structures.filter(s => s.health > 0 && (VEHICLE_KINDS as readonly string[]).includes(s.type)).map(s => s.id);
    const holdsAtStart = standingHolds(this.state);   // (69.61 step 3: the FOBs and HQs standing as the tick began, with the story on)
    if (this.state.modeState.respawn && !this.state.actionHomes) {
      const bi = battlePlanetIndex(this.state);
      this.state = { ...this.state, actionHomes: bi >= 0 ? Object.fromEntries(teamAnchors(this.state, bi)) : {} };
    }
    // Everyone decides from the same start-of-tick world, then everyone acts: deciding in turn
    // let later units see earlier ones' moves this tick (a measured side bias in mirror duels)
    // A held unit follows its holder's intents (64.10: who holds it is state); any other bot, its brain
    const held = this.state.controllers;
    // §5.16 step 1: the decide phase as a function (match/decide.ts) — each brain's staging comes back as its return, replayed
    // here in the players array's order, so the staged memory and inputs are as when the brains staged them on the Match
    const live = (unit: PlayerState) => unit.alive && !droneBusy(unit);
    const decided = decidePhase(this, this.actionDriver, unit => live(unit) && held?.[unit.id] === undefined && unit.isBot && !unit.boarded, unit => live(unit) && held?.[unit.id] !== undefined);   // (208: aboard the wave ship, no brain)
    for (const d of decided) for (const s of d.staged) {
      if (s.kind === 'brain') this.stageBrain(s.unitId, s.brain);
      else if (s.kind === 'input') this.stageInput(s.unitId, s.body);
      else this.stageTrackEdit(s.team, s.id, s.entry);
    }
    // 69.3c: an arrival still in its pod does nothing till it lands (its intent idle, its own or a human's); the fall counts down
    const landing = (unit: PlayerState) => (unit.landingLeft ?? 0) > 0;
    if (this.state.players.some(landing)) this.state = { ...this.state, players: this.state.players.map(p => !landing(p) ? p : { ...p, landingLeft: p.landingLeft! - PHYS_DT > 1e-9 ? p.landingLeft! - PHYS_DT : undefined }) };
    // 69.3b's follow-up: a dropship's rider walks out of the bay down the ramp, scripted, doing nothing else till it is out
    const onRamp = (unit: PlayerState) => unit.rampWalk !== undefined;
    if (this.state.players.some(onRamp)) this.state = stepRampWalks(this.state);
    // DECISIONS 208: the boarded sit where their ship is (its bay on the pad, or under it in the air), doing nothing
    const aboardShip = (unit: PlayerState) => !!unit.boarded;
    if (this.state.players.some(aboardShip)) this.state = pinBoarded(this.state, this.actionTick);
    // 69.20 (DECISIONS 237): the seat key, aboard its side's wave ship still on its pad, steps a held unit out down the ramp
    // (E enters, E leaves); bots stay aboard (the owner: "can keep bots from exiting as it may cause problems"); lifted, nothing
    if (held && this.state.players.some(u => u.boarded && held[u.id] !== undefined)) {
      for (const u of this.state.players) {
        const s = u.boarded && held[u.id] !== undefined ? this.state.intents?.[u.id]?.seat : undefined;
        if (s === 'enter' || s === 'leave') this.state = leavePadShip(this.state, u.id);
      }
    }
    const intents = this.state.players.map((unit, i) => !live(unit) || landing(unit) || onRamp(unit) || aboardShip(unit) ? IDLE_INTENT : (decided[i].ran ? decided[i].intent : this.state.intents?.[unit.id]) ?? IDLE_INTENT);
    // 69.17 step 3: a pilot's own controls reach its ship (core/piloted.ts); to everything else it is aboard and still
    const pilots = this.state.players.some(u => u.piloting !== undefined) ? this.state.players.map(u => (u.piloting !== undefined && u.alive ? this.state.intents?.[u.id] : undefined)) : null;
    this.commitBotMemory();
    // An intent's turn and jump are impulses: they act on the tick they land, and the intent holds without them —
    // so a frame that runs many ticks turns once and hops once (64.10)
    if (this.state.intents) {
      let intents: Record<number, UnitIntent> | null = null;
      for (const [id, it] of Object.entries(this.state.intents)) {
        if (it.turn === 0 && !it.jump && !it.reload && !it.seat && !it.heal && !it.resupply && !it.pilot && !it.caseDrop) continue;
        const { reload: _reload, seat: _seat, seatPick: _seatPick, heal: _heal, healTarget: _healTarget, resupply: _resupply, pilot: _pilot, caseDrop: _caseDrop, ...rest } = it;   // (69.45: the support's case set down or picked up)   // (69.17 step 3: taking and leaving a pilot's seat too)   // a reload (Phase 65), getting in or out (64.11; 63.1d's follow-up: its pick) and a bandage (65.7) are impulses too
        (intents ??= { ...this.state.intents })[Number(id)] = { ...rest, turn: 0, jump: false };
      }
      if (intents) this.state = { ...this.state, intents };
    }
    // E5.4 steps 4–5: the planet forest's cells round the units and under the low shots join, the long-gone leave — from
    // the start-of-tick state, before anyone acts (core/forestHeld.ts; only with GameConfig.planetForest)
    if (this.state.forest) this.state = stepForestHeld(this.state, this.actionTick);
    // DECISIONS 209 (5): the seat key at a parked wave ship's ramp boards it (else the seats' rule below)
    if (this.state.shipPads && intents.some(it => it.seat === 'enter')) {
      const reach = SHIP_BOARD_REACH_PEOPLE * PERSON_BODY_HEIGHT * personSetOf(this.state).k;
      this.state.players.forEach((u, i) => {
        if (intents[i].seat !== 'enter' || !u.alive || u.mount || u.boarded || !this.state.shipPads?.[u.teamIndex] || !boardingOpen(this.state, u.teamIndex)) return;
        const foot = padRampFoot(this.state, u.teamIndex);
        if (foot.planetIndex !== u.planetIndex || dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(foot.surfaceDir), vecNormalize(u.surfaceDir))))) * this.state.planets[u.planetIndex].radius > reach) return;
        this.boardShip(u.id);
        intents[i] = { ...intents[i], seat: undefined };
      });
    }
    // 69.17 step 3: a pilot's seat taken (a ship in reach: a parked wave ship of any side, a flight standing landed)
    if (intents.some(it => it.pilot === 'take')) {
      const takers = this.state.players.filter((u, i) => intents[i].pilot === 'take').map(u => u.id);
      for (const id of takers) { const r = takeShip(this.state, id, this.actionTick); this.state = r.state; if (r.event) events.push(r.event); }
    }
    // 69.23: a bot squad leader's call for its side's mortar (its intent's; a person's comes as the radial's input) — refused quietly
    if (intents.some(it => it.call)) {
      const callers = this.state.players.filter((u, i) => intents[i].call?.kind === 'mortar' && u.alive && held?.[u.id] === undefined);
      for (const u of callers) { const r = callMortar(this.state, u.teamIndex, intents[this.state.players.indexOf(u)].call!, this.actionTick); if (!('refused' in r)) this.state = r.state; }
    }
    // 64.11: seats first — out, then in (core/seats.ts stepSeats); a seated person lays and fires the gun below
    if (intents.some(it => it.seat)) {
      const seated = stepSeats(this.state, intents.map(it => it.seat), personSetOf(this.state).seatReach, intents.map(it => it.seatPick), id => held?.[id] !== undefined);
      // 63.1k: a bot whose seat a person took leaves that gun be for lane B's shun (63.1i: it would walk straight back)
      for (const e of seated.events) {
        if (e.kind !== 'seatTaken') continue;
        // (§5.16: the one write into a brain from outside it — with the brains on their workers (step 4), a patch to the owner)
        const brain = seated.state.actionBrains?.[e.fromUnitId];
        if (brain) seated.state = { ...seated.state, actionBrains: { ...seated.state.actionBrains, [e.fromUnitId]: { ...brain, gunShun: { id: e.structureId, until: this.actionTick + Math.round(GUN_SHUN_SECONDS / PHYS_DT) } } } };
      }
      this.state = seated.state;
      events.push(...seated.events);
    }
    // 65.9: restocks from the team's crate in reach (core/supply.ts), after the seats so a person who just sat down takes nothing
    if (intents.some(it => it.resupply)) {
      const stocked = stepSupplies(this.state, intents.map(it => it.resupply), personSetOf(this.state));
      if (stocked) { this.state = { ...this.state, players: stocked.players, structures: stocked.structures }; events.push(...stocked.events); }
      // 65.23: and grenades back up to the issue from the side's stock (core/grenadeStock.ts)
      if (this.state.config.grenadeStock) {
        const restocked = stepGrenadeRestock(this.state, intents.map(it => it.resupply));
        if (restocked) { this.state = restocked.state; events.push(...restocked.events); }
      }
      // 65.29 (2): and the rocket's spare from the side's stock (core/rocketStock.ts)
      const rockets = stepRocketRestock(this.state, intents.map(it => it.resupply));
      if (rockets) { this.state = rockets.state; events.push(...rockets.events); }
      // 65.31 step 2: and the 40 mm's spares from the side's stock (core/gl40Stock.ts)
      const gl40 = stepGl40Restock(this.state, intents.map(it => it.resupply));
      if (gl40) { this.state = gl40.state; events.push(...gl40.events); }
      // 69.4 step 6a: and the squad's relays and its carrier's beacon from the side's kits (core/buildKits.ts)
      const kits = stepKitRestock(this.state, intents.map(it => it.resupply));
      if (kits) { this.state = kits.state; events.push(...kits.events); }
    }
    // 69.45: the support's case — refilled at a dump in reach (with the restocks' asks), set down or picked up, one magazine
    // handed to the teammate in reach shortest of them on its beat, and the cases emptied or broken gone (core/supportRole.ts)
    if (supportOn(this.state.config)) {
      if (intents.some(it => it.resupply)) {
        const filled = stepCaseRefills(this.state, intents.map(it => it.resupply));
        if (filled) { this.state = { ...this.state, players: filled.players, structures: filled.structures }; events.push(...filled.events); }
      }
      if (intents.some(it => it.caseDrop)) {
        const cases = stepCases(this.state, intents.map(it => it.caseDrop));
        if (cases) { this.state = { ...this.state, players: cases.players, structures: cases.structures }; events.push(...cases.events); }
      }
      if (this.actionTick > 0 && this.actionTick % Math.round(SUPPORT.handEveryS / PHYS_DT) === 0) {
        const handed = stepHandOuts(this.state, personSetOf(this.state));
        if (handed) { this.state = { ...this.state, players: handed.players }; events.push(...handed.events); }
      }
      const cleared = clearCases(this.state);
      if (cleared) this.state = { ...this.state, players: cleared.players, structures: cleared.structures };
    }
    // X31 (R12 step 5): the bikes drive — steered and throttled by their riders, coasting without; a crash throws the rider
    let engines: Array<{ unitId: number; pos: Vec3; loud: number }> = [];
    // (62.60, the look's cost: whether any hull drives, asked once a tick — it was asked twice a unit, a scan of the structures each)
    const anyDrives = this.state.structures.some(drives);
    if (anyDrives) {
      const driven = stepDrive(this.state, intents, dt, this.actionTick);
      if (driven) { this.state = driven.state; events.push(...driven.events); engines = driven.heard; }   // (63.19: a run-down enemy's `struck` names its killer, credited in core/vehicleImpact.ts)
    }
    // 69.17 step 3: the piloted ships fly a tick on their pilots' controls (core/piloted.ts; nothing when none is flown)
    if (pilots || this.state.players.some(u => u.piloting !== undefined)) {
      const flown = stepPiloted(this.state, pilots ?? [], this.actionTick);
      this.state = flown.state;
      events.push(...flown.events);
    }
    // 65.10: the point crates refill while their points are held, on the action clock's interval
    if (!this.state.config.droneDrops && this.actionTick > 0 && this.actionTick % Math.round(SUPPLY.point.refillS / PHYS_DT) === 0) {   // (69.32: with the drones, refilled by drops)
      const refilled = refillPointCrates(this.state);
      if (refilled) this.state = { ...this.state, structures: refilled };
    }
    // 69.32: the supply drones — a load down sets its point's crate, a point held with none (or dry) sends one (core/supplyDrone.ts)
    if (this.state.supplyDrones) {
      // (step 3: the drones brought down since the last tick — by the bullets' or the mounts' step — drained to events first)
      const downed = this.state.supplyDrones.downed;
      if (downed?.length) {
        for (const x of downed) events.push({ kind: 'droneDown', team: x.team, id: x.id, planetIndex: x.planetIndex, at: x.at, carrying: x.carrying });
        const { downed: _d, ...rest } = this.state.supplyDrones;
        this.state = { ...this.state, supplyDrones: rest };
      }
      const r = stepSupplyDrones(this.state, this.actionTick);
      this.state = r.state;
      for (const run of r.launched) events.push({ kind: 'droneLaunched', team: run.team, id: run.id, ...(run.site !== undefined ? { site: run.site } : {}) });
      for (const x of r.drops) events.push({ kind: 'droneDropped', team: x.run.team, id: x.run.id, structureId: x.structureId, ...(x.run.site !== undefined ? { site: x.run.site } : {}) });
    }
    // 69.4: the linked forward bases' crates refill from the base, on their own interval (core/fob.ts)
    if (this.state.config.fobs && this.actionTick > 0 && this.actionTick % Math.round(FOB.refillS / PHYS_DT) === 0) {
      const refilled = refillFobCrates(this.state);
      if (refilled) this.state = { ...this.state, structures: refilled };
    }
    // 69.4 step 5: the convoys (core/convoys.ts) — the base stocks, the trucks' cargo (loaded at home, handed to the side's
    // FOBs in reach, lost with a wreck), the runs' log and the calls for a driver at the base
    if (convoysOn(this.state.config) && isDue(RATES.convoys, this.actionTick)) {
      const c = stepConvoys(this.state, this.actionTick);
      if (c) { this.state = c.state; events.push(...c.events); }
    }
    // 69.30 step 1: the parked tanks rearm at the dumps in reach (core/munitions.ts), on their beat
    if (this.state.config.munitionsSupply && isDue(RATES.rearm, this.actionTick)) {
      const beds = loadBeds(this.state);   // (step 5: the supply trucks at home load their beds first)
      if (beds) { this.state = { ...this.state, structures: beds.structures }; events.push(...beds.events); }
      const r = stepRearm(this.state, RATES.rearm.period * PHYS_DT);
      if (r) { this.state = { ...this.state, structures: r.structures }; events.push(...r.events); }
    }
    const gunFire: Array<{ i: number; params: FireParams; structureId: number; brain?: boolean }> = [];
    // One copy of the players array per tick, written in place as each unit acts (a unit's act never reads
    // another's this tick: decisions came first, and walking, jumping and firing read only the unit, the props,
    // the planets and the bullets), assigned once at the end — was a copy of the array per unit (62.20)
    // 65.8 (the medic): treatments under way run and new ones start here, before the units act — a bandage on a
    // teammate in reach is a state on both (the patient's timer, the medic's `treating`); the medic spends their own
    if (this.state.config.medical !== false) {
      const treated = stepTreatments(this.state, intents, dt);
      if (treated) { this.state = { ...this.state, players: treated.players }; events.push(...treated.events); }
    }
    // 125a / 69.2b: the build path — plantings under way (a beacon, a relay) run or stop, ordered planters in reach start (core/build.ts)
    if (this.state.config.beacons || this.state.config.relays) {
      const planted = stepBuilds(this.state, intents, this.actionTick, dt);
      if (planted) { this.state = { ...this.state, players: planted.players, structures: planted.structures, squads: planted.squads, ...(planted.fobsLeft ? { fobsLeft: planted.fobsLeft } : {}), ...(planted.convoys ? { convoys: planted.convoys } : {}), ...(planted.buildKits ? { buildKits: planted.buildKits } : {}) }; events.push(...planted.events); }   // (69.4 step 6b: `convoys`, the base's material paid)   // (6e: `buildKits`, an HMG's kit spent)
      if (planted) this.state = restand(this.state);   // 209's footing follow-up: a build that stands (a plinth kind) lays its stand, as the setup laid the others
    }
    const start = this.state.players;
    const players = start.slice();
    let heard: Record<number, { pos: Vec3; tick: number; loud?: number; step?: boolean }> = (this.state.heard ?? EMPTY_HEARD) as Record<number, { pos: Vec3; tick: number; loud?: number; step?: boolean }>;
    const hear = () => { if (heard === (this.state.heard ?? EMPTY_HEARD)) heard = { ...heard }; };
    for (const e of engines) { hear(); heard[e.unitId] = { pos: e.pos, tick: this.actionTick, loud: e.loud }; }   // X31: an engine is heard as a shot is, at its own range
    for (let i = 0; i < start.length; i++) {
      const unit = start[i];
      if (!unit.alive) continue;
      const intent = intents[i];
      const n = vecNormalize(unit.surfaceDir);
      const aimPitch = Math.max(-1.4, Math.min(1.4, intent.aimPitch));
      let u: PlayerState = { ...unit, aimPitch, fireCooldown: Math.max(0, (unit.fireCooldown ?? 0) - dt) };
      // 65.25a cut 2: the aim across (the player's assist), clamped; kept only while an intent carries it (a bot never does)
      const aimYaw = intent.aimYaw ? Math.max(-AIM_YAW_MAX, Math.min(AIM_YAW_MAX, intent.aimYaw)) : undefined;
      if (aimYaw !== undefined) u = { ...u, aimYaw };
      else if (u.aimYaw !== undefined) { const { aimYaw: _gone, ...rest } = u; u = rest; }
      // 62.2: crouch or go prone (or stand up) on the ground, unless the match has stances off — 65.22: not while sitting in
      // a sat seat (the HMG's gunner, the gun pit's gun-layer: it sits as it sits)
      if (intent.stance && !unit.airborne && this.state.config.stances !== false && !(unit.mount && this.state.structures.some(s => s.id === unit.mount!.bodyId && isSatSeat(s.type, unit.mount!.seat)))) {
        const want = intent.stance === 'stand' ? undefined : intent.stance;
        if (want !== unit.stance) {
          u = { ...u, stance: want };
          // 216 (GameConfig.realTiming): the change runs over its drawn time — from the shape the body has now, the eye from
          // where it is now (core/stance.ts eyeScaleOf / bodyStanceOf)
          if (defaultOn(this.state.config, 'realTiming')) {
            // (turned back before the half, the body still the stance it is going back to: back over the time spent)
            const from = bodyStanceOf(unit) ?? 'stand', to = want ?? 'stand';
            const total = from === to ? (unit.stanceShift ? unit.stanceShift.total - unit.stanceShift.left : 0) : stanceChangeSeconds(from, to);
            if (total > 0) u = { ...u, stanceShift: { from, fromEye: eyeScaleOf(unit), left: total, total } };
            else if (u.stanceShift) { const { stanceShift: _gone, ...rest } = u; u = rest; }
          }
        }
      }
      // 216: a stance change runs down (with real timing; nothing else ever sets it)
      if (u.stanceShift) {
        const left = u.stanceShift.left - dt;
        if (left > 0) u = { ...u, stanceShift: { ...u.stanceShift, left } };
        else { const { stanceShift: _done, ...rest } = u; u = rest; }
      }
      // 66.7: the cover it holds (CoverHold), kept while it stands still on the ground — a step, a seat or the air ends it;
      // set only when it changes, so a unit that never holds cover keeps no field (the checksum and every stored case as were)
      const coverNow = intent.cover && !unit.airborne && !unit.mount && Math.abs(intent.forward) < 1e-3 && Math.abs(intent.right) < 1e-3 ? intent.cover : undefined;
      if (coverNow ? !unit.cover || unit.cover.pose !== coverNow.pose || unit.cover.side !== coverNow.side || unit.cover.out !== coverNow.out : unit.cover !== undefined) {
        if (coverNow) u = { ...u, cover: { pose: coverNow.pose, ...(coverNow.side !== undefined ? { side: coverNow.side } : {}), out: coverNow.out } };
        else { const { cover: _gone, ...rest } = u; u = rest; }
      }
      const stance = u.stance ?? 'stand';
      // Phase 65: the weapon in hand — a switch (65.3) and a reload (65.2) are timed states with no firing
      const armed = this.state.config.groundWeapons !== false;
      if (armed) {
        if (u.switchLeft !== undefined) u = { ...u, switchLeft: u.switchLeft - dt > 0 ? u.switchLeft - dt : undefined };
        if (u.reloadLeft !== undefined) {
          if (u.reloadLeft - dt > 0) u = { ...u, reloadLeft: u.reloadLeft - dt };
          else {   // done: the magazine takes what it is missing from the spares (S7, 65.10: a limited supply gives what it has)
            u = { ...u, ...finishReload(u, weaponSpec(personSetOf(this.state), weaponOf(u), this.state.config)), reloadLeft: undefined, reloadTime: undefined };
          }
        }
        // S7: the rocket tube only with the match's switch on, and with a rocket left (loaded or spare)
        // 65.13 / 65.14: a long gun only if it is the person's own primary (the kit's); the pistol always; 65.32: or in the player's own loadout
        const drawable = (k: GroundWeaponKind) => k === sidearmOf(u) || k === primaryOf(u) || (k !== 'rocket' && k !== 'gl40' && k !== 'panzerfaust' && u.carry?.includes(k) === true) || (k === 'panzerfaust' && carriesPanzerfaust(u) && roundsIn(u, 'panzerfaust', weaponSpec(personSetOf(this.state), 'panzerfaust')) > 0) || (k === 'gl40' && carriesGl40(u) && (() => {   // (65.43: the sidearm, the M1911 in the pistol's place; the Panzerfaust while loaded)
          const gs = weaponSpec(personSetOf(this.state), 'gl40', this.state.config);
          return roundsIn(u, 'gl40', gs) > 0 || reserveLeft(u, 'gl40', gs) > 0;
        })()) || (k === 'rocket' && carriesRocket(u, this.state.config) && (() => {
          const rs = weaponSpec(personSetOf(this.state), 'rocket');
          return roundsIn(u, 'rocket', rs) > 0 || reserveLeft(u, 'rocket', rs) > 0;
        })()) || (k === 'missile' && carriesMissile(u) && (() => {   // 65.49 step 2: the anti-dropship missile, loaded or spare
          const ms = weaponSpec(personSetOf(this.state), 'missile');
          return roundsIn(u, 'missile', ms) > 0 || reserveLeft(u, 'missile', ms) > 0;
        })());
        if (intent.weapon && intent.weapon !== weaponOf(u) && u.switchLeft === undefined && u.healLeft === undefined && drawable(intent.weapon)) {
          u = { ...u, weapon: intent.weapon === primaryOf(u) ? undefined : intent.weapon, switchLeft: WEAPON_DRAW_SECONDS, reloadLeft: undefined, reloadTime: undefined };
        }
        const w = weaponSpec(personSetOf(this.state), weaponOf(u), this.state.config);
        const rounds = roundsLeft(u, w);
        // 67.44: a teammate's bandage leaves the hands free (reload under it); your own takes them
        // (65.43: the Garand's clip goes in only when it is empty — a reload asked for sooner is not taken)
        if (((intent.reload && (!w.reloadWhenEmpty || rounds <= 0)) || (intent.fire && rounds <= 0)) && rounds < w.magazine && u.reloadLeft === undefined && u.switchLeft === undefined && (u.healLeft === undefined || u.healBy !== undefined) && reserveLeft(u, weaponOf(u), w) > 0) {
          u = { ...u, reloadLeft: w.reload, reloadTime: w.reload };
        }
        // 65.49 step 2: the anti-dropship missile's seeker while it is up (the field absent otherwise: no other unit carries it)
        if ((u.missileLock || weaponOf(u) === 'missile') && !(u.mount && mountedSeat(u, this.state)?.seat.launcher === 'missile')) {   // (69.21: a missile mount's gunner's seeker is the mount's, below)
          const lock = stepMissileLock(this.state, u, dt, this.actionTick);
          if (lock) u = { ...u, missileLock: lock };
          else { const { missileLock: _l, ...rest } = u; u = rest as PlayerState; }
        }
      }
      // 65.7 / 65.11: a bandage (core/medical.ts) — a timed state holding the person still with the weapon down; a step or a jump breaks it off, the bandage unspent and its progress kept
      if (this.state.config.medical !== false) {
        if (u.healLeft !== undefined) {
          // (a teammate's bandage on this person counts down in the treatments pass; here only the patient's own shot or jump interrupts it)
          // 65.11: the weapon is down while your own goes on (a shot is refused, never an interruption); 67.44 (the owner): a
          // teammate's leaves you firing and reloading; stepping off or jumping breaks either off, its progress kept for the next
          if (intent.jump || intent.forward !== 0 || intent.right !== 0) { u = cancelBandage(u); events.push({ kind: 'bandageStopped', unitId: u.id }); }
          else if (u.healBy === undefined) { const before = u.health; u = stepBandage(u, dt); if (u.healLeft === undefined) events.push({ kind: 'bandaged', unitId: u.id, healed: u.health - before }); }
        } else if (intent.heal && intent.healTarget === undefined && canBandage(u)) u = beginBandage(u);
      }
      // bandaging yourself, bandaging a teammate or planting a beacon (125a): held still, no firing; 67.44: a teammate's bandage on
      // you holds neither (you fire and reload under it; a step breaks it off, above)
      const bandaging = (u.healLeft !== undefined && u.healBy === undefined) || u.treating !== undefined || u.plantLeft !== undefined;
      let heading = turnHeading(headingOf(unit), n, intent.turn);
      if (u.mount) {
        const seated = mountedSeat(u, this.state);
        // X31: a seat with no weapon (a driver's: the drive carried the rider this tick, stepDrive) — no gun to lay, no walking, jumping or sidearm
        if (seated && !seatArmed(seated.seat)) { players[i] = u; continue; }
        // 69.21 (DECISIONS 239): a mounted launcher — the rocket technical's twin LAW tubes or the missile technical's rail — laid
        // by the gunner's bearing and pitch from its hatch: the rocket fires the LAW's round (a bullet with its blast), the missile
        // launches the 65.49 round on what its seeker holds (unlocked, it flies straight, as from the shoulder); a dry mount
        // reloads from its store (R early): the rounds the tubes want, as many as the store has (counted in when it starts:
        // none fire till it ends); the store empty, dry
        if (seated?.seat.launcher) {
          const kind = seated.seat.launcher, L = MOUNT_LAUNCHERS[kind];
          let seat = seated.seat;
          const spare = seat.spare ?? this.state.config.mountStore ?? L.store;   // (the store's dial, GameConfig.mountStore)
          if (seat.reloadLeft === undefined && (seat.belt ?? L.magazine) < L.magazine && spare > 0 && ((seat.belt ?? L.magazine) <= 0 || intent.reload)) {
            const take = Math.min(L.magazine - (seat.belt ?? L.magazine), spare);
            seat = { ...seat, reloadLeft: L.reload, belt: (seat.belt ?? L.magazine) + take, spare: spare - take };
          }
          const loaded = seat.belt ?? L.magazine;
          const cool = Math.max(0, (seat.cooldown ?? 0) - dt);
          seat = cool > 0 ? { ...seat, cooldown: cool } : (seat.cooldown !== undefined ? (({ cooldown: _c, ...rest }) => rest)(seat) : seat);
          let gunner: PlayerState = { ...u, heading, facing: heading };
          if (kind === 'missile') gunner = { ...gunner, missileLock: stepMountLock(this.state, gunner, dt, this.actionTick) };
          players[i] = gunner;
          if (intent.fire && seat.reloadLeft === undefined && (seat.cooldown ?? 0) <= 0 && loaded > 0) {
            if (kind === 'rocket') {
              const spec = weaponSpec(personSetOf(this.state), 'rocket', this.state.config);
              const shot = fireBullet(this.state, gunner, 0, spec);
              this.state = shot.state;
              seat = { ...seat, cooldown: L.interval };   // (fix/69.21-second-tube: the mount's own, the tube beside it ready)
              events.push({ kind: 'fired', unitId: u.id, bullet: shot.bullet });
              hear(); heard[u.id] = { pos: shot.bullet.pos, tick: this.actionTick };
            } else {
              const shot = launchMissile(this.state, gunner);
              this.state = shot.state;
              seat = { ...seat, cooldown: L.interval };
              events.push({ kind: 'missileFired', unitId: u.id, missile: shot.missile });
              hear(); heard[u.id] = { pos: shot.missile.pos, tick: this.actionTick };
            }
            seat = { ...seat, belt: loaded - 1 };
          }
          if (seat !== seated.seat) {
            const sid = seated.structure.id, k = seated.index;
            this.state = { ...this.state, structures: this.state.structures.map(x => (x.id === sid ? { ...x, seats: x.seats!.map((y, j) => (j === k ? seat : y)) } : x)) };
          }
          continue;
        }
        // 69.29: a tank's turret — its gunner's bearing and pitch moved at the turret's rates (core/turret.ts), the turret's
        // yaw off the hull kept with the structure (its hit shape's turret part and the drawn turret turned by it)
        if (seated && isTurretSeat(seated.structure, seated.index) && this.state.planets[seated.structure.planetIndex]) {
          const ps = personSetOf(this.state);
          const lay = layTurret(seated.structure, this.state.planets[seated.structure.planetIndex], ps, PERSON_BODY_HEIGHT * ps.k, headingOf(unit), n, intent.turn, unit.aimPitch ?? 0, intent.aimPitch, dt, layRate(this.state, unit));   // (69.46: a crewman's at CREWMAN.lay)
          // 69.54 (DECISIONS 264 (1)): a held gunner's turn beyond the turret's rate this tick stays asked — the intent keeps
          // what is left (its turn otherwise an impulse, gone after a tick), so the turret slews on to where the player laid it
          // at its full rate, not half of it with the rest lost (a frame's input lands on one of its ticks)
          if (intent.turn !== 0 && this.state.controllers?.[u.id] !== undefined && this.state.intents?.[u.id]) {
            const from = headingOf(unit), turned = datan2(vecDot(vecCross(lay.heading, from), n), vecDot(from, lay.heading));
            const left = intent.turn > 0 ? Math.max(0, intent.turn - turned) : Math.min(0, intent.turn - turned);
            if (left !== this.state.intents[u.id].turn) this.state = { ...this.state, intents: { ...this.state.intents, [u.id]: { ...this.state.intents[u.id], turn: left } } };
          }
          heading = lay.heading;
          u = { ...u, aimPitch: lay.aimPitch };
          if (lay.turretYaw !== (seated.structure.turretYaw ?? 0)) {
            const sid = seated.structure.id;
            this.state = { ...this.state, structures: this.state.structures.map(x => (x.id === sid ? { ...x, turretYaw: lay.turretYaw } : x)) };
            seated.structure = this.state.structures.find(x => x.id === sid)!;
          }
        }
        // 69.29 (DECISIONS 245): the tank's main gun, beside its coax — the trigger fires the round in it (the coax on its own
        // trigger, `fire2`, in the guns branch below); a reload runs while its gunner sits there and loads the round asked for
        // (`round`), the other kind when that one is out, nothing once both are (dry until supply)
        let gunTrigger = intent.fire;
        if (seated?.seat.main) {
          gunTrigger = !!intent.fire2;
          let main = seated.seat.main;
          if (intent.round && intent.round !== main.next) main = { ...main, next: intent.round };
          // 69.51 (DECISIONS 260: "let tanks swap rounds"): a swap takes the round out of the breech, back to its store, and
          // loads the other kind in a reload's time — only with one in the breech, no reload under way and the other kind stowed
          if (intent.swap && main.loaded && main.reloadLeft === undefined) {
            const want: TankRound = intent.round && intent.round !== main.loaded ? intent.round : main.loaded === 'he' ? 'solid' : 'he';
            if (want !== main.loaded && main[want] > 0) main = { ...main, [main.loaded]: main[main.loaded] + 1, loaded: null, next: want, reloadLeft: mainGunReload(this.state, u) };
          }
          if (main.reloadLeft !== undefined) {
            const left = main.reloadLeft - dt;
            if (left > 0) main = { ...main, reloadLeft: left };
            else {
              const other: TankRound = main.next === 'he' ? 'solid' : 'he';
              const k: TankRound | null = main[main.next] > 0 ? main.next : main[other] > 0 ? other : null;
              const { reloadLeft: _done, ...rest } = main;
              main = k ? { ...rest, loaded: k, [k]: main[k] - 1 } : { ...rest, loaded: null };
            }
          } else if (main.loaded === null && (main.he > 0 || main.solid > 0)) main = { ...main, reloadLeft: mainGunReload(this.state, u) };
          if (intent.fire && main.loaded && main.reloadLeft === undefined) {
            // 69.55 (DECISIONS 264 (2)): out of the barrel's muzzle as drawn, not the gunner's eye above the turret
            const ps = personSetOf(this.state), from = tankMuzzle(seated.structure, this.state.planets[seated.structure.planetIndex], ps, PERSON_BODY_HEIGHT * ps.k, u.aimPitch ?? 0, 'main');
            const shot = fireBullet(this.state, { ...u, heading, facing: heading }, 0, tankRoundSpec(ps, main.loaded), 0, from);
            this.state = shot.state;
            events.push({ kind: 'fired', unitId: u.id, bullet: shot.bullet });
            hear(); heard[u.id] = { pos: shot.bullet.pos, tick: this.actionTick };
            main = { ...main, loaded: null, reloadLeft: mainGunReload(this.state, u) };
          }
          if (main !== seated.seat.main) {
            const sid = seated.structure.id, k = seated.index, seat = { ...seated.seat, main };
            this.state = { ...this.state, structures: this.state.structures.map(x => (x.id === sid ? { ...x, seats: x.seats!.map((y, j) => (j === k ? seat : y)) } : x)) };
            seated.seat = seat;
          }
        }
        // 63.1d step 3 (65.22): a mounted bullet gun — laid by the gunner's bearing and pitch, fired from where the gunner
        // stands (its eye, in its hatch) on the belt, a round every interval; a dry belt reloads (R reloads it early)
        if (seated?.seat.guns?.length) {
          let spec = mountedGunSpec(personSetOf(this.state), seated.seat.guns[0]);
          // decision 141: a worn mounted gun (the body's wear: its gun is no target) fires wider and slower
          const wear = gunWear(this.state, seated.structure);
          if (wear > 0) spec = { ...spec, spread: spec.spread * (1 + WORN_GUN.bulletSpread * wear), interval: spec.interval * (1 + WORN_GUN.slow * wear) };
          let seat = seated.seat;
          const cool = Math.max(0, (seat.cooldown ?? 0) - dt);
          seat = cool > 0 ? { ...seat, cooldown: cool } : (seat.cooldown !== undefined ? (({ cooldown: _c, ...rest }) => rest)(seat) : seat);
          // a reload runs down in the seats' pass below (one countdown, gunner or none; it ends with a full belt) — here R starts one early
          if (seat.reloadLeft === undefined && intent.reload && (seat.belt ?? spec.magazine) < spec.magazine) seat = { ...seat, reloadLeft: spec.reload };
          // 65.22: a sat seat's gunner swings with its gun (the HMG's) — re-placed behind it on its new bearing before it fires
          const sat = isSatSeat(seated.structure.type, seated.index) && this.state.planets[seated.structure.planetIndex]
            ? satSeatPos(seated.structure, seated.index, heading, this.state.planets[seated.structure.planetIndex], PERSON_BODY_HEIGHT * personSetOf(this.state).k, personSetOf(this.state)) : null;
          const gunner: PlayerState = { ...u, heading, facing: heading, ...(sat ? { seatPos: sat } : {}) };
          players[i] = gunner;
          if (gunTrigger && seat.reloadLeft === undefined && (seat.cooldown ?? 0) <= 0 && (seat.belt ?? spec.magazine) > 0) {
            // 69.55: a tank's coax out of its port beside the main gun (the turret seat's bullet gun), as drawn
            const tps = personSetOf(this.state), coax = isTurretSeat(seated.structure, seated.index) && this.state.planets[seated.structure.planetIndex]
              ? tankMuzzle(seated.structure, this.state.planets[seated.structure.planetIndex], tps, PERSON_BODY_HEIGHT * tps.k, gunner.aimPitch ?? 0, 'coax') : undefined;
            const shot = fireBullet(this.state, gunner, 0, spec, 0, coax);
            this.state = shot.state;
            const belt = (seat.belt ?? spec.magazine) - 1;
            seat = { ...seat, belt, cooldown: spec.interval, ...(belt <= 0 ? { reloadLeft: spec.reload } : {}) };
            events.push({ kind: 'fired', unitId: u.id, bullet: shot.bullet });
            hear(); heard[u.id] = { pos: shot.bullet.pos, tick: this.actionTick };
          }
          if (seat !== seated.seat) {
            const sid = seated.structure.id, k = seated.index;
            this.state = { ...this.state, structures: this.state.structures.map(x => (x.id === sid ? { ...x, seats: x.seats!.map((y, j) => (j === k ? seat : y)) } : x)) };
          }
          continue;
        }
        // 64.11: seated — the gun's bearing is the heading and its elevation the pitch; no walking, jumping or rifle
        // (63.1f, the owner in a gun pit: "I can't figure out how to turn the cannon left / right" — the pit's turret turns
        // with the bearing as drawn, its gun-layer swung with it about the turret's axis: SEAT_POINTS' pivot, as the HMG's)
        const pitSat = seated && isSatSeat(seated.structure.type, seated.index) && this.state.planets[seated.structure.planetIndex]
          ? satSeatPos(seated.structure, seated.index, heading, this.state.planets[seated.structure.planetIndex], PERSON_BODY_HEIGHT * personSetOf(this.state).k, personSetOf(this.state)) : null;
        players[i] = { ...u, heading, facing: heading, ...(pitSat ? { seatPos: pitSat } : {}) };
        const st = seated?.structure;
        // (the coordinator: a drawn shield that doesn't match the shape it protects is false cover) — the turret as its gunner
        // lays it: its parts (the shield) stand turned with it (core/hits.ts partFrame) and the drawn turret reads it
        if (st && TURRET_PARTS_FROM[st.type as BodyKey] !== undefined) {
          const { tx, tz } = uprightFrame(vecNormalize(st.surfaceDir));
          const yaw = datan2(heading.x * tx.x + heading.y * tx.y + heading.z * tx.z, heading.x * tz.x + heading.y * tz.y + heading.z * tz.z);
          if (yaw !== (st.turretYaw ?? 0)) this.state = { ...this.state, structures: this.state.structures.map(x => (x.id === st.id ? { ...x, turretYaw: yaw } : x)) };
        }
        const weaponKey = intent.gun?.weaponKey ?? seated?.seat.weapons[0];
        if (intent.fire && seated && weaponKey && seated.seat.weapons.includes(weaponKey) && (intent.gun?.power ?? 0) > 0) {
          const { azimuth } = directionToAzimuthElevation(n, heading);
          gunFire.push({ i, structureId: st!.id, params: { azimuth, elevation: Math.max(0, aimPitch), power: intent.gun!.power, weaponKey } });
        }
        continue;
      }
      if (!u.airborne) {
        const planet = this.state.planets[u.planetIndex];
        const filed = footprintObstacles(this.state, u.planetIndex, personMover(personSetOf(this.state)));   // 63.3 step 4: the shapes' footprints for a person (62.10: memoised, filed by cell)
        const ps = personSetOf(this.state);
        // 63.1d's follow-up: a person on foot inside a vehicle's hull (stepped out against it, or driven onto) is moved out first
        if (anyDrives) { const out = outOfHulls(this.state, u.planetIndex, u.surfaceDir, ps); if (out) u = { ...u, surfaceDir: out }; }
        let fwd = bandaging ? 0 : intent.forward, rgt = bandaging ? 0 : intent.right;
        let speed = ps.walkSpeed * STANCE_SPEED[stance] * (u.modifiers?.walkSpeedMult ?? 1);   // 62.2: a crouched walk, a crawl
        // 63.8: a move or a sprint, standing on foot and walking (GAIT): the pace up, the rocket tube's weight against it
        const gait = intent.gait && intent.gait !== 'walk' && stance === 'stand' && !bandaging && (Math.abs(fwd) > 1e-3 || Math.abs(rgt) > 1e-3) ? intent.gait : null;
        if (gait) speed *= GAIT[gait].speed * (u.weapon === 'rocket' ? GAIT.load.rocket : 1);
        if (gait ? u.gait !== gait : u.gait !== undefined) { if (gait) u = { ...u, gait }; else { const { gait: _was, ...rest } = u; u = rest; } }
        let body: Parameters<typeof stepWalk>[8] = ps;
        let slip: Vec3 | undefined;
        if (groundMaterialsOn(this.state.config)) {
          // 63.5 step 4: the ground underfoot sets the pace and the footing (core/groundWalk.ts) — for every walker
          const f = footingAt(this.state, u.planetIndex, n, this.actionTick * PHYS_DT);
          speed *= f.speed;
          body = { ...ps, scramble: (ps.scramble ?? 0) * f.scramble, ...(f.uphill > 0 ? { uphill: f.uphill } : {}) };
          if (f.grip !== null) {
            // ice: the walk's velocity closes on the one asked for at the grip (slow to turn, a slide to a stop) and drifts down a slope
            const ask = vecAdd(vecScale(heading, fwd), vecScale(rightOf(heading, n), rgt));
            const amount = Math.min(1, vecLength(ask));
            const want = amount > 1e-9 ? vecScale(vecNormalize(ask), speed * amount) : { x: 0, y: 0, z: 0 };
            const groundAt = (d: Vec3) => ps.fineAmplitude > 0 ? personSurfaceRadius(planet, d, ps) : getSurfaceRadius(planet, d);
            const v = slipStep(u.slip, want, n, f.grip, downhillAt(groundAt, n, ps.unitRadius), f.slide, ps.walkSpeed, dt);
            const w = velocityAsWalk(v, heading, n);
            fwd = w.forward; rgt = w.right; speed = w.speed;
            slip = w.speed > 1e-4 * ps.walkSpeed ? v : undefined;
          }
        }
        const walk = stepWalk(u.surfaceDir, heading, planet, filed.list, fwd, rgt, speed, dt, body, filed);
        heading = walk.heading;
        // 63.1d step 2: a person does not walk into a vehicle's hull (where it stands this tick)
        // (63.1d's follow-up: a person already within its radius of a hull may step away from it — closer only is refused)
        const intoHull = walk.moved > 0 && anyDrives && movingHullNear(personHip(planet, walk.surfaceDir, personSetOf(this.state)), personSetOf(this.state).unitRadius, u.planetIndex, this.state)
          && hullClearance(personHip(planet, walk.surfaceDir, ps), 2 * ps.unitRadius, u.planetIndex, this.state) <= hullClearance(personHip(planet, u.surfaceDir, ps), 2 * ps.unitRadius, u.planetIndex, this.state);
        u = intoHull ? u : { ...u, surfaceDir: walk.surfaceDir, ...(walk.scrambled ? { scrambling: true } : {}) };   // (decision 134)
        if (!walk.scrambled && u.scrambling) delete u.scrambling;
        if (slip && !walk.blocked) u.slip = slip; else if (u.slip) delete u.slip;   // (off ice, or stopped against something: no slide)
        // 63.5 (67.25, footstepsHeard): a walker is heard at its step's own range — its ground, stance and pace — unless
        // something louder of its was heard this tick or the last (a shot, written after its walk, wins anyway)
        if (this.state.config.footstepsHeard && !intoHull && walk.moved > 0) {
          const loud = stepLoudness(this.state, u, walk.moved / (dt * ps.walkSpeed), this.actionTick * dt);
          const prev = heard[u.id];
          if (!prev || prev.tick < this.actionTick - 1 || (prev.loud ?? 1) <= loud) {
            hear(); heard[u.id] = { pos: getPlayerWorldPosition(u, this.state.planets), tick: this.actionTick, loud, step: true };
          }
        }
        if (intent.jump && stance === 'stand') {   // 62.2: only a standing person jumps
          // A hop to the person's jump height under the gravity a person feels, carrying the walk
          const ps = personSetOf(this.state);
          const feet = ps.fineAmplitude > 0 ? personPointAt(this.state.planets[u.planetIndex], u.surfaceDir, ps, 0) : getPlayerWorldPosition(u, this.state.planets);   // S3: off the fine ground
          const up = vecNormalize(u.surfaceDir);
          const g = vecLength(gravityAccel(feet, this.state.planets)) * ps.gravityScale;   // felt gravity (a person)
          const along = vecAdd(vecScale(heading, intent.forward), vecScale(rightOf(heading, up), intent.right));
          const carry = vecLength(along) > 1e-9 ? vecScale(vecNormalize(along), personSetOf(this.state).walkSpeed * Math.min(1, vecLength(along))) : { x: 0, y: 0, z: 0 };
          // 63.15 (GameConfig.jumpFeel): to its own apex under its own gravity, its take-off height kept (core/jump.ts); off, as before
          const feel = defaultOn(this.state.config, 'jumpFeel'), from = vecAdd(feet, vecScale(up, ps.jumpLiftoff));
          u = {
            ...u, airborne: true, airborneCause: 'jump', airborneTime: 0,
            airbornePos: from,
            airborneVel: vecAdd(vecScale(up, feel ? jumpSpeed(this.state.config) : Math.sqrt(2 * g * personSetOf(this.state).jumpHeight)), carry),
            ...(feel ? { jumpFrom: jumpFromOf(this.state.planets[u.planetIndex], from) } : {}),
          };
          events.push({ kind: 'jumped', unitId: u.id });
        }
      }
      // (62.60, the look's cost: written into the tick's own copy of the unit — `u` is never `unit`, every path above made it
      // anew — as `scrambling` and `slip` are; the same object, key for key, as a spread would make, one copy a unit fewer)
      u.heading = heading; u.facing = heading;
      players[i] = u;
      const wpn = armed ? weaponSpec(personSetOf(this.state), weaponOf(u)) : null;
      const ready = !bandaging && (!wpn || (u.reloadLeft === undefined && u.switchLeft === undefined && roundsLeft(u, wpn) > 0));
      // 65.43: a semi-automatic or bolt gun fires on the press — held since the last tick, the trigger fires nothing more
      const pressed = !(wpn?.action && u.triggerHeld);
      if (intent.fire && pressed && (u.fireCooldown ?? 0) <= 0 && ready && u.gait !== 'sprint') {   // (63.8: a sprint holds the fire)
        // How fast it moved this tick (airborne counts as full) — a moving shooter's cone can open (PersonTuning.moveSpread)
        const moving = u.airborne ? 1 : Math.min(1, Math.sqrt(intent.forward * intent.forward + intent.right * intent.right));
        const spent = wpn ? { magazines: { ...(u.magazines ?? {}), [weaponOf(u)]: roundsLeft(u, wpn) - 1 } } : {};
        // 65.37: the Lab's fire rate for the player's own guns (a unit a seat holds), ÷ the interval; bots keep theirs
        const rate = this.state.controllers?.[u.id] !== undefined ? playerFireRate(this.state.config) : 1;
        let from: Vec3;
        if (weaponOf(u) === 'missile') {   // 65.49 step 2: no bullet — a homing body after the ship it locked (core/antiDropshipMissile.ts)
          const shot = launchMissile(this.state, u);
          this.state = shot.state;
          events.push({ kind: 'missileFired', unitId: u.id, missile: shot.missile });
          from = shot.missile.pos;
        } else {
          const shot = fireBullet(this.state, u, moving, undefined, u.gait === 'move' ? GAIT.move.spread : 0);   // (63.8: a move's fire unaimed)
          this.state = shot.state;
          events.push({ kind: 'fired', unitId: u.id, bullet: shot.bullet });
          from = shot.bullet.pos;
        }
        players[i] = { ...u, ...spent, fireCooldown: (wpn ? wpn.interval : personSetOf(this.state).fireInterval) / rate, shotsFired: u.shotsFired + 1 };
        hear(); heard[u.id] = { pos: from, tick: this.actionTick };
      }
      // 65.43: the trigger as it stands this tick, kept only while a semi or bolt gun is in hand (no other unit carries the field)
      const held = !!wpn?.action && intent.fire === true;
      if (held !== (players[i].triggerHeld === true)) {
        if (held) players[i] = { ...players[i], triggerHeld: true };
        else { const { triggerHeld: _t, ...rest } = players[i]; players[i] = rest as PlayerState; }
      }
    }
    this.state = { ...this.state, players };
    // 66.8 (§5.18): digging in — the diggers' scrapes deepen by the ground (a planet's digs change at most once a tick)
    if (this.state.config.entrench) this.state = stepDigs(this.state, intents, dt, this.actionTick * dt);
    if (this.state.config.modularBuildings) this.state = stepRubble(this.state);   // 63.14 step 4: the rubble's decay
    if (this.state.config.modularBuildings && this.state.config.collapse) this.state = stepCollapse(this.state);   // 72.3 step 3: what loses its support comes down
    // 64.11: the guns — reloads run down, then a gun that is ready and asked to fire fires its shell
    if (this.state.structures.some(st => st.seats?.some(x => x.reloadLeft !== undefined))) {
      this.state = { ...this.state, structures: this.state.structures.map(st => !st.seats?.some(x => x.reloadLeft !== undefined) ? st
        : { ...st, seats: st.seats.map(x => x.reloadLeft === undefined ? x : x.reloadLeft - dt > 0 ? { ...x, reloadLeft: x.reloadLeft - dt }
          // 65.22: a mounted gun's reload ends with a full belt (its seat's guns: belt and reload gone); a shell gun's as ever
          : x.guns?.length ? (({ reloadLeft: _r, belt: _b, ...rest }) => rest)(x) : x.launcher ? (({ reloadLeft: _r, ...rest }) => rest)(x) : { ...x, reloadLeft: undefined }) }) };   // (69.21: a launcher's rounds were counted in at its reload's start)
    }
    // 64.11: the gun brain's controller — a seated unit its brain drives fires the decision in force, exactly as solved,
    // on the first tick its seat is ready, once per decision (the decision came in as an input at T + L)
    if (this.state.gunOrders) {
      for (const [idText, order] of Object.entries(this.state.gunOrders)) {
        const id = Number(idText);
        if (order.fired || order.fire !== 'whenReady' || this.state.controllers?.[id] !== undefined) continue;
        const i = this.state.players.findIndex(q => q.id === id);
        const gunner = this.state.players[i];
        if (!gunner?.alive || gunner.mount?.bodyId !== order.structureId) continue;
        gunFire.push({ i, structureId: order.structureId, params: order.shot, brain: true });
      }
    }
    for (const g of gunFire) {
      const st = this.state.structures.find(x => x.id === g.structureId);
      const gunner = this.state.players[g.i];
      const gunSeat = st && gunner.mount ? seatAt(st, gunner.mount.seat) : undefined;
      if (!st || !gunSeat || gunSeat.reloadLeft !== undefined) continue;
      if (shellsLeft(this.state, gunSeat, g.params.weaponKey) <= 0) continue;   // decision 128: the gun's stock is out
      if (roleLost(st, 'function')) continue;   // partial destruction step 4: its gun part gone, the gun is silent (141)
      let params = g.params, reload = GUN_RELOAD_SECONDS[g.params.weaponKey] ?? GUN_RELOAD_DEFAULT, unspotted = false;
      // decision 141 ("less accurate"): a worn gun throws its shot off by its wear and reloads slower
      const wear = gunWear(this.state, st);
      if (wear > 0) {
        const rng = keyedGenerator(this.state.config.seed, gunner.id, this.state.shellsFired ?? 0, 'worn');
        params = { ...params, azimuth: params.azimuth + rng.range(-1, 1) * WORN_GUN.shellSpread * wear, elevation: params.elevation + rng.range(-1, 1) * WORN_GUN.shellSpread * wear };
        reload *= 1 + WORN_GUN.slow * wear;
      }
      // decision 128: aimed where none of the team sees, the shot is thrown off and the next one waits longer
      if (gunSpottingOn(this.state)) {
        const land = predictShellLanding(this.state, gunner, params);
        if (!land || !aimSpotted(this.state, gunner, land.planetIndex, land.point, this.actionTick)) {
          const rng = keyedGenerator(this.state.config.seed, gunner.id, this.state.shellsFired ?? 0, 'unspotted');
          params = { ...params, azimuth: params.azimuth + rng.range(-1, 1) * GUN_UNSPOTTED.spread, elevation: params.elevation + rng.range(-1, 1) * GUN_UNSPOTTED.spread };
          reload *= GUN_UNSPOTTED.reload;
          unspotted = true;
        }
      }
      const fired = fireShell(this.state, gunner, st.id, params);
      this.state = { ...fired.state, structures: fired.state.structures.map(x => x.id !== st.id ? x : { ...x, seats: x.seats!.map((y, i) => i === gunner.mount!.seat ? { ...spendShell(this.state, y, params.weaponKey), reloadLeft: reload } : y) }) };
      events.push({ kind: 'shellFired', unitId: gunner.id, shell: fired.shell, ...(unspotted ? { unspotted } : {}) });
      if (g.brain) this.state = { ...this.state, gunOrders: { ...this.state.gunOrders, [gunner.id]: { ...this.state.gunOrders![gunner.id], fired: true } } };
      hear(); heard[gunner.id] = { pos: fired.shell.pos, tick: this.actionTick };   // a gun is loud
    }
    // R14 (a): match events tick on the action clock too, once a second, on the event clock in turns (decision 110)
    if (this.state.matchEvents && this.actionTick % ACTION_EVENT_PERIOD_TICKS === 0) this.state = tickMatchEvents(this.state, actionEventClock(this.actionTick * dt));
    // 69.9: the bases' point defence engages what is coming in (the shells, the blast bullets, low ships) before this tick's flight
    if (pdOn(this.state.config) && this.state.structures.some(s => s.type === 'pointDefence')) {   // (69.50 step 2a: or the air-defence sites)
      const pd = stepPointDefence(this.state, this.actionTick);
      this.state = pd.state;
      events.push(...pd.events);
    }
    // 63.13: the gunships fire (their rounds and rockets made bullets), and the guided rockets steer onto their marks, before the bullets fly
    if (countOf(this.state.config, 'gunships') && this.state.dropships?.flights.some(f => f.gunship)) {
      const g = stepGunships(this.state, this.actionTick);
      this.state = g.state;
      for (const sh of g.shots) events.push({ kind: 'gunshipFire', flight: sh.flight, team: sh.team, weapon: sh.weapon, from: sh.from, to: sh.to });
    }
    if (countOf(this.state.config, 'gunships')) this.state = steerGuided(this.state, dt);   // (off: nothing scanned)
    // 69.59 step 3: the helicopters' door guns and pods fire (their rounds made bullets) before the bullets fly
    if (countOf(this.state.config, 'helicopters') && this.state.dropships?.flights.some(f => f.heliGuns)) {
      const h = stepHeliGuns(this.state, this.actionTick);
      this.state = h.state;
      for (const sh of h.shots) events.push({ kind: 'heliFire', flight: sh.flight, team: sh.team, weapon: sh.weapon, from: sh.from, to: sh.to });
    }
    const flown = stepBullets(this.state, dt, this.actionTick * PHYS_DT);   // (63.5: the ground's material now — rain's mud)
    // decision 104: terrain fog at L0, per team, on the sight beat, from units that moved (core/teamFog.ts)
    stepTeamFog(this.state, pi => this.truePlanetColors.get(pi), this.actionTick);
    // 65.28 (decision 161): suppression — the marks run down, the units the rounds passed near are marked (switch off: as was)
    this.state = markUnderFire(defaultOn(this.state.config, 'suppression') ? stepSuppression(flown.state, dt, flown.near ?? [], flown.nearRounds ?? []) : flown.state, flown.events);   // the capture-under-fire rule (no-op when off)
    // S7: a rocket bursts where it stopped — on a person, a prop, the ground or a drone (core/rocket.ts; no crater)
    for (const e of flown.events) {
      if (e.kind === 'expired' || !e.bullet.blast) continue;
      if (e.bullet.armAge !== undefined && e.bullet.age < e.bullet.armAge) continue;   // 65.31: a 40 mm round not yet armed is a dud
      const planetIndex = e.kind === 'hitGround' || e.kind === 'hitCover' ? e.planetIndex
        : e.kind === 'hitUnit' ? (this.state.players.find(q => q.id === e.targetId)?.planetIndex ?? -1) : -1;
      const r = rocketBlast(this.state, e.bullet.ownerId, e.point, planetIndex, e.bullet.blast, bulletExplosion(e.bullet));
      this.state = r.state;
      if (cratering && e.kind === 'hitGround') craters.push({ kind: 'rocket', planetIndex, point: e.point });   // 63.11: on the ground only
      events.push({ kind: 'blast', ownerId: e.bullet.ownerId, point: e.point, planetIndex, damages: r.damages, kills: r.kills, launched: r.launched, explosion: bulletExplosion(e.bullet),
        ...(e.bullet.fromShip !== undefined ? { fromShip: e.bullet.fromShip } : {}), ...(e.bullet.by ? { by: e.bullet.by } : {}) });   // (69.83: what fired it, for the feed)
    }
    // 69.58: the ships' countermeasures before the missiles fly — flares popped at the missiles after them, the gunship's turret
    if ((defaultOn(this.state.config, 'countermeasures') || defaultOn(this.state.config, 'shipTurrets')) && (this.state.missiles?.live.length || this.state.decoys?.live.length)) {
      const cm = stepCountermeasures(this.state, this.actionTick);
      this.state = cm.state;
      events.push(...cm.events);
    }
    // 65.49 step 2: the anti-dropship missiles fly a tick (the ships at this tick's poses); a burst is a rocket's, at the missile's damage
    if (this.state.missiles?.live.length) {
      const flown = stepMissiles(this.state, dt, this.actionTick);
      this.state = flown.state;
      for (const e of flown.ends) {
        if (e.kind === 'spent') { events.push({ kind: 'missileSpent', missile: e.missile }); continue; }
        const r = rocketBlast(this.state, e.missile.ownerId, e.point, e.missile.planetIndex, missileBlast(this.state, e.missile), 'law');   // (69.50 step 2a: at its class's damage)
        this.state = r.state;
        events.push({ kind: 'blast', ownerId: e.missile.ownerId, point: e.point, planetIndex: e.missile.planetIndex, damages: r.damages, kills: r.kills, launched: r.launched, explosion: 'law' });
      }
    }
    // 64.11: shells fly a tick; a landing is resolved as a turn shot's is, its push converted for people (62.8)
    if (this.state.shells?.length) {
      const sh = stepShells(this.state);
      this.state = { ...this.state, shells: sh.shells };
      for (const s of sh.lost) events.push({ kind: 'shellLost', shell: s });
      for (const hit of sh.impacts) {
        // 69.65 (DECISIONS 289): with the blast model on, the shell bursts as its person-scale class (the crater stays its own)
        const cls = shellExplosion(hit.shell.weaponKey), model = burstModelFor(this.state, cls ?? undefined);
        const r = resolveImpact(this.state, { impactPoint: hit.point, planetIndex: hit.planetIndex, projectile: shellProjectile(hit.shell) }, {
          createCraterImpl: this.enterKernel() ? this.wasmCrater : undefined,
          personScale: personSetOf(this.state).k,
          ...(model && cls ? { burst: { model, damage: SHELL_HEART[cls]!, ...(zonesOn(this.state.config) ? { pen: BURST_PENETRATION[cls] } : {}) } } : {}),
        });
        this.state = r.state;
        this.applyImpactWorld(r);
        events.push({ kind: 'shellImpact', shell: hit.shell, planetIndex: hit.planetIndex, point: hit.point, resolution: r });
      }
    }
    if (this.state.structures.some(st => st.seats?.some(x => seatUnit(x) !== null))) {
      const before = this.state;
      this.state = reconcileSeats(this.state);   // the dead leave their seats
      // 69.89: a wrecked hull's crew, lost with it, named by the tick's blow that wrecked it (the kill feed's line) — among
      // the tick's events so far and its rounds' (which join them below)
      if (this.state !== before) { const blows = [...events, ...flown.events]; events.push(...crewLostEvents(before, this.state, s => hullBlow(before, s, blows))); }
    }
    // 63.1n: destroyed vehicles queue for their side's stock; the due ones are parked at their base or the side's pad
    if (this.state.vehicleStocks) { const v = stepVehicleStock(this.state, this.actionTick); if (v) { this.state = v.state; events.push(...v.events); } }
    if (this.state.motorPools) this.state = syncPoolStacks(this.state, this.actionTick);   // 69.14: the pool's stacks as the stock stands them (its shapes)
    // 64.11: a gun decision lapses when its unit no longer sits in that seat
    if (this.state.gunOrders) {
      const keep = Object.entries(this.state.gunOrders).filter(([id, o]) => { const u = this.state.players.find(q => q.id === Number(id)); return !!u?.alive && u.mount?.bodyId === o.structureId; });
      if (keep.length !== Object.keys(this.state.gunOrders).length) this.state = { ...this.state, gunOrders: keep.length ? Object.fromEntries(keep) : undefined };
    }
    this.state = { ...this.state, tick: this.actionTick + 1, ...(heard !== (this.state.heard ?? EMPTY_HEARD) ? { heard } : {}) };
    events.push(...flown.events);
    for (const r of flown.nearRounds ?? []) events.push({ kind: 'nearMiss', unitId: r.unitId, point: r.point });   // 65.28
    // I2: launches run down (the operator stands still until done); drones fly their routes on their own
    this.state = tickDroneLaunches(this.state, dt);
    if (this.state.drones?.some(d => d.alive)) {
      const flew = stepDrones(this.state, dt);
      this.state = flew.state;
      events.push(...flew.events);
      // 65.16: strike drones dive on their aim and burst in the person-family blast (the rocket's S7 path), spent in it
      const struck = stepStrikeDrones(this.state, dt);
      this.state = struck.state;
      for (const d of struck.dived) events.push({ kind: 'droneDived', droneId: d.id, pos: d.dive!.pos });
      for (const b of struck.bursts) {
        const r = rocketBlast(this.state, b.operatorId, b.point, b.planetIndex, strikeBlast(this.state), STRIKE_DRONE.explosion);
        this.state = r.state;
        if (cratering && this.onGround(b.planetIndex, b.point)) craters.push({ kind: 'rocket', planetIndex: b.planetIndex, point: b.point });   // 63.11: a charge, as a rocket
        events.push({ kind: 'droneSpent', droneId: b.droneId, end: 'struck' });
        events.push({ kind: 'blast', ownerId: b.operatorId, point: b.point, planetIndex: b.planetIndex, damages: r.damages, kills: r.kills, launched: r.launched, explosion: STRIKE_DRONE.explosion });
      }
    }
    // 69.23: the mortar carriers' set (still since), once a tick while there are any
    if (this.state.structures.some(s => s.mortar)) this.state = stepMortars(this.state, this.actionTick);
    // 68.39: the airstrikes on their way — announced once, warned once near their impact; 68.39b: their rounds fired, to fly and burst as rounds do
    if (this.state.airstrikes?.inbound.length) {
      const air = stepAirstrikes(this.state, this.actionTick);
      this.state = air.state;
      for (const s of air.announced) events.push({ kind: 'airstrikeCalled', team: s.team, id: s.id, planetIndex: s.planetIndex, aim: s.aim, called: s.called, impactTick: s.impactTick, fix: s.fix });
      for (const s of air.warned) events.push({ kind: 'airstrikeInbound', team: s.team, id: s.id, planetIndex: s.planetIndex, points: s.warnPoints ?? strikePoints(this.state, s) });
      // 68.39b: each round due is fired — a round in flight from the battery, its burst its class (the bullets' step flies it;
      // where it lands, the rounds' burst path below makes the burst)
      if (air.fire.length) {
        const blast = airstrikeBlast(this.state), mortarBlast = { ...blast, damage: blast.damage * MORTAR.damageScale };   // (69.23: a mortar mission's round the medium tube's)
        let next = this.state.bulletsFired ?? 0;
        const fired = air.fire.map(({ strike, round }) => ({ id: next++, ownerId: airstrikeOwner(strike.team), pos: round.pos, vel: round.vel, age: 0, damage: 0,
          life: (round.landTick - round.fireTick) * PHYS_DT * 1.5 + 1, gravity: round.fall ?? AIRSTRIKE.fall, wind: ACTION_WIND.shell, blast: strike.kind === 'mortar' ? mortarBlast : blast, explosion: AIRSTRIKE.explosion,
          ...(strike.kind === 'mortar' ? { by: 'mortar' as const } : {}) }));   // (69.83: the feed names the mortar)
        this.state = { ...this.state, bullets: [...(this.state.bullets ?? []), ...fired], bulletsFired: next };
      }
    }
    // 216 (GameConfig.realTiming): wound throws due now leave the hand (a thrower down, seated or in the air gets it back)
    if (defaultOn(this.state.config, 'realTiming')) this.state = stepRealTiming(this.state, this.actionTick);
    // 65.15: grenades fly, bounce and burst (HE: the person-family blast, the rocket's path) or smoke; clouds stand and clear
    if (this.state.grenades?.length || this.state.smokes?.length) {
      const g = stepGrenades(this.state, dt);
      this.state = g.state;
      for (const b of g.bursts) {
        const r = rocketBlast(this.state, b.grenade.ownerId, b.point, b.grenade.planetIndex, grenadeBlast(this.state), GRENADES.he.explosion);
        this.state = r.state;
        if (cratering && this.onGround(b.grenade.planetIndex, b.point)) craters.push({ kind: 'grenade', planetIndex: b.grenade.planetIndex, point: b.point });   // 63.11
        events.push({ kind: 'grenadeBurst', grenadeId: b.grenade.id, ownerId: b.grenade.ownerId, point: b.point });
        events.push({ kind: 'blast', ownerId: b.grenade.ownerId, point: b.point, planetIndex: b.grenade.planetIndex, damages: r.damages, kills: r.kills, launched: r.launched, explosion: GRENADES.he.explosion });
      }
      for (const c of g.clouds) events.push({ kind: 'smokeCloud', cloud: c });
    }
    // 69.47 step 1 (GameConfig.charges): the engineers' kneels run, the timers due and the wires pulled go off, a charge a
    // burst this tick lay over is lost (not set off), an engineer short in reach of a dump is refilled
    if (this.state.config.charges) {
      const person = PERSON_BODY_HEIGHT * personSetOf(this.state).k;
      const blasts = events.flatMap(e => (e.kind === 'blast' && e.planetIndex >= 0 ? [{ planetIndex: e.planetIndex, point: e.point, radius: (BLAST_RADII[e.explosion]?.kill ?? 1) * person }] : []));
      const c = stepCharges(this.state, intents, this.actionTick, dt, blasts);
      if (c) { this.state = c.state; events.push(...c.events); for (const b of c.bursts) this.blowCharge(b, events); }
    }
    // I1 / I2: towers and flying drones look a few times a second (as the action bots do), for their team
    if (isDue(RATES.towerSight, this.actionTick) && this.state.structures.some(isTowerSensor)) this.state = updateTowerSightings(this.state, this.actionTick);
    // 69.4 step 4: a relay or FOB one enemy side holds alone changes hands (core/structureCapture.ts) — both webs at once when one does
    let taken = false;
    if (this.state.config.relays && isDue(RATES.structureCapture, this.actionTick)) {
      const c = stepStructureCapture(this.state, RATES.structureCapture.period * dt);
      if (c) { this.state = { ...this.state, structures: c.structures }; events.push(...c.events); taken = c.events.length > 0; }
    }
    // 69.2c: each side's web (core/web.ts), once a second — before the relays look, which see only while linked
    if (this.state.config.relays && (taken || isDue(RATES.web, this.actionTick))) this.state = updateWebs(this.state, this.actionTick);
    // 69.72 step 5 (DECISIONS 304): stale tasks cleared every tick — expired, its units all dead, a lapsed form, a mark the
    // picture no longer reads — so none holds a squad's claim, before the plays read their squads
    if (this.state.squads?.length) this.state = pruneMarks(pruneTasks(this.state, this.actionTick, { useWasm: this.state.squads.some(sq => sq.tasks.some(t => t.order.kind === 'engage')) && this.enterKernel() }), this.actionTick);   // (the kernel only for an engage's sight)
    // 68.22: the plays' phases (core/plays.ts), once a second
    if (this.state.plays && isDue(RATES.plays, this.actionTick)) {
      const r = stepPlaysLogged(this.state, this.actionTick);
      this.state = r.state;
      if (r.refusals.length) this.playRefusals.push(...r.refusals);
    }
    // 69.2a: what each side has learned of the points (core/objectiveIntel.ts), once a second — before the scouts, which end on it
    if (this.state.config.roughStart && isDue(RATES.plays, this.actionTick)) this.state = stepObjectiveIntel(this.state, this.actionTick);
    // bots against ships (with shipsDestructible): where each side has seen enemy ships land (core/landingIntel.ts), on the same beat
    if (this.state.config.shipsDestructible && isDue(RATES.plays, this.actionTick)) this.state = stepLandingIntel(this.state, this.actionTick);
    if (pdOn(this.state.config) && isDue(RATES.plays, this.actionTick)) {
      const before = this.state;
      this.state = stepMountIntel(this.state, this.actionTick);   // 69.9 step 5: the mounts each side has seen
      events.push(...skyOpened(before, this.state));   // 69.61 step 3: a site gone from a side's known cover — the sky opened (with the story on)
    }
    // 69.73 step 2a: each side's intel record (core/intelRecord.ts) — this tick's share of the looks, and on its beat the people
    if (this.state.config.intelRecord) this.state = stepIntel(this.state, this.actionTick);
    if (this.state.config.lighters && isDue(RATES.plays, this.actionTick)) this.state = stepLighterOrders(this.state, this.actionTick);   // 69.10 step 2: the standing order's runs
    // 69.2e: a scout that has seen its place ends (core/scouts.ts), on the same once-a-second beat
    if (isDue(RATES.plays, this.actionTick)) this.state = stepScouts(this.state, this.actionTick);
    // 69.2b: the standing relays look round them (core/relay.ts) on the same beat, each on its own tick of it (its id's phase)
    if (this.state.config.relays && this.state.structures.some(isStandingRelay)) {
      const tick = this.actionTick;
      this.state = updateRelaySightings(this.state, tick, r => isDue(RATES.relaySight, (tick + r.id) % RATES.relaySight.period));
    }
    if (isDue(RATES.droneSight, this.actionTick) && this.state.drones?.some(d => d.alive)) this.state = mergeSightings(this.state, droneSightingsNow(this.state, this.actionTick));
    // 64.4: the knowledge ages count on this clock too — ghosts fade on the sight beat, their "now" converted at the rule (knowledgeTurn)
    if (isDue(RATES.towerSight, this.actionTick) && this.state.fog.some(f => f.ghosts.length > 0 || f.structureGhosts.length > 0)) {
      const now = knowledgeTurn(this.state);
      const fog = this.state.fog.map(f => pruneFogMarkers(f, this.state.players, this.state.structures, now, GHOST_MARKER_MAX_AGE));
      if (fog.some((f, i) => f !== this.state.fog[i])) this.state = { ...this.state, fog };
    }
    // X22: withdrawing units standing in their exit get out
    if (this.state.exits) this.state = stepExtraction(this.state, this.actionTick);
    // 59.6: capture points and the bleed; respawn waves (the respawn layer, on the action clock)
    if (this.state.modeState.points) {
      const c = stepCapture(this.state, dt);
      this.state = c.state;
      events.push(...c.events);
      // 69.6 (DECISIONS 166 (5)): a sector falling takes the other sides' beacons standing in it with it (core/beacon.ts);
      // 69.4 step 6d (171 Q4, with groundRules): and gives their FOBs and relays there to the taker — both webs at once
      for (const e of c.events) {
        if (e.kind !== 'sectorTaken') continue;
        if (this.state.config.beacons) {
          const lost = beaconsLostWith(this.state, e.sector, e.team);
          if (lost) { this.state = { ...this.state, structures: lost.structures }; events.push(...lost.events); }
        }
        if (this.state.config.groundRules && this.state.config.relays) {
          const t = takenWithSector(this.state, e.sector, e.team);
          if (t) { this.state = updateWebs({ ...this.state, structures: t.structures }, this.actionTick); events.push(...t.events); }
        }
      }
    }
    // 69.50 step 1: the story's objectives (with GameConfig.storyObjectives) — a hold's clock, a set destroyed, latched done
    if (this.state.modeState.objectives) {
      const o = stepObjectives(this.state, dt, this.actionTick);
      this.state = o.state;
      events.push(...o.events);
    }
    // 69.3b: the dropships in the air — one set down leaves its container standing (the side's oldest over the cap taken away),
    // its riders come in at its ramp one by one (a wave's respawn, each its ticket), and one gone is dropped
    if (this.state.pods?.drops.length) events.push(...this.stepPodDrops());   // DECISIONS 200 step 5
    if (this.state.sorties?.length) {
      // DECISIONS 200 step 4: the sorties due leave (a full container, or the beat's wait); one to a relay spends a drop, as a wave did
      const r = stepSorties(this.state, this.actionTick);
      this.state = r.state;
      // (69.37 step 4: a wave ship away on a play's air element — the play's ship has flown)
      for (const l of r.launched) { const play = l.flight.fromPad ? this.state.shipPads?.[l.flight.team]?.dest?.play : undefined; if (play !== undefined) this.state = airFlown(this.state, play, this.actionTick); }
      const relays = new Set(r.launched.map(l => l.at).filter(at => this.state.structures.find(s => s.id === at)?.type === 'relay'));
      if (relays.size) this.state = { ...this.state, structures: spendDrops(this.state.structures, relays) };
    }
    // 69.52 step 3 (core/skyEvade.ts): the ships under fire this tick (a lock, a missile after them, a hit) answer by their risk
    if (this.state.dropships?.flights.length) this.state = stepEvasion(this.state, shipsAtStart, this.actionTick);
    if (this.state.dropships?.flights.length) events.push(...this.stepDropships());
    if (this.state.lighters?.runs.length || this.state.lighters?.staged) {   // 69.10: the lighters' runs — announced, down, lifting, gone; step 3: the staged frames stacked
      const l = stepLighters(this.state, this.actionTick);
      this.state = l.state;
      for (const r of l.announced) events.push({ kind: 'lighterCalled', team: r.team, id: r.id, planetIndex: r.planetIndex, strip: r.strip, touchTick: r.touchTick, frames: r.frames });
      for (const r of l.down) events.push({ kind: 'lighterDown', team: r.team, id: r.id });
      for (const u of l.unloaded) events.push({ kind: 'lighterUnloaded', team: u.run.team, id: u.run.id, vehicle: u.kind, left: u.left });
      for (const r of l.lifted) events.push({ kind: 'lighterLifted', team: r.team, id: r.id });
      for (const r of l.gone) events.push({ kind: 'lighterGone', team: r.team, id: r.id });
    }
    if (this.state.dropships?.lost?.length) {   // 69.3b, the ship as a body: the ships lost this tick (core/shipDamage.ts loseShip), out as events
      for (const l of this.state.dropships.lost) events.push({ kind: 'shipLost', team: l.team, id: l.id, planetIndex: l.planetIndex, at: l.at, survived: l.survived, lost: l.lost });
      const lostNow = this.state.dropships.lost;
      const { lost: _l, ...rest } = this.state.dropships;
      this.state = { ...this.state, dropships: rest };
      for (const l of lostNow) this.state = noteCrew(this.state, l.team, l.survived, this.actionTick);   // 69.62: a crew to rescue
    }
    if (this.state.dropships?.crashes?.length) events.push(...this.stepShipCrashes());   // 69.3b, the ship's crash: the falling ships due down
    if (this.state.structures.some(s => s.type === 'shipWreck')) this.state = decayWrecks(this.state, this.actionTick);
    // 68.4: a called reserve comes in at once (the teams whose call is due), then the regular wave on its beat
    const called = this.state.reserveCalls ? new Set(Object.entries(this.state.reserveCalls).filter(([, c]) => c.due).map(([t]) => Number(t))) : null;
    const waveDue = !!this.state.modeState.respawn && isDue(respawnWaveRate(this.state), this.actionTick);
    if (this.state.modeState.respawn && (waveDue || (called && called.size))) {
      if (called?.size) this.state = { ...this.state, reserveCalls: Object.fromEntries(Object.entries(this.state.reserveCalls!).map(([t, c]) => [t, { ...c, due: false }])) };
      const wave = this.respawnWave(waveDue ? undefined : called!);
      events.push(...wave);
      // 69.3c: at a person scale the arrivals come down in pods (decision 125) — held till they land
      if (personSetOf(this.state).k < 1) {
        const landed = new Set(wave.flatMap(e => (e.kind === 'respawned' ? [e.unitId] : [])));
        if (landed.size) this.state = { ...this.state, players: this.state.players.map(p => (landed.has(p.id) && !p.airborne ? { ...p, landingLeft: ARRIVAL.fallSeconds } : p)) };
      }
      // 68.10: a respawned unit finds its squad — the nearest leader's with room, its own, or a spent one re-formed
      if (this.state.config.fluidSquads && this.state.squads?.length) {
        const r = squadsOnRespawn(this.state, wave.flatMap(e => (e.kind === 'respawned' ? [e.unitId] : [])), this.state.config.fluidSquads);
        this.state = r.state;
        events.push(...r.events);
      }
    }
    // DECISIONS 208: the parked wave ships take their waiting riders aboard (this wave's among them)
    if (this.state.shipPads && this.state.sorties?.length) events.push(...this.boardShips());
    if (this.state.shipPads) this.stepShipBoarding();   // 209 (5): the ships' destinations; who walks up a ramp boards
    if (this.state.dropships?.flights.some(f => f.pickup)) this.state = pickupBoarding(this.state, this.actionTick);   // 69.18: a pickup's squad boards at its ramp
    if (this.state.dropships?.flights.some(f => f.pickup?.to || f.lift)) this.state = stepHeliLifts(this.state, this.actionTick);   // 69.74 step 1: a helicopter's lift on to its drop, then home
    if (this.state.dropships?.pads) this.state = stepHelipads(this.state, this.actionTick);   // 69.74 step 2: the helicopters turned round on their pads ready again; a pad down grounds its own
    if (this.state.rescues?.length) {   // 69.62: the hot rescues' phases (core/rescue.ts)
      const r = stepRescues(this.state, this.actionTick, this.enterKernel());
      this.state = r.state;
      for (const e of r.events) events.push(rescueEvent(e));
    }
    // 68.10: fluid membership — separation and strays, absorption, merging — once a sight period, on state alone
    if (this.state.config.fluidSquads && this.state.squads?.length && this.actionTick % SIGHT_PERIOD_TICKS === 0) {
      const r = stepSquadMembership(this.state, this.actionTick, this.state.config.fluidSquads);
      this.state = r.state;
      events.push(...r.events);
    }
    // 65.35 step 5 (the owner, DECISIONS 195: "sure explosions for vehicle but yes modest"): a vehicle destroyed this tick bursts
    // at its hull with its class's modest blast (EXPLOSIONS.vehicleDeath) — which may wreck another, which bursts in turn (at most
    // once each); 63.11: each leaves a craterlet under its hull; the tick's craterlets land together (one planet change)
    // 63.12 (vehicleZones): a vehicle shot to death burns out — its burst `burnOut.delayS` later, a third the size, its crew out
    // first (core/seats.ts reconcileSeats); a burst's kill bursts at once, and the load aboard widens either (loadWidening)
    const zoned = zonesOn(this.state.config);
    const deathBurst = (s: StructureState, share: number) => {
      const blast = explosionBlast(this.state, 'vehicleDeath');
      if (!blast) return;
      const ps = personSetOf(this.state), at = vecAdd(getStructureWorldPosition(s, this.state.planets), vecScale(vecNormalize(s.surfaceDir), 0.5 * PERSON_BODY_HEIGHT * ps.k));   // (its hull's middle)
      const sized = share === 1 ? blast : { ...blast, radius: blast.radius * share };
      const r = rocketBlast(this.state, vehicleBlastOwner(s.id), at, s.planetIndex, sized, 'vehicleDeath');   // (step 6: its push, with Bursts throw on)
      this.state = r.state;
      events.push({ kind: 'blast', ownerId: vehicleBlastOwner(s.id), point: at, planetIndex: s.planetIndex, damages: r.damages, kills: r.kills, launched: r.launched, explosion: 'vehicleDeath' });
    };
    if (zoned && this.state.burnOuts?.some(b => b.due <= this.actionTick)) {
      const due = this.state.burnOuts.filter(b => b.due <= this.actionTick), left = this.state.burnOuts.filter(b => b.due > this.actionTick);
      this.state = left.length ? { ...this.state, burnOuts: left } : (({ burnOuts: _b, ...rest }) => rest as GameState)(this.state);
      for (const b of due) { const s = this.state.structures.find(x => x.id === b.id); if (s) deathBurst(s, ZONE_RULES.burnOut.radiusShare * loadWidening(s)); }
    }
    const up = new Set(vehiclesUp);
    for (let wrecked = vehiclesUp.filter(id => (this.state.structures.find(x => x.id === id)?.health ?? 1) <= 0); wrecked.length; wrecked = [...up].filter(id => (this.state.structures.find(x => x.id === id)?.health ?? 1) <= 0)) {
      for (const id of wrecked) {
        up.delete(id);
        const s = this.state.structures.find(x => x.id === id), planet = s ? this.state.planets[s.planetIndex] : undefined;
        if (!s || !planet) continue;
        if (cratering) craters.push({ kind: 'vehicle', planetIndex: s.planetIndex, point: vecAdd(planet.position, vecScale(vecNormalize(s.surfaceDir), planet.radius)) });
        if (zoned && s.gunfireKill) {
          this.state = { ...this.state, burnOuts: [...(this.state.burnOuts ?? []), { id: s.id, due: this.actionTick + Math.round(ZONE_RULES.burnOut.delayS / PHYS_DT) }] };
          continue;
        }
        deathBurst(s, zoned ? loadWidening(s) : 1);
      }
    }
    if (cratering && craters.length) this.state = addCraterlets(this.state, craters, this.actionTick * PHYS_DT);
    events.push(...holdsLost(holdsAtStart, this.state));   // 69.61 step 3: a side's FOB fallen or taken, or its HQ fallen, this tick (with the story on)
    // Time's up: the mode's turn-limit rule decides (the one win pipeline — alive, kills, damage, objective); it reads
    // the action time limit in seconds through core/clockRules.ts timeLimitReached (64.4: no counter is rewritten)
    const gameOver = !this.actionOverSent && isGameOver(this.state);
    if (gameOver) this.actionOverSent = true;
    // 67.14: the record reads this step's events and who is still standing (cheap when nothing happened)
    const rec = this.record;
    if (events.length > 0 || this.state.players.some(pl => !pl.alive && rec.alive.includes(pl.id)) || this.state.players.filter(pl => pl.alive).length !== rec.alive.length || heldChanged(rec, this.state)) this.record_ = recordEvents(rec, events as RecordEvent[], this.state, this.actionTick);
    if (events.length > 0 || gameOver) p.actionEvents?.(events, gameOver);
  }

  /**
   * Launch a shot or bomblet: firing phase, flight precomputed (the same simulation
   * the aim preview shows), playback cursors reset.
   */
  launchShot(projectile: ProjectileState, p: SimPresenter): void {
    this.state = startFiringPhase(this.state, projectile);
    this.boostedGeyserIds.clear();
    this.probeTrailPoints = WEAPONS[projectile.weaponKey].reveals ? [vecClone(projectile.pos)] : [];
    this.probeRevealCounter = 0;
    this.projCursor = 0;
    this.flightEnded = false;
    this.precomputedGeyserIdx = 0;
    this.precomputedSim = this.simulateFlight(projectile);
    p.launched(projectile, this.precomputedSim);
  }

  /**
   * The complete flight of a projectile — the SAME simulation for the aim preview
   * and the real shot, on the kernel when the match uses it (identical results),
   * wormhole transits folded in as multi-segment flights.
   */
  simulateFlight(projectile: ProjectileState): ProjectileSimResult {
    const planets = this.state.planets;
    const geysers = buildGeyserSims(this.state.hazards, planets);
    const hitboxes = buildHitboxes(this.state.players, planets, projectile.ownerId);
    let segment: SegmentSimulator | undefined;
    // decision 120 with 42/43: the wind lives in the kernel only — a windy match needs it (no TypeScript twin)
    if (this.state.config.wind && !this.gameWasm) throw new Error('wind on lobbed shells needs the physics kernel (decision 120)');
    if (this.enterKernel()) {
      setGeysers(geysers);
      setHitboxes(hitboxes);
      segment = (pos, vel, pl, gs, hb, steps, oob, cd) =>
        simulateProjectileFull(pos, vel, steps, oob, cd) ?? simulateFlightSegment(pos, vel, pl, gs, hb, steps, oob, cd);
    }
    return simulateFlight(projectile.pos, projectile.vel, planets, geysers, hitboxes, OUT_OF_BOUNDS, segment);
  }

  /** The current flight as flown so far (for its shot record). */
  flownPoints(): Vec3[] {
    const sim = this.precomputedSim;
    return sim ? sim.points.slice(0, Math.min(sim.points.length, this.projCursor + 1)) : [];
  }

  /** Advance the flight by one step; at the end of the precomputed path, resolve how it ended. */
  private stepFlight(p: SimPresenter): void {
    let projectile = this.state.projectile!;
    const sim = this.precomputedSim;
    if (!sim) { this.endLost('noSim', null, -1, projectile, p); return; }   // always precomputed at launch
    const last = sim.points.length - 1;
    this.projCursor++;

    if (this.projCursor > last) {
      this.projCursor = last;
      if (p.holdFlightEnd(projectile.ownerId)) return;   // P2P: wait for the remote shooter's detonate / flightEnd
      p.flightEndReached(projectile.ownerId);
      if (sim.hitPlanetIndex >= 0 && sim.hitPoint) {
        const hitPlanet = this.state.planets[sim.hitPlanetIndex];
        if (sim.hitPlayerId < 0 && hitPlanet.type === 'star') {
          this.endLost('star', sim.hitPoint, sim.hitPlanetIndex, { ...projectile, pos: sim.points[last], vel: sim.finalVel }, p);
        } else if (sim.hitPlayerId < 0 && hitPlanet.type === 'wormhole') {
          // Max transits reached (transits themselves are folded into the precompute)
          this.endLost('wormhole', sim.hitPoint, sim.hitPlanetIndex, { ...projectile, pos: sim.points[last], vel: sim.finalVel }, p);
        } else {
          this.endImpact(sim.hitPlayerId >= 0 ? 'directHit' : 'impact', sim.hitPoint, sim.hitPlanetIndex, { ...projectile, pos: sim.hitPoint, vel: sim.finalVel }, p);
        }
      } else {
        this.endLost('lost', null, -1, projectile, p);   // out of bounds / timeout
      }
      return;
    }

    const curPos = sim.points[this.projCursor];
    // Velocity from adjacent points (orientation only)
    const prevPos = sim.points[this.projCursor - 1];
    projectile = {
      ...projectile,
      pos: curPos,
      vel: { x: (curPos.x - prevPos.x) / PHYS_DT, y: (curPos.y - prevPos.y) / PHYS_DT, z: (curPos.z - prevPos.z) / PHYS_DT },
      time: projectile.time + PHYS_DT,
      fuelRemaining: Math.max(0, projectile.fuelRemaining - PHYS_DT),
      phase: projectile.fuelRemaining > PHYS_DT ? 'burn' : 'ballistic',
    };
    // A probe scans the ground below it as it flies, every 10th step
    if (this.probeTrailPoints.length > 0) {
      this.probeTrailPoints.push(vecClone(curPos));
      contentChanged(this.probeTrailPoints);   // grown in place (state.flight.probeTrail): the checksum's array cache must forget it
      if (++this.probeRevealCounter >= 10) {
        this.probeRevealCounter = 0;
        const scan = this.scanAt(curPos, projectile.ownerId);
        if (scan) p.probeScan(scan);
      }
    }
    while (this.precomputedGeyserIdx < sim.geyserEvents.length && sim.geyserEvents[this.precomputedGeyserIdx].step <= this.projCursor) {
      this.precomputedGeyserIdx++;
      p.geyserDeflect(curPos);
    }
    this.state = { ...this.state, projectile };
  }

  private endImpact(ending: 'directHit' | 'impact', point: Vec3, planetIndex: number, projectile: ProjectileState, p: SimPresenter): void {
    this.state = { ...this.state, projectile };
    const hitPlayerId = this.precomputedSim?.hitPlayerId ?? -1;
    const { r, record, alerts } = this.resolveShotImpact(point, planetIndex, projectile, hitPlayerId >= 0 ? 'playerHit' : 'impact', this.flownPoints());
    const probeSpotted = this.takeProbeReveal(projectile);
    p.impacted({ ending, point, planetIndex, projectile, hitPlayerId, r, record, alerts, probeSpotted });
    this.nextFlightOrOver(projectile.ownerId, p);
  }

  private endLost(ending: 'star' | 'wormhole' | 'lost' | 'noSim', point: Vec3 | null, planetIndex: number, projectile: ProjectileState, p: SimPresenter): void {
    this.state = { ...this.state, projectile };
    const probeSpotted = this.takeProbeReveal(projectile);
    const record = this.shotLost(this.flownPoints());
    p.lost({ ending, point, planetIndex, projectile, record, probeSpotted });
    this.nextFlightOrOver(projectile.ownerId, p);
  }

  /** A probe's whole-path reveal at the end of its flight (and the trail cleared). */
  private takeProbeReveal(projectile: ProjectileState): number[] {
    if (!WEAPONS[projectile.weaponKey].reveals) return [];
    const spotted = this.revealProbePath(projectile.ownerId, this.probeTrailPoints);
    this.probeTrailPoints = [];
    return spotted;
  }

  /** Queue the next cluster bomblet (it launches after a fixed number of steps), or end the shot. */
  private nextFlightOrOver(ownerId: number, p: SimPresenter): void {
    const cluster = this.nextCluster();
    if (cluster) {
      this.pendingClusterLaunch = {
        stepsRemaining: CLUSTER_LAUNCH_DELAY_STEPS,
        projectile: {
          pos: cluster.pos, vel: cluster.vel, thrustDir: { x: 0, y: 1, z: 0 }, thrustMag: 0, fuelRemaining: 0,
          weaponKey: cluster.weaponKey, phase: 'ballistic', time: 0, ownerId, lastWormholeId: null, wormholeTransits: 0,
        },
      };
    } else {
      this.flightEnded = true;
      this.shotSettling = true;   // 64.6 2b-ii: settles once nobody it knocked into the air is still up
      p.flightOver(ownerId);
    }
  }

  // ===========================================================================
  // Impacts (51.5a): what happens when a shot lands, bursts or is lost
  // ===========================================================================
  // The flight itself is still stepped by the host (51.5b moves it here); these
  // are the resolutions it calls at the end of a flight, in the game's order.

  /**
   * A shot or bomblet hit a planet: crater (on the kernel when the match uses it),
   * damage, knockback, specials — then the world kept in step (applyImpactWorld),
   * the shot record, noise for the alert layer, and the cluster's bomblets queued.
   * `points` is the flight as flown (for the record). The host presents `r`.
   */
  resolveShotImpact(impactPoint: Vec3, planetIndex: number, projectile: ProjectileState, outcome: 'impact' | 'playerHit', points: Vec3[]): { r: ImpactResolution; record: ShotRecord | null; alerts: AlertChange[] } {
    const weapon = WEAPONS[projectile.weaponKey];
    const r = resolveImpact(this.state, { impactPoint, planetIndex, projectile }, {
      createCraterImpl: this.enterKernel() ? this.wasmCrater : undefined,
    });
    this.state = reconcileSeats(r.state);
    this.applyImpactWorld(r);
    const record = this.recordShot(outcome, r.damages, r.kills.length, planetIndex, impactPoint, points);
    // A probe is a sensor drone landing, not a blast
    const alerts = this.noise(impactPoint, planetIndex, weapon.reveals ? 'quiet' : 'loud');
    this.state = queueClusterSubmunitions(this.state, projectile.weaponKey, impactPoint, planetIndex, projectile.ownerId);
    return { r, record, alerts };
  }

  /** Air burst at `point` (remote detonation): blast only — no crater, drop, specials or flora. */
  detonate(point: Vec3, projectile: ProjectileState, points: Vec3[]): { r: ImpactResolution; record: ShotRecord | null } {
    const r = resolveImpact(this.state, { impactPoint: point, planetIndex: -1, projectile, airBurst: true });
    this.state = reconcileSeats(r.state);
    this.applyImpactWorld(r);
    const record = this.recordShot('detonated', r.damages, r.kills.length, -1, point, points);
    return { r, record };
  }

  /**
   * Air-burst the shot in flight at a step of its precomputed trajectory (the `detonate` turn input):
   * every peer bursts it at the same point, however far its playback had got. The host presents the
   * blast and moves the turn on.
   */
  detonateAtStep(step: number): PlanResult<{ r: ImpactResolution; record: ShotRecord | null; point: Vec3 }> {
    const projectile = this.state.projectile;
    const sim = this.precomputedSim;
    if (!projectile || !sim || this.pendingClusterLaunch) return refuse('Nothing to detonate', 'no flight');
    const idx = Math.max(0, Math.min(step, sim.points.length - 1));
    const point = sim.points[idx];
    this.projCursor = idx;
    const { r, record } = this.detonate(point, projectile, this.flownPoints());
    this.shotSettling = true;   // 64.6 2b-ii: an air burst ends the shot
    return { ok: true, r, record, point };
  }

  /** The shot left the system (or its battery died): its record. */
  shotLost(points: Vec3[]): ShotRecord | null {
    return this.recordShot('lost', [], 0, -1, null, points);
  }

  /** The next queued cluster bomblet, if any (taken off the queue). */
  nextCluster(): { pos: Vec3; vel: Vec3; weaponKey: ProjectileState['weaponKey'] } | null {
    const { state, cluster } = popPendingCluster(this.state);
    this.state = state;
    return cluster;
  }

  /**
   * Keep the world in step with a resolved impact (also called per impact while a
   * round plays back): the kernel's terrain and mass, the true planet colours,
   * every player's fog memory of the cratered planet's colours, and where incoming
   * fire came from, for the bots it landed near. 69.98 (4): with `fogLater`, the fog's
   * refresh is noted there for `refreshFogLater` after a run of impacts (a crash's pieces).
   */
  applyImpactWorld(r: ImpactResolution, fogLater?: FogLater): void {
    if (r.terrain) {
      const p = this.state.planets[r.terrain.planetIndex];
      if (this.enterKernel()) updatePlanetTerrain(r.terrain.planetIndex, p.heightmap, p.mass);
      // 69.41 (DECISIONS 254 (2)): a footing the edits reached laid again on the ground as it is now (core/stands.ts restandAfter)
      this.state = restandAfter(this.state, r.edits);
      // Fog remembers colours per revealed vertex: refresh every player's memory of this planet (37.2c) — 62.56: at the
      // vertices whose colour changed (the same memory: the rest already hold the true colours), all of them when the
      // match held no colours for the planet
      const before = this.truePlanetColors.get(r.terrain.planetId);
      // (the landing tick: among the crater's recoloured facets when it says which — every pass recolours from the colours
      // before it, so only those can differ — else over the whole planet)
      const changed = !before || before.length !== r.terrain.newColors.length ? null
        : r.terrain.recoloured ? changedVerticesAmong(before, r.terrain.newColors, recolouredFacets(r.terrain.newColors.length / 3, r.terrain.recoloured))
        : changedVertices(before, r.terrain.newColors);
      this.truePlanetColors.set(r.terrain.planetId, new Float32Array(r.terrain.newColors));
      const later = fogLater?.get(r.terrain.planetIndex);
      // (a vertex an earlier impact changed and this one didn't holds the same colour in both: the latest colours at
      // every vertex either changed are what refreshing after each would leave)
      if (fogLater) fogLater.set(r.terrain.planetIndex, { colors: r.terrain.newColors, changed: changed && (!later || later.changed) ? new Set([...(later?.changed ?? []), ...changed]) : null });
      else this.refreshFogColours(r.terrain.planetIndex, r.terrain.newColors, changed);
    }
    if (r.incomingFireIntel.length > 0) {
      const botIntel = { ...this.state.botIntel };
      for (const { playerId, intel } of r.incomingFireIntel) botIntel[playerId] = [...(botIntel[playerId] ?? []), intel];
      this.state = { ...this.state, botIntel };
    }
  }

  /** Every player's fog memory of a planet's colours refreshed: at the `changed` vertices, or all of them when null. */
  private refreshFogColours(planetIndex: number, colors: Float32Array, changed: readonly number[] | null): void {
    for (let fi = 0; fi < this.state.fog.length; fi++) {
      this.state.fog[fi] = changed
        ? refreshFogColorsAt(this.state.fog[fi], planetIndex, colors, changed)
        : refreshFogColors(this.state.fog[fi], planetIndex, colors);
    }
  }

  /** 69.98 (4): the refreshes `applyImpactWorld` noted, one a planet. */
  refreshFogLater(later: FogLater): void {
    for (const [planetIndex, l] of later) this.refreshFogColours(planetIndex, l.colors, l.changed && [...l.changed]);
  }

  /** Noise raises the alert ladder (Phase 57); a no-op without the layer. The changes, for the host to report. */
  noise(point: Vec3, planetIndex: number, loudness: Loudness): AlertChange[] {
    if (!this.state.modeState.alert) return [];
    const r = noise(this.state, { point, planetIndex, loudness }, this.state.turnNumber);
    this.state = reconcileSeats(r.state);
    return r.changes;
  }

  /**
   * The current shot's record (core/shotRecord.ts), appended to the state. Null
   * when no shot is being tracked. `points` is the flight as flown.
   */
  recordShot(outcome: ShotOutcome, damages: Array<{ playerId: number; damage: number }>, kills: number, impactPlanetIndex: number, impactPoint: Vec3 | null, points: Vec3[]): ShotRecord | null {
    const shot = this.currentShot;
    if (!shot) return null;
    const record = buildShotRecord({
      turnNumber: this.state.turnNumber, shooterId: shot.ownerId,
      weaponKey: this.state.projectile?.weaponKey ?? shot.params.weaponKey, params: shot.params,
      submunition: shot.submunition, outcome, launchPlanetIndex: shot.launchPlanetIndex,
      points, planets: this.state.planets, impactPlanetIndex, impactPoint, intendedTarget: shot.targetPos,
      damages, kills,
    });
    this.state = appendShotRecord(this.state, record);
    return record;
  }

  /** Normal-crater implementation backed by the WASM kernel (falls back to JS). */
  private wasmCrater: CraterFn = (planet: PlanetState, impactPoint, blastRadius, depthMult) => {
    const idx = this.state.planets.indexOf(planet);
    if (idx >= 0 && !planet.indestructible) {
      const w = createCraterWasm(idx, impactPoint, blastRadius, depthMult, planet.density, planet.initialMass);
      if (w) {
        const newPlanet: PlanetState = {
          ...planet, heightmap: w.newHeightmap, mass: w.newMass,
          initialHeightmap: planet.initialHeightmap ?? planet.heightmap,
          scorch: accumulateScorch(planet, impactPoint, blastRadius, 'crater'),
        };
        const modifiedVertices = Array.from(w.modifiedVertices);
        const prev = this.truePlanetColors.get(planet.id);
        return {
          planet: newPlanet,
          modifiedVertices,
          newColors: prev ? recolorVertices(newPlanet, prev, modifiedVertices) : computePlanetVertexColors(newPlanet),
          ...(prev ? { recoloured: modifiedVertices } : {}),
        };
      }
    }
    return createCrater(planet, impactPoint, blastRadius, depthMult, this.truePlanetColors.get(planet.id));
  };

  // ===========================================================================
  // Airborne units (51.4): jumps, knockbacks, geyser launches
  // ===========================================================================

  /**
   * One physics tick (PHYS_DT) for every airborne unit: gravity flight, landing and
   * crash damage, deaths in a star / a wormhole / space, rift warps, geyser boosts
   * (once per geyser per flight); kill credit to whoever launched a unit to its
   * death; for deaths off the map, a carried core goes home with the carrier's heal
   * items dropped, and every off-map death starts the respawn cooldown. When the
   * match does not end, every 10th tick each airborne unit scans the ground below it
   * (flyover). Order inside the tick is the game's, and matters for determinism.
   * Null when nobody is airborne.
   */
  stepAirborne(): AirborneStep | null {
    if (!hasAirbornePlayers(this.state.players)) return null;
    const s0 = this.state;
    // R12: the gravity a body feels is its body's family (a person's scales, a vehicle's is the planet's), read per unit (core/body.ts)
    // 62.8: on the action clock the airborne are people, and their landings are judged in the person family (SCALE.md §6)
    const landingScale = s0.config.clock === 'action' ? personSetOf(s0).k : 1;
    // S3: on the action clock at person scale, people land on the fine ground (a hummock catches them)
    const fineLand = s0.config.clock === 'action' && personSetOf(s0).fineAmplitude > 0
      ? (q: Vec3) => personGroundHit(q, s0) ?? checkCollision(q, s0.planets) : undefined;
    // 63.16: the walls the airborne meet (the action clock's people; the turn game's jumps are its travel lobs, untouched)
    const walls = s0.config.clock === 'action' ? { ps: personSetOf(s0) } : null;
    const events: AirborneEvent[] = [];
    const credits: Array<{ killerId: number; victimId: number }> = [];
    const offMapDeathIds: number[] = [];
    let playersChanged = false;
    const players = s0.players.map(player => {
      if (!player.alive || !player.airborne) return player;
      const cause: AirborneCause = player.airborneCause ?? 'knockback';
      // On the action clock the airborne are people and feel their own gravity (felt gravity, WORKBOARD §8.1)
      // 63.15: a jump with its own feel keeps its own gravity above its take-off height (core/jump.ts); else the felt gravity
      const r = updateAirbornePlayer(player, s0.planets, PHYS_DT, s0.config.fallDamageMultiplier ?? 1.0, s0.config.fallDamageByCause, jumpGravityScale(s0.config, player, s0.planets) ?? gravityScaleOf(player, s0), fineLand, landingScale);
      if (r.landed && r.player.jumpFrom !== undefined) r.player = { ...r.player, jumpFrom: undefined };   // (63.15: down, its take-off forgotten)
      // 63.16 (1), (2): on the action clock a person in the air meets the walk's obstacles (core/surfaceWalk.ts
      // airborneAgainstObstacles) — a jump at a wall ends at its foot instead of through it; the walk's filed discs, for the airborne only
      if (walls && !r.landed && r.player.airborne && r.player.airbornePos && r.player.airborneVel && player.airbornePos) {
        const planet = s0.planets[player.planetIndex];
        if (planet) {
          const filed = footprintObstacles(s0, player.planetIndex, personMover(walls.ps));
          const held = airborneAgainstObstacles(player.airbornePos, r.player.airbornePos, r.player.airborneVel, planet, filed, walls.ps.unitRadius,
            d => walls.ps.fineAmplitude > 0 ? personSurfaceRadius(planet, d, walls.ps) : getSurfaceRadius(planet, d));
          if (held) { r.player = { ...r.player, airbornePos: held.pos, airborneVel: held.vel }; playersChanged = true; }
        }
      }
      const credit = () => { if (player.lastDamagedBy !== undefined) credits.push({ killerId: player.lastDamagedBy, victimId: player.id }); };
      if (r.landed) {
        playersChanged = true;
        events.push({ kind: 'landed', playerId: player.id, cause, planetIndex: r.landedPlanetIndex!, damage: r.landingDamage, died: !r.player.alive });
        if (!r.player.alive) credit();
      }
      if (r.diedInStar) {
        playersChanged = true;
        offMapDeathIds.push(player.id);
        events.push({ kind: 'diedInStar', playerId: player.id, cause, starIndex: r.landedPlanetIndex! });
        credit();
      }
      if (r.diedInWormhole) {
        playersChanged = true;
        offMapDeathIds.push(player.id);
        events.push({ kind: 'diedInWormhole', playerId: player.id, cause });
        credit();
      }
      if (r.teleported) {
        playersChanged = true;
        events.push({ kind: 'warped', playerId: player.id });
      }
      if (r.diedInSpace) {
        playersChanged = true;
        offMapDeathIds.push(player.id);
        events.push({ kind: 'lostInSpace', playerId: player.id, cause });
        credit();
      }
      let updated = r.player;
      if (updated.airborne && updated.airbornePos && updated.airborneVel) {
        if (!this.playerBoostedGeysers.has(updated.id)) this.playerBoostedGeysers.set(updated.id, new Set());
        const boosted = this.playerBoostedGeysers.get(updated.id)!;
        const g = checkGeyserBoost(updated.airbornePos, updated.airborneVel, s0.planets, s0.hazards, boosted);
        if (g) {
          updated = { ...updated, airborneVel: g.newVel };
          boosted.add(g.geyserHazardId);
          playersChanged = true;
          events.push({ kind: 'geyserBoost', playerId: player.id, pos: updated.airbornePos! });
        }
      }
      if (!updated.airborne) this.playerBoostedGeysers.delete(updated.id);
      return updated;
    });

    // Kill credit to whoever launched the unit
    if (credits.length > 0) {
      const byKiller = new Map<number, number>();
      for (const c of credits) byKiller.set(c.killerId, (byKiller.get(c.killerId) || 0) + 1);
      for (let i = 0; i < players.length; i++) {
        const bonus = byKiller.get(players[i].id);
        if (bonus) players[i] = { ...players[i], kills: players[i].kills + bonus };
      }
      for (const c of credits) if (players.some(p => p.id === c.killerId)) events.push({ kind: 'killCredit', ...c });
      playersChanged = true;
    }

    this.state = { ...s0, players };
    if (playersChanged) {
      for (const deadId of offMapDeathIds) {
        if (getPlayerCarrying(deadId, this.state.carryables)) {
          this.state = respawnCoreAtBase(this.state, deadId);
          this.state = dropHealItemsOnDeath(this.state, deadId);
          events.push({ kind: 'cargoRespawned', playerId: deadId });
        }
        this.state = applyDeathCooldown(this.state, deadId);
      }
      if (isGameOver(this.state)) { this.refreshLandedFog(events); return { events, playersChanged, gameOver: true, flyovers: [] }; }
    }

    // Flyover: an airborne unit scans what it flies over, every 10th tick
    const flyovers: ScanResult[] = [];
    this.airborneRevealCounter++;
    if (this.airborneRevealCounter >= 10) {
      this.airborneRevealCounter = 0;
      for (const p of this.state.players) {
        if (!p.alive || !p.airborne || !p.airbornePos) continue;
        const scan = this.scanAt(p.airbornePos, p.id);
        if (scan) flyovers.push(scan);
      }
    }
    this.refreshLandedFog(events);
    return { events, playersChanged, gameOver: false, flyovers };
  }

  /**
   * Every unit that landed this tick looks round from where it came down (its line of sight
   * and ghost markers), in landing order. Logic, so both hosts get it: the game used to do this
   * only for the current unit and the harness for all — harmless until bots aimed at ghost
   * positions (37.20), after which a watched battle drifted from its auto-resolve.
   */
  private refreshLandedFog(events: AirborneStep['events']): void {
    for (const e of events) {
      if (e.kind !== 'landed') continue;
      const i = this.state.players.findIndex(p => p.id === e.playerId);
      if (i >= 0) this.refreshFog(i);
    }
  }

  /**
   * A point scan (flyover, a probe in flight) into the scanner's own fog: terrain
   * within probe range, ghost markers on enemies it finds, structure ghosts. The
   * host adds what is viewer-relative (exposure markers, alerts, the screen).
   * Mutates the scanner's fog in place, as the game always has.
   */
  scanAt(pos: Vec3, scannerId: number): ScanResult | null {
    const idx = this.state.players.findIndex(p => p.id === scannerId);
    if (idx < 0) return null;
    const scanner = this.state.players[idx];
    const fogState = this.state.fog[idx];
    if (!fogState) return null;
    const { players, planets, structures } = this.state;
    const turnNumber = knowledgeTurn(this.state);   // 64.4: the knowledge ages' "now", on either clock
    const colors = planets.map(p => this.truePlanetColors.get(p.id)!);
    const terrainRevealed = revealAtPoint(fogState, pos, planets, PROBE_REVEAL_RADIUS, colors, turnNumber);
    const spotted = getEnemiesAtPoint(scanner, players, planets, pos, PROBE_REVEAL_RADIUS);
    const spottedStructures = getStructuresAtPoint(scanner, structures, players, planets, pos, PROBE_REVEAL_RADIUS);
    if (spotted.length > 0) {
      const current = this.state.config.datalinkedLOS
        ? getTeamVisibleEnemies(scanner, players, planets, this.enterKernel())
        : getVisibleEnemies(scanner, players, planets, this.enterKernel());
      this.state.fog[idx] = updateGhostMarkers(this.state.fog[idx], [...new Set([...current, ...spotted])], players, planets, turnNumber);
    }
    if (spottedStructures.length > 0) {
      this.state.fog[idx] = updateStructureGhosts(this.state.fog[idx], spottedStructures, structures, planets, turnNumber);
    }
    return { scannerId, pos, spotted, spottedStructures, terrainRevealed };
  }

  /**
   * A probe's scan of what it flew over, into its owner's fog: terrain along the
   * path, enemy units (ghost markers) and structures it passed. Returns the enemy
   * ids it spotted (the host shows them if the viewer's side scanned).
   */
  revealProbePath(ownerId: number, points: Vec3[]): number[] {
    if (points.length === 0) return [];
    const idx = this.state.players.findIndex(p => p.id === ownerId);
    if (idx < 0) return [];
    const s = this.state;
    const owner = s.players[idx];
    const radius = PROBE_REVEAL_RADIUS * (owner.modifiers?.probeRangeMult ?? 1);
    if (!s.fog[idx]) return [];
    const colors = s.planets.map(p => this.truePlanetColors.get(p.id)!);
    const fog = [...s.fog];
    fog[idx] = revealAlongPath(fog[idx], points, s.planets, radius, colors, s.turnNumber);
    const spotted = getEnemiesAlongPath(owner, s.players, s.planets, points, radius);
    if (spotted.length > 0) {
      const current = s.config.datalinkedLOS
        ? getTeamVisibleEnemies(owner, s.players, s.planets, this.enterKernel(), s)
        : getVisibleEnemies(owner, s.players, s.planets, this.enterKernel(), s);   // s: guard perception (Phase 57) — LOS alone when there are no guards
      fog[idx] = updateGhostMarkers(fog[idx], [...new Set([...current, ...spotted])], s.players, s.planets, knowledgeTurn(s));
    }
    const structures = getStructuresAlongPath(owner, s.structures, s.players, s.planets, points, radius);
    if (structures.length > 0) fog[idx] = updateStructureGhosts(fog[idx], structures, s.structures, s.planets, knowledgeTurn(s));
    this.state = { ...s, fog };
    // R14 (c): a probe that flew over the open survey bounty's target claims it (the planets its path passed within reveal radius of)
    const surveyed = s.planets.map((pl, i) => ({ pl, i })).filter(({ pl }) => pl.alive && points.some(pt => dhypot(pt.x - pl.position.x, pt.y - pl.position.y, pt.z - pl.position.z) <= pl.radius + radius)).map(x => x.i);
    if (surveyed.length) this.state = claimSurveyBounty(this.state, ownerId, surveyed);
    // I3: the probe is the cheapest drone — what it saw goes into the team's sightings like a drone's (identified,
    // with the turn), the one path the awareness core reads every instrument through
    if (spotted.length > 0) {
      const seen: Sighting[] = spotted.map(id => {
        const u = this.state.players.find(p => p.id === id)!;
        return { teamIndex: owner.teamIndex, unitId: u.id, unitTeam: u.teamIndex, planetIndex: u.planetIndex, surfaceDir: { ...u.surfaceDir }, pos: getPlayerWorldPosition(u, this.state.planets), tick: this.actionTick, turn: this.state.turnNumber, by: 'probe', sourceId: ownerId };
      });
      this.state = mergeSightings(this.state, seen);
    }
    return spotted;
  }
}
